import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BookOpenCheck,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  Pencil,
  RefreshCw,
  Save,
  Search,
  ShieldCheck,
  Sprout,
  X,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { buildApiUrl } from '../../utils/apiUrl';
import { useEmpresaId } from '../../context/EmpresaContext';
import {
  btnSecondary,
  inputCls,
  labelCls,
  pageSubtitleCls,
  pageTitleCls,
  selectCls,
  tableWrapCls,
  tdCls,
  thCls,
  theadCls,
  trCls,
} from '../../components/ui';

type Cuenta = {
  id: number;
  codigo: string;
  nombre: string;
  activo: boolean;
};

type EstadoCierreAB = {
  ultimo_corte: string | null;
  siguiente_desde: string;
  siguiente_hasta: string;
  incluye_cedulas_tardias?: boolean;
  movimientos_por_preparar: number;
  costos_congelados: number;
  costos_disponibles: number;
  total_crc: number;
  preasientos_pendientes: number;
  listo_para_preasiento: boolean;
  al_dia: boolean;
};
type EstadoTomaFinca = {
  configurada: boolean; toma_id?: number; descripcion?: string; estado: string;
  referencia?: string | null; asiento_id?: number | null; lineas?: number;
  diferencias?: number; conciliada?: boolean; aplicada_at?: string | null;
};
type ControlIntegralAB = {
  ok: boolean;
  verificado_at: string;
  problemas: number;
  ultimo_corte: string | null;
  costos: {
    total: number; monto_crc: number; asignados: number;
    disponibles: number; duplicados: number;
  };
  distribucion: {
    reglas_aprobadas: number; lineas: number; desbalanceadas: number;
  };
  contabilidad: {
    preasientos: number; preasientos_desbalanceados: number;
    asientos_desbalanceados: number; reversiones_desbalanceadas: number;
  };
  siguiente_corte: { movimientos_pendientes: number };
};
type TransicionABResumen = {
  configurado: boolean;
  periodo_id: string;
  fecha_saldo_inicial: string;
  fecha_desde: string;
  fecha_hasta: string;
  estado: string;
  saldo_libros_inicial_crc: number | null;
  cedulas: number;
  lineas: number;
  valor_vfp_crc: number;
  conciliables: number;
  sin_producto: number;
  valor_cero: number;
  excluidas: number;
  solapadas_erp: number;
  ya_capitalizadas: number;
  exclusiones_controladas?: number;
  lotes: number;
  observacion: string;
};
type TransicionABObservacion = {
  id: string;
  nboleta: string;
  fecha_aplicacion: string;
  lote_num: string | null;
  articulo: string | null;
  producto_nombre: string | null;
  valor_vfp_crc: number;
  estado: string;
  es_solapamiento_erp: boolean;
};
type TransicionABClasificacionResumen = {
  grupos: number;
  valor_total_crc: number;
  vivos: number;
  valor_vivo_crc: number;
  cosechados: number;
  valor_cosechado_crc: number;
  mixtos: number;
  transferir_pc: number;
  semilleros_fondo: number;
  valor_semillero_crc: number;
  revision: number;
  propuestas?: number;
  aprobadas?: number;
};
type TransicionABLoteCiclo = {
  id: string;
  lote_num: string;
  ciclo: 'PC' | 'RC' | 'SEMILLERO' | 'BARBECHO';
  cedulas: number;
  lineas: number;
  valor_vfp_crc: number;
  bloques_encontrados: number;
  bloques_vivos: number;
  bloques_cosechados: number;
  clasificacion: 'vivo' | 'cosechado' | 'mixto' | 'transferir_a_pc' | 'barbecho_abierto' | 'requiere_revision_semillero' | 'sin_evidencia';
  criterio: string;
  fecha_corte: string;
  decision_id?: string | null;
  decision_propuesta?: 'mantener_vivo' | 'reclasificar_ciclo' | 'cosechado' | 'excluir' | null;
  ciclo_destino?: 'PC' | 'RC' | 'SEMILLERO' | 'BARBECHO' | null;
  decision_fundamento?: string | null;
  decision_estado?: 'propuesta' | 'aprobada' | 'rechazada' | null;
  clasificacion_efectiva?: 'vivo' | 'cosechado' | 'mixto' | 'transferir_a_pc' | 'barbecho_abierto' | 'requiere_revision_semillero' | 'sin_evidencia' | 'excluido';
};
type TransicionABFondoSemillero = {
  id: string;
  fecha_desde: string;
  fecha_hasta: string;
  metodo: 'plantas_sembradas';
  valor_fondo_crc: number;
  total_conductor: number;
  lotes_beneficiarios: number;
  fundamento: string;
  limitacion_origen: string;
  estado: 'propuesta' | 'revisada' | 'aprobada' | 'descartada';
};
type TransicionABDistribucionSemillero = {
  id: string;
  lote_num: string;
  fecha_inicio_pc: string;
  bloques: number;
  plantas_sembradas: number;
  area_ha: number;
  proporcion: number;
  valor_asignado_crc: number;
};
type TransicionABConciliacionResumen = {
  cuentas: number;
  saldo_legacy_crc: number;
  adicion_viva_crc: number;
  adicion_cosechada_crc: number;
  adicion_total_crc: number;
  base_puente_crc: number;
  diferencia_adiciones_crc: number;
  estado: 'requiere_medicion_legacy' | 'conciliada';
};
type TransicionABConciliacionCuenta = {
  id: string;
  cuenta_codigo: string;
  cuenta_nombre: string;
  saldo_legacy_crc: number;
  adicion_viva_crc: number;
  adicion_cosechada_crc: number;
  adicion_total_crc: number;
  base_puente_crc: number;
  estado: 'requiere_medicion_legacy' | 'conciliada';
  observacion: string;
};
type TransicionABPoliticaLegacy = {
  id: string;
  version: number;
  saldo_legacy_crc: number;
  base_puente_crc: number;
  costos_cosechados_2026_crc: number;
  enfoque_contable: string;
  enfoque_fiscal: string;
  decision_transitoria: string;
  alternativas: Array<{ codigo: string; nombre: string; recomendada: boolean; importe_crc: number | null; efecto: string }>;
  insumos_faltantes: Array<{ codigo: string; nombre: string; disponible: boolean }>;
  evidencia_calidad: {
    bloques: number;
    con_plantas: number;
    con_area: number;
    con_siembra: number;
    cronologia_invalida: number;
    columnas_valoracion_encontradas: number;
    conclusion: string;
  };
  fuentes: Array<{ tipo: string; referencia: string; url: string }>;
  estado: 'propuesta' | 'aprobada' | 'rechazada' | 'sustituida';
  aprobado_at: string | null;
};

type MedicionNIIF = {
  id: string;
  lote_num: string;
  ciclo: 'PC' | 'RC';
  version: number;
  fecha_medicion: string;
  plantas: number;
  area_ha: number;
  costo_reconstruido_crc: number;
  frutos_por_planta: number | null;
  peso_kg_por_fruto: number | null;
  merma_pct: number | null;
  probabilidad_cosecha_pct: number | null;
  precio_crc_kg: number | null;
  costo_venta_crc_kg: number | null;
  evidencia_referencia: string | null;
  observacion: string | null;
  estado: 'borrador' | 'completa' | 'aprobada' | 'sustituida';
  valor_razonable_neto_crc: number;
  kg_esperados: number;
  datos_completos: boolean;
  parametro_id: string | null;
  densidad_plantas_ha: number | null;
  mortalidad_modelo_pct: number | null;
  empaque_pct: number | null;
  jugo_pct: number | null;
  peso_empaque_kg: number | null;
  peso_jugo_kg: number | null;
  precio_empaque_crc_kg: number | null;
  precio_jugo_crc_kg: number | null;
  costo_venta_empaque_crc_kg: number | null;
  costo_venta_jugo_crc_kg: number | null;
  plantas_sembradas_estimadas: number;
  plantas_productivas_estimadas: number;
  kg_empaque_estimados: number;
  kg_jugo_estimados: number;
  valor_bruto_modelo_crc: number;
  costos_venta_modelo_crc: number | null;
  valor_razonable_modelo_crc: number | null;
  modelo_productivo_completo: boolean;
  costos_venta_completos: boolean;
};

type ParametroMedicion = {
  id: string;
  version: number;
  vigencia_desde: string;
  densidad_plantas_ha: number;
  mortalidad_pct: number;
  empaque_pct: number;
  jugo_pct: number;
  peso_empaque_kg: number;
  peso_jugo_kg: number;
  precio_empaque_crc_kg: number;
  precio_jugo_crc_kg: number;
  costo_venta_empaque_crc_kg: number | null;
  costo_venta_jugo_crc_kg: number | null;
  evidencia_referencia: string;
  fundamento: string;
  estado: 'propuesta' | 'aprobada' | 'rechazada' | 'sustituida';
  aprobado_at?: string | null;
};

type CapacidadesMedicion = {
  puede_ver: boolean;
  puede_editar: boolean;
  puede_aprobar: boolean;
};

type ConciliacionMedicion = {
  id: string;
  version: number;
  fecha_medicion: string;
  saldo_legacy_crc: number;
  costos_2026_crc: number;
  valor_libros_puente_crc: number;
  valor_bruto_crc: number;
  costos_venta_crc: number;
  valor_razonable_neto_crc: number;
  diferencia_medicion_crc: number;
  naturaleza_diferencia: 'ganancia' | 'perdida' | 'sin_diferencia';
  cuenta_activo_ra_codigo: string | null;
  cuenta_resultado_ra_codigo: string | null;
  base_fiscal_estado: 'separada_pendiente' | 'documentada';
  estado: 'borrador' | 'requiere_revision' | 'revisada' | 'aprobada' | 'contabilizada' | 'sustituida';
  criterio: string;
  limitaciones: string;
  motivo_revision?: string | null;
};

type ConfiguracionMedicion = {
  cuenta_ajuste_activo_id: number;
  cuenta_ganancia_id: number;
  cuenta_perdida_id: number;
};

type CorteMedicion = {
  id: string;
  fecha_medicion: string;
  version: number;
  valor_libros_crc: number;
  valor_bruto_crc: number;
  costos_venta_crc: number;
  valor_razonable_neto_crc: number;
  diferencia_crc: number;
  naturaleza: 'ganancia' | 'perdida' | 'sin_diferencia';
  cuenta_ajuste_activo_id: number;
  cuenta_resultado_id: number | null;
  estado: 'preparado' | 'revisado' | 'aprobado' | 'contabilizado' | 'anulado' | 'reversado';
  asiento_id: number | null;
  preparado_at: string;
  revisado_at: string | null;
  aprobado_at: string | null;
  contabilizado_at: string | null;
  evidencia: {
    requiere_modelo_madurez?: boolean;
    validacion_etapas?: boolean;
    motivo_bloqueo?: string;
  };
};

type ModeloEtapas = {
  id: string;
  version: number;
  vigencia_desde: string;
  estado: 'borrador' | 'aprobado' | 'sustituido';
  etapas: number;
  etapas_completas: number;
  fundamento: string | null;
  evidencia_referencia: string | null;
};

type ModeloEtapaDetalle = {
  id: string;
  etapa_codigo: string;
  etapa_nombre: string;
  ciclo: 'PC' | 'RC';
  orden: number;
  dias_restantes: number | null;
  metodo_dias: 'promedio_etapa' | 'evento_lote';
  evento_ancla: 'fecha_siembra' | 'fecha_induccion' | 'fecha_cosecha' | 'fecha_induccion2' | null;
  duracion_desde_ancla_dias: number | null;
  tolerancia_dias: number;
  probabilidad_realizacion_pct: number | null;
  costo_completar_crc_ha: number | null;
  tasa_descuento_anual_pct: number | null;
  fundamento: string | null;
  evidencia_referencia: string | null;
};

type CronologiaLoteControl = {
  medicion_id: string;
  fecha_medicion: string;
  lote_num: string;
  ciclo: 'PC' | 'RC';
  area_ha: number;
  bloques_al_corte: number;
  bloques_fecha_inconsistente: number;
  bloques_sin_induccion: number;
  fecha_siembra_desde: string | null;
  fecha_siembra_hasta: string | null;
  fecha_induccion_desde: string | null;
  fecha_induccion_hasta: string | null;
  etapa_sugerida: string | null;
  dias_restantes_min_referencia: number | null;
  dias_restantes_max_referencia: number | null;
  control_cronologia: 'SIN_LOTE_MAESTRO' | 'SIN_BLOQUES_AL_CORTE' | 'FECHAS_INCONSISTENTES' | 'SIN_FECHA_SIEMBRA' | 'PENDIENTE_FECHA_INDUCCION' | 'CRONOLOGIA_PARCIAL' | 'CRONOLOGIA_UTILIZABLE';
};

type ProgramaCostoFuente = {
  id: string;
  codigo: string;
  version: number;
  ciclo: 'PC' | 'RC';
  etapa_codigo: string;
  fecha_documento: string | null;
  documento_referencia: string;
  moneda: 'USD' | 'CRC';
  total_ha: number;
  estado: 'referencia' | 'revisado' | 'sustituido';
  limitaciones: string;
};

type PlanCostoEtapaControl = {
  etapa_codigo: string;
  etapa: string;
  ciclo_cosecha: string;
  ciclos: number;
  lineas: number;
  lineas_sin_costo: number;
  dia_minimo: number | null;
  dia_maximo: number | null;
  costo_programa_crc_ha: number;
  costo_programa_usd_ha: number;
  completo: boolean;
  advertencia: string;
};

type PlanSnapshot = {
  id: string;
  version: number;
  fecha_corte: string;
  estado: 'borrador' | 'observado' | 'aprobado' | 'sustituido';
  lineas: number;
  lineas_sin_costo: number;
  costo_total_crc_ha: number;
  fundamento: string | null;
  evidencia_referencia: string | null;
};

type FuenteCostoCompletar = {
  id: string;
  codigo: string;
  componente: string;
  fuente_erp: string;
  conductor_requerido: string;
  nivel_asignacion: 'lote_etapa' | 'lote' | 'etapa' | 'produccion';
  tratamiento: string;
  estado: 'pendiente' | 'en_construccion' | 'documentado' | 'aprobado';
  evidencia_referencia: string | null;
  observacion: string | null;
};

type MoConductorValidacion = {
  id: string;
  anio: number;
  claves_evaluadas: number;
  claves_distribucion_multiple: number;
  coincidencias_plantas: number;
  coincidencia_pct: number;
  desviacion_mediana: number;
  fuente: string;
  conclusion: string;
};

type MoFaseCalibracion = {
  id: string;
  fase_codigo: string;
  lineas: number;
  horas: number;
  monto_crc: number;
  uso_modelo: string;
  incluido_costo_cultivo: boolean;
};

type CostoFuturoLote = {
  medicion_id: string;
  lote_num: string;
  ciclo: string;
  area_ha: number;
  etapas: string | null;
  dias_hasta_cosecha: number | null;
  productos_pendientes_crc: number;
  mano_obra_pendiente_crc: number;
  cosecha_crc: number;
  acarreo_central_crc: number;
  soporte_bajo_crc: number;
  soporte_central_crc: number;
  soporte_alto_crc: number;
  costo_futuro_bajo_crc: number | null;
  costo_futuro_central_crc: number | null;
  costo_futuro_alto_crc: number | null;
  valor_bruto_modelo_crc: number;
  costos_venta_modelo_crc: number | null;
  valor_neto_pre_descuento_bajo_crc: number | null;
  valor_neto_pre_descuento_central_crc: number | null;
  valor_neto_pre_descuento_alto_crc: number | null;
  tasa_fecha_referencia: string | null;
  tasa_base_anual_pct: number | null;
  prima_riesgo_pct: number | null;
  tasa_total_anual_pct: number | null;
  valor_presente_referencia_base_crc: number | null;
  tasa_referencia_id: string | null;
  tasa_estado: 'referencia' | 'revisada' | 'aprobada' | 'sustituida' | null;
  tasa_fuente: string | null;
  tasa_url_fuente: string | null;
  tasa_fundamento: string | null;
  tasa_aplicada_control_pct: number | null;
  valor_presente_tasa_control_crc: number | null;
  control_descuento: string;
  control_integral: string;
  base_calculable: boolean;
  control: string;
};

type PruebaIntegral = {
  id: string; codigo: string; lote_num: string; ciclo: string; fecha_medicion: string;
  area_ha: number; plantas: number; plantas_productivas: number; kg_esperados: number;
  dias_hasta_cosecha: number; saldo_reconstruido_crc: number; valor_bruto_crc: number;
  costos_venta_crc: number; costo_futuro_bajo_crc: number; costo_futuro_central_crc: number;
  costo_futuro_alto_crc: number; tasa_control_pct: number; valor_presente_control_crc: number;
  diferencia_saldo_crc: number; naturaleza_diferencia: string; control_tasa: string;
  estado: string; fundamento: string; evidencia: Record<string, any>;
};

type CostoHistoricoResumen = {
  fuente_id: string;
  periodo_desde: string;
  periodo_hasta: string;
  area_referencia_ha: number;
  documento_referencia: string;
  naturaleza: 'real_contable' | 'presupuesto' | 'proyeccion';
  estado: 'referencia' | 'revisada' | 'sustituida';
  total_documentado_crc: number;
  costo_campo_real_crc: number;
  costo_campo_crc_ha_anio: number;
  costo_empaque_referencia_crc: number;
  total_excluido_crc: number;
  habilita_medicion: boolean;
  bloqueo_medicion: string;
};

type CostoHistoricoDetalle = {
  id: string;
  orden: number;
  codigo: string;
  concepto: string;
  importe_crc: number;
  uso_modelo: 'referencia_campo' | 'referencia_costo_venta'
    | 'excluido_compra_terceros' | 'excluido_doble_conteo'
    | 'excluido_no_incremental';
  justificacion: string;
};

type SaldoHistoricoEtapa = {
  id: string;
  orden: number;
  etapa_codigo: string;
  etapa_nombre: string;
  ciclo: 'PC' | 'RC' | null;
  area_ha: number;
  saldo_crc: number;
  costo_crc_ha: number;
  advertencia: string;
};

type VentaHistoricaResumen = {
  fuente_id: string;
  periodo_desde: string;
  periodo_hasta: string;
  venta_contabilizada_crc: number;
  venta_aceptada_contabilizada_crc: number;
  venta_rechazada_contabilizada_crc: number;
  venta_procesando_no_mayorizada_crc: number;
  venta_operativa_valida_crc: number;
  documentos_contables: number;
  documentos_rechazados: number;
  documentos_procesando: number;
  fuente_contable: string;
  fuente_producto: string;
  fuente_fiscal: string;
  criterio: string;
  habilita_medicion: boolean;
};

type VentaHistoricaProducto = {
  id: string;
  producto_codigo: string;
  producto_nombre: string;
  categoria: 'FRUTA_NACIONAL' | 'JUGO' | 'EXPORTACION' | 'OTRA';
  estado_fiscal: 'ACEPTADO' | 'RECHAZADO' | 'PROCESANDO' | 'SIN_ESTADO_FISCAL';
  documentos: number;
  cantidad_neta: number;
  importe_crc: number;
};

type VentaHistoricaExcepcion = {
  id: string;
  documento: string;
  fecha: string;
  estado_fiscal: 'RECHAZADO' | 'PROCESANDO';
  categoria: string;
  importe_contable_crc: number;
  importe_operativo_crc: number;
  tratamiento: string;
};

type CalibracionVentaHistorica = {
  id: string;
  codigo: string;
  destino: 'EXPORTACION' | 'JUGO' | 'FRUTA_NACIONAL';
  unidad_origen: string;
  cantidad_origen: number;
  peso_supuesto_kg: number | null;
  cantidad_estimada_kg: number | null;
  venta_aceptada_crc: number;
  venta_crc_unidad: number;
  costo_empaque_asignado_crc: number | null;
  costo_empaque_crc_kg: number | null;
  cobertura_dato_directo_pct: number;
  estado: 'dato_directo' | 'escenario' | 'descartado';
  advertencia: string;
  habilita_parametro: boolean;
};

type CostoVentaHistorico = {
  id: string;
  orden: number;
  cuenta_codigo: string;
  concepto: string;
  importe_crc: number;
  clasificacion: 'transformacion_empaque' | 'incremental_potencial'
    | 'no_incremental' | 'posterior_anormal';
  tratamiento_nic41: string;
  requiere_contrato: boolean;
  incluido_rango: boolean;
  habilita_parametro: boolean;
};

type CostoVentaEscenario = {
  id: string;
  codigo: string;
  descripcion: string;
  kg_exportacion: number;
  costo_minimo_crc: number;
  costo_maximo_crc: number;
  costo_minimo_crc_kg: number;
  costo_maximo_crc_kg: number;
  criterio: string;
  estado: 'bloqueado' | 'revisado' | 'descartado';
  habilita_parametro: boolean;
};

type Boleta = {
  id: number;
  numero: string;
  lote_id: string | null;
  lote_num: string | null;
  finca_nombre: string | null;
  fecha_desde: string;
  fecha_aplicacion: string | null;
  etapa_id: number | null;
  etapa_nombre: string | null;
  area_ha: number | null;
  plantas: number | null;
  estado: string;
};

type MovimientoInventario = {
  id: number;
  producto_id: number | null;
  cantidad: number | null;
  costo_unitario: number | null;
  costo_promedio_resultante: number | null;
  referencia: string | null;
};

type LineaBoleta = {
  id: number;
  boleta_id: number;
  inv_producto_id: number | null;
  articulo: string | null;
  nombre_producto: string | null;
  medida: string | null;
  cantidad_aplicar: number | null;
  excluida: boolean | null;
  es_agua: boolean | null;
};

type Producto = {
  id: number;
  codigo: string;
  descripcion: string;
  unidad_medida: string | null;
  costo_promedio: number | null;
  costo_neto_unitario: number | null;
  precio_compra_ref: number | null;
  moneda_costo: string | null;
  tipo_cambio_costo: number | null;
};

type Etapa = {
  id: number;
  nombre: string;
  ciclo_cosecha: string;
};

type Fila = {
  key: string;
  lote: string;
  finca: string;
  ciclo: string;
  cuenta: Cuenta | null;
  area: number;
  plantas: number;
  aplicaciones: number;
  aplicacionesConMovimiento: number;
  aplicacionesValorizadas: number;
  insumosReferenciales: number;
  costoCapitalizable: number;
  movimientosSinValor: number;
  aplicacionesSinMovimiento: number;
};

type AplicacionSinMovimiento = {
  id: number;
  numero: string;
  lote: string;
  finca: string;
  etapa: string;
  ciclo: string;
  fechaAplicacion: string;
  fechaProgramada: string;
  referenciaEsperada: string;
};

type RegularizacionHistorica = AplicacionSinMovimiento & {
  productos: number;
  detalleProductos: string;
  costoReferencial: number;
  estadoRegularizacion: 'PENDIENTE_INVENTARIO_INICIAL';
};

type TrazabilidadProducto = {
  lineaId: number;
  productoId: number | null;
  codigo: string;
  producto: string;
  boletaId: number;
  boleta: string;
  fecha: string;
  lote: string;
  finca: string;
  ciclo: string;
  cantidad: number;
  unidad: string;
  movimientoId: number | null;
  costoUnitario: number;
  estado: 'VALORIZADO' | 'COSTO_CERO' | 'SIN_MOVIMIENTO';
};

type ProductoCorreccion = Producto;

type CostoCongelado = {
  id: string;
  inv_movimiento_id?: number;
  lote_id?: string | null;
  lote_num: string;
  fecha_consumo: string;
  costo_total_crc: number;
  ciclo_cosecha: string;
};

type LoteMaestro = {
  id: string; numero: string; nombre: string; area_ha: number | null;
  area_ha_sembrada: number | null; fecha_primera_siembra: string | null;
  variedad: string | null; activo: boolean; emp_fincas?: { nombre: string } | null;
};

type ExpedienteLote = {
  id: string; numero: string; nombre: string; finca: string; area: number;
  variedad: string; fechaPrimeraSiembra: string; ciclo: string; etapa: string;
  ultimaAplicacion: string; movimientos: number; costoAcumulado: number;
  costoHa: number; cuenta: Cuenta | null;
};

type ResultadoPreparacion = {
  movimientos_en_periodo: number;
  insertados: number;
  distribuciones_creadas?: number;
  sin_valor: number;
  sin_vinculo: number;
  sin_cuenta: number;
};

type PreasientoAB = {
  id: string;
  referencia: string;
  periodo_desde: string;
  periodo_hasta: string;
  fecha_contable: string;
  estado: 'pendiente' | 'revisado' | 'contabilizado' | 'anulado' | 'reversado';
  total_debito_crc: number;
  total_credito_crc: number;
  diferencia_crc: number;
  creado_at: string;
  motivo_anulacion?: string | null;
  asiento_id?: number | null;
  asiento_reversion_id?: number | null;
  motivo_reversion?: string | null;
};

type LineaPreasientoAB = {
  id: string;
  preasiento_id: string;
  linea: number;
  tipo: 'debito' | 'credito';
  cuenta_codigo: string;
  cuenta_nombre: string;
  lote_num: string | null;
  ciclo_cosecha: string | null;
  descripcion: string;
  debito_crc: number;
  credito_crc: number;
};

type ReglaCapitalizacion = {
  id: string;
  producto_id: number;
  producto_codigo: string;
  producto_nombre: string;
  version: number;
  metodo: 'directa' | 'porcentaje' | 'dosis' | 'hectareas' | 'plantas';
  porcentaje_pc: number;
  porcentaje_rc: number;
  vigencia_desde: string;
  vigencia_hasta: string | null;
  fundamento_tecnico: string;
  evidencia_referencia: string | null;
  estado: 'borrador' | 'aprobada' | 'rechazada' | 'inactiva';
  creado_at: string;
  aprobado_at: string | null;
  motivo_estado: string | null;
};

const CUENTA_INVENTARIO = '0101-06-001-001';
const CUENTAS_ACTIVO = {
  desarrolloPC: '0102-01-008-001',
  desarrolloRC: '0102-01-008-002',
  forzaPC: '0102-01-008-003',
  forzaRC: '0102-01-008-004',
  semillero: '0102-01-008-005',
  barbecho: '0102-01-008-006',
} as const;

function isoToday() {
  return new Date().toISOString().slice(0, 10);
}

function addMonths(date: string, months: number) {
  const d = new Date(`${date}T12:00:00`);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

function fmtCRC(value: number) {
  return `CRC ${value.toLocaleString('es-CR', { maximumFractionDigits: 0 })}`;
}

function etiquetaClasificacionAB(valor: TransicionABLoteCiclo['clasificacion']) {
  return ({
    vivo: 'Vivo al corte',
    cosechado: 'Cosechado al corte',
    mixto: 'Mixto: revisar bloques',
    transferir_a_pc: 'Preparación transferible a PC',
    barbecho_abierto: 'Barbecho abierto',
    requiere_revision_semillero: 'Semillero: confirmar destino',
    sin_evidencia: 'Sin evidencia suficiente',
  } as const)[valor];
}

function fmtCostoProducto(producto: Producto) {
  const costo = Number(
    producto.costo_promedio ||
    producto.costo_neto_unitario ||
    producto.precio_compra_ref ||
    0,
  );
  const moneda = String(producto.moneda_costo || 'CRC').toUpperCase();
  if (moneda === 'USD') {
    const tipoCambio = Number(producto.tipo_cambio_costo || 0);
    return {
      principal: `USD ${costo.toLocaleString('es-CR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`,
      equivalente: tipoCambio > 0
        ? `${fmtCRC(costo * tipoCambio)} · TC ${tipoCambio.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : 'Sin tipo de cambio',
    };
  }
  return { principal: fmtCRC(costo), equivalente: 'Moneda original CRC' };
}

function fmtNumber(value: number, decimals = 2) {
  return value.toLocaleString('es-CR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function cronologiaControlLabel(control: CronologiaLoteControl['control_cronologia']) {
  const labels: Record<CronologiaLoteControl['control_cronologia'], string> = {
    SIN_LOTE_MAESTRO: 'Lote sin vínculo maestro',
    SIN_BLOQUES_AL_CORTE: 'Sin bloques al corte',
    FECHAS_INCONSISTENTES: 'Fechas incompatibles',
    SIN_FECHA_SIEMBRA: 'Falta fecha de siembra',
    PENDIENTE_FECHA_INDUCCION: 'Pendiente de inducción',
    CRONOLOGIA_PARCIAL: 'Cronología parcial',
    CRONOLOGIA_UTILIZABLE: 'Cronología utilizable',
  };
  return labels[control];
}

function costoCRC(producto?: Producto) {
  if (!producto) return 0;
  const costo = Number(
    producto.costo_promedio ||
    producto.costo_neto_unitario ||
    producto.precio_compra_ref ||
    0,
  );
  const moneda = String(producto.moneda_costo || 'CRC').toUpperCase();
  const tipoCambio = Math.max(Number(producto.tipo_cambio_costo || 1), 1);
  return moneda === 'USD' ? costo * tipoCambio : costo;
}

function cuentaDestino(
  etapaNombre: string,
  ciclo: string,
  cuentas: Map<string, Cuenta>,
) {
  const nombre = etapaNombre.toLowerCase();
  const esRC = ciclo === 'RC' || ciclo === 'RC2' || /\brc\b|segunda|ii cosecha/.test(nombre);
  let codigo: string;
  if (/semill/.test(nombre)) codigo = CUENTAS_ACTIVO.semillero;
  else if (/barbecho|preparaci/.test(nombre)) codigo = CUENTAS_ACTIVO.barbecho;
  else if (/forza|post forza/.test(nombre)) codigo = esRC ? CUENTAS_ACTIVO.forzaRC : CUENTAS_ACTIVO.forzaPC;
  else codigo = esRC ? CUENTAS_ACTIVO.desarrolloRC : CUENTAS_ACTIVO.desarrolloPC;
  return cuentas.get(codigo) || null;
}

function Kpi({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  tone: string;
}) {
  return (
    <div className="rounded-lg border px-4 py-3" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
      <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider" style={{ color: tone }}>
        {icon}
        {label}
      </div>
      <div className="font-mono text-xl font-bold" style={{ color: 'var(--ink)' }}>{value}</div>
    </div>
  );
}

export function ActivoBiologicoInsumosPage() {
  const empresaId = useEmpresaId();
  const [fechaDesde, setFechaDesde] = useState(addMonths(isoToday(), -3));
  const [fechaHasta, setFechaHasta] = useState(isoToday());
  const [criterioFecha, setCriterioFecha] = useState<'aplicacion' | 'programacion'>('aplicacion');
  const [tabActivo, setTabActivo] = useState<'resumen' | 'expediente' | 'politica' | 'simulador' | 'trazabilidad' | 'sin-movimiento' | 'regularizacion' | 'cierre' | 'preparacion' | 'preasiento' | 'transicion' | 'medicion'>('resumen');
  const [filas, setFilas] = useState<Fila[]>([]);
  const [sinMovimiento, setSinMovimiento] = useState<AplicacionSinMovimiento[]>([]);
  const [regularizacion, setRegularizacion] = useState<RegularizacionHistorica[]>([]);
  const [busquedaRegularizacion, setBusquedaRegularizacion] = useState('');
  const [trazabilidad, setTrazabilidad] = useState<TrazabilidadProducto[]>([]);
  const [productoTrazado, setProductoTrazado] = useState('');
  const [busquedaProducto, setBusquedaProducto] = useState('');
  const [soloCostoCero, setSoloCostoCero] = useState(false);
  const [editando, setEditando] = useState<TrazabilidadProducto | null>(null);
  const [busquedaCorreccion, setBusquedaCorreccion] = useState('');
  const [resultadosCorreccion, setResultadosCorreccion] = useState<ProductoCorreccion[]>([]);
  const [productoCorreccion, setProductoCorreccion] = useState<ProductoCorreccion | null>(null);
  const [motivoCorreccion, setMotivoCorreccion] = useState('');
  const [guardandoCorreccion, setGuardandoCorreccion] = useState(false);
  const [errorCorreccion, setErrorCorreccion] = useState('');
  const [mostrarSinMovimiento, setMostrarSinMovimiento] = useState(false);
  const [cuentas, setCuentas] = useState<Cuenta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [costosCongelados, setCostosCongelados] = useState<CostoCongelado[]>([]);
  const [expedientes, setExpedientes] = useState<ExpedienteLote[]>([]);
  const [busquedaExpediente, setBusquedaExpediente] = useState('');
  const [confirmarPreparacion, setConfirmarPreparacion] = useState(false);
  const [preparando, setPreparando] = useState(false);
  const [resultadoPreparacion, setResultadoPreparacion] = useState<ResultadoPreparacion | null>(null);
  const [preasientos, setPreasientos] = useState<PreasientoAB[]>([]);
  const [lineasPreasiento, setLineasPreasiento] = useState<LineaPreasientoAB[]>([]);
  const [fechaContable, setFechaContable] = useState(isoToday());
  const [confirmarPreasiento, setConfirmarPreasiento] = useState(false);
  const [generandoPreasiento, setGenerandoPreasiento] = useState(false);
  const [preasientoAnular, setPreasientoAnular] = useState<PreasientoAB | null>(null);
  const [motivoAnulacion, setMotivoAnulacion] = useState('');
  const [anulandoPreasiento, setAnulandoPreasiento] = useState(false);
  const [estadoCierre, setEstadoCierre] = useState<EstadoCierreAB | null>(null);
  const [estadoToma, setEstadoToma] = useState<EstadoTomaFinca | null>(null);
  const [controlIntegral, setControlIntegral] = useState<ControlIntegralAB | null>(null);
  const [cargandoCierre, setCargandoCierre] = useState(false);
  const [productoSimulado, setProductoSimulado] = useState('');
  const [costoSimulado, setCostoSimulado] = useState('100000');
  const [porcentajePC, setPorcentajePC] = useState('100');
  const [criterioDistribucion, setCriterioDistribucion] = useState<'directa' | 'porcentaje' | 'dosis' | 'hectareas' | 'plantas'>('directa');
  const [fundamentoSimulacion, setFundamentoSimulacion] = useState('');
  const [evidenciaSimulacion, setEvidenciaSimulacion] = useState('');
  const [vigenciaDesdeRegla, setVigenciaDesdeRegla] = useState(isoToday());
  const [vigenciaHastaRegla, setVigenciaHastaRegla] = useState('');
  const [reglasCapitalizacion, setReglasCapitalizacion] = useState<ReglaCapitalizacion[]>([]);
  const [guardandoRegla, setGuardandoRegla] = useState(false);
  const [accionRegla, setAccionRegla] = useState('');
  const [mensajeRegla, setMensajeRegla] = useState('');
  const [transicion, setTransicion] = useState<TransicionABResumen | null>(null);
  const [observacionesTransicion, setObservacionesTransicion] = useState<TransicionABObservacion[]>([]);
  const [clasificacionTransicion, setClasificacionTransicion] = useState<TransicionABClasificacionResumen | null>(null);
  const [lotesTransicion, setLotesTransicion] = useState<TransicionABLoteCiclo[]>([]);
  const [fondoSemillero, setFondoSemillero] = useState<TransicionABFondoSemillero | null>(null);
  const [distribucionSemillero, setDistribucionSemillero] = useState<TransicionABDistribucionSemillero[]>([]);
  const [mostrarDistribucionSemillero, setMostrarDistribucionSemillero] = useState(false);
  const [conciliacionTransicion, setConciliacionTransicion] = useState<TransicionABConciliacionResumen | null>(null);
  const [cuentasConciliacion, setCuentasConciliacion] = useState<TransicionABConciliacionCuenta[]>([]);
  const [mostrarConciliacion, setMostrarConciliacion] = useState(false);
  const [politicaLegacy, setPoliticaLegacy] = useState<TransicionABPoliticaLegacy | null>(null);
  const [mostrarPoliticaLegacy, setMostrarPoliticaLegacy] = useState(false);
  const [confirmarPoliticaLegacy, setConfirmarPoliticaLegacy] = useState(false);
  const [procesandoPoliticaLegacy, setProcesandoPoliticaLegacy] = useState(false);
  const [mensajePoliticaLegacy, setMensajePoliticaLegacy] = useState('');
  const [mediciones, setMediciones] = useState<MedicionNIIF[]>([]);
  const [parametroMedicion, setParametroMedicion] = useState<ParametroMedicion | null>(null);
  const [capacidadesMedicion, setCapacidadesMedicion] = useState<CapacidadesMedicion>({
    puede_ver: false,
    puede_editar: false,
    puede_aprobar: false,
  });
  const [conciliacionMedicion, setConciliacionMedicion] = useState<ConciliacionMedicion | null>(null);
  const [configuracionMedicion, setConfiguracionMedicion] = useState<ConfiguracionMedicion | null>(null);
  const [cortesMedicion, setCortesMedicion] = useState<CorteMedicion[]>([]);
  const [modeloEtapas, setModeloEtapas] = useState<ModeloEtapas | null>(null);
  const [modeloEtapasDetalle, setModeloEtapasDetalle] = useState<ModeloEtapaDetalle[]>([]);
  const [cronologiaLotes, setCronologiaLotes] = useState<CronologiaLoteControl[]>([]);
  const [programaCostoFuente, setProgramaCostoFuente] = useState<ProgramaCostoFuente | null>(null);
  const [planCostosEtapa, setPlanCostosEtapa] = useState<PlanCostoEtapaControl[]>([]);
  const [planSnapshot, setPlanSnapshot] = useState<PlanSnapshot | null>(null);
  const [fuentesCostoCompletar, setFuentesCostoCompletar] = useState<FuenteCostoCompletar[]>([]);
  const [moConductorValidaciones, setMoConductorValidaciones] = useState<MoConductorValidacion[]>([]);
  const [moFasesCalibracion, setMoFasesCalibracion] = useState<MoFaseCalibracion[]>([]);
  const [costosFuturosLote, setCostosFuturosLote] = useState<CostoFuturoLote[]>([]);
  const [pruebasIntegrales, setPruebasIntegrales] = useState<PruebaIntegral[]>([]);
  const [primaRiesgoTasa, setPrimaRiesgoTasa] = useState('');
  const [fundamentoTasa, setFundamentoTasa] = useState('');
  const [procesandoTasa, setProcesandoTasa] = useState(false);
  const [mensajeTasa, setMensajeTasa] = useState('');
  const [cuentaAjusteMedicion, setCuentaAjusteMedicion] = useState('');
  const [cuentaGananciaMedicion, setCuentaGananciaMedicion] = useState('');
  const [cuentaPerdidaMedicion, setCuentaPerdidaMedicion] = useState('');
  const [fechaCorteMedicion, setFechaCorteMedicion] = useState(isoToday());
  const [procesandoCorteMedicion, setProcesandoCorteMedicion] = useState(false);
  const [mostrarConciliacionMedicion, setMostrarConciliacionMedicion] = useState(false);
  const [costoHistoricoResumen, setCostoHistoricoResumen] = useState<CostoHistoricoResumen | null>(null);
  const [costosHistoricosDetalle, setCostosHistoricosDetalle] = useState<CostoHistoricoDetalle[]>([]);
  const [saldosHistoricosEtapa, setSaldosHistoricosEtapa] = useState<SaldoHistoricoEtapa[]>([]);
  const [ventaHistoricaResumen, setVentaHistoricaResumen] = useState<VentaHistoricaResumen | null>(null);
  const [ventasHistoricasProducto, setVentasHistoricasProducto] = useState<VentaHistoricaProducto[]>([]);
  const [ventasHistoricasExcepcion, setVentasHistoricasExcepcion] = useState<VentaHistoricaExcepcion[]>([]);
  const [calibracionVentasHistoricas, setCalibracionVentasHistoricas] = useState<CalibracionVentaHistorica[]>([]);
  const [costosVentaHistoricos, setCostosVentaHistoricos] = useState<CostoVentaHistorico[]>([]);
  const [costosVentaEscenarios, setCostosVentaEscenarios] = useState<CostoVentaEscenario[]>([]);
  const [busquedaVentaHistorica, setBusquedaVentaHistorica] = useState('');
  const [estadoVentaHistorica, setEstadoVentaHistorica] = useState<'TODOS' | VentaHistoricaProducto['estado_fiscal']>('ACEPTADO');
  const [mostrarVentasHistoricas, setMostrarVentasHistoricas] = useState(false);
  const [mostrarCostosVentaHistoricos, setMostrarCostosVentaHistoricos] = useState(false);
  const [mostrarBaseHistorica, setMostrarBaseHistorica] = useState(false);
  const [editandoParametros, setEditandoParametros] = useState(false);
  const [confirmandoParametros, setConfirmandoParametros] = useState(false);
  const [guardandoParametros, setGuardandoParametros] = useState(false);
  const [adoptandoRecomendacion, setAdoptandoRecomendacion] = useState(false);
  const [costoVentaEmpaque, setCostoVentaEmpaque] = useState('');
  const [costoVentaJugo, setCostoVentaJugo] = useState('');
  const [evidenciaParametros, setEvidenciaParametros] = useState('');
  const [fundamentoParametros, setFundamentoParametros] = useState('');
  const [cargandoMediciones, setCargandoMediciones] = useState(false);
  const [medicionEditando, setMedicionEditando] = useState<MedicionNIIF | null>(null);
  const [mensajeMedicion, setMensajeMedicion] = useState('');
  const [filtroMedicion, setFiltroMedicion] = useState('');
  const [filtroClasificacion, setFiltroClasificacion] = useState('revision');
  const [decisionPorConfirmar, setDecisionPorConfirmar] = useState<string | null>(null);
  const [procesandoDecision, setProcesandoDecision] = useState(false);
  const [mensajeDecision, setMensajeDecision] = useState('');
  const [cargandoTransicion, setCargandoTransicion] = useState(false);

  const cargarTransicion = useCallback(async () => {
    setCargandoTransicion(true);
    const [
      { data: resumenData, error: resumenError },
      { data: observacionesData, error: observacionesError },
      { data: clasificacionData, error: clasificacionError },
      { data: lotesData, error: lotesError },
      { data: fondoData, error: fondoError },
      { data: distribucionData, error: distribucionError },
      { data: conciliacionData, error: conciliacionError },
      { data: cuentasConciliacionData, error: cuentasConciliacionError },
      { data: politicaLegacyData, error: politicaLegacyError },
    ] = await Promise.all([
      supabase.rpc('app_ab_transicion_resumen', { p_empresa_id: empresaId }),
      supabase
        .from('app_ab_transicion_lineas_control')
        .select('id,nboleta,fecha_aplicacion,lote_num,articulo,producto_nombre,valor_vfp_crc,estado,es_solapamiento_erp')
        .eq('empresa_id', empresaId)
        .not('estado_control', 'like', 'excluido_%')
        .neq('estado_control', 'pendiente_conciliar')
        .order('valor_vfp_crc', { ascending: false })
        .limit(100),
      supabase.rpc('app_ab_transicion_clasificacion_resumen', { p_empresa_id: empresaId }),
      supabase
        .from('app_ab_transicion_lote_ciclo_revision')
        .select('id,lote_num,ciclo,cedulas,lineas,valor_vfp_crc,bloques_encontrados,bloques_vivos,bloques_cosechados,clasificacion,criterio,fecha_corte,decision_id,decision_propuesta,ciclo_destino,decision_fundamento,decision_estado,clasificacion_efectiva')
        .eq('empresa_id', empresaId)
        .order('valor_vfp_crc', { ascending: false }),
      supabase
        .from('app_ab_transicion_semillero_fondos')
        .select('id,fecha_desde,fecha_hasta,metodo,valor_fondo_crc,total_conductor,lotes_beneficiarios,fundamento,limitacion_origen,estado')
        .eq('empresa_id', empresaId)
        .order('fecha_hasta', { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from('app_ab_transicion_semillero_distribucion')
        .select('id,lote_num,fecha_inicio_pc,bloques,plantas_sembradas,area_ha,proporcion,valor_asignado_crc')
        .eq('empresa_id', empresaId)
        .order('fecha_inicio_pc', { ascending: true }),
      supabase.rpc('app_ab_transicion_conciliacion_resumen', { p_empresa_id: empresaId }),
      supabase
        .from('app_ab_transicion_conciliacion_cuentas_v2')
        .select('id,cuenta_codigo,cuenta_nombre,saldo_legacy_crc,adicion_viva_crc,adicion_cosechada_crc,adicion_total_crc,base_puente_crc,estado,observacion')
        .eq('empresa_id', empresaId)
        .order('cuenta_codigo', { ascending: true }),
      supabase
        .from('app_ab_transicion_politicas_legacy')
        .select('id,version,saldo_legacy_crc,base_puente_crc,costos_cosechados_2026_crc,enfoque_contable,enfoque_fiscal,decision_transitoria,alternativas,insumos_faltantes,evidencia_calidad,fuentes,estado,aprobado_at')
        .eq('empresa_id', empresaId)
        .order('version', { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);
    setCargandoTransicion(false);
    if (resumenError || observacionesError || clasificacionError || lotesError || fondoError || distribucionError || conciliacionError || cuentasConciliacionError || politicaLegacyError) {
      setError(resumenError?.message || observacionesError?.message || 'No se pudo cargar la transición 2026.');
      return;
    }
    setTransicion(resumenData as TransicionABResumen);
    setObservacionesTransicion((observacionesData || []) as TransicionABObservacion[]);
    setClasificacionTransicion(clasificacionData as TransicionABClasificacionResumen);
    setLotesTransicion((lotesData || []) as TransicionABLoteCiclo[]);
    setFondoSemillero((fondoData || null) as TransicionABFondoSemillero | null);
    setDistribucionSemillero((distribucionData || []) as TransicionABDistribucionSemillero[]);
    setConciliacionTransicion(conciliacionData as TransicionABConciliacionResumen);
    setCuentasConciliacion((cuentasConciliacionData || []) as TransicionABConciliacionCuenta[]);
    setPoliticaLegacy((politicaLegacyData || null) as TransicionABPoliticaLegacy | null);
  }, [empresaId]);

  const aprobarDecisionTransicion = async (decisionId: string) => {
    setProcesandoDecision(true);
    setMensajeDecision('');
    const { error: aprobarError } = await supabase.rpc('app_ab_aprobar_decision_transicion', {
      p_empresa_id: empresaId,
      p_decision_id: decisionId,
    });
    setProcesandoDecision(false);
    if (aprobarError) {
      setMensajeDecision(aprobarError.message || 'No se pudo aprobar la propuesta.');
      return;
    }
    setDecisionPorConfirmar(null);
    setMensajeDecision('Propuesta aprobada y registrada. No se generó ningún asiento.');
    await cargarTransicion();
  };

  const aprobarPoliticaLegacy = async () => {
    if (!politicaLegacy) return;
    setProcesandoPoliticaLegacy(true);
    setMensajePoliticaLegacy('');
    const { error: politicaError } = await supabase.rpc('app_ab_aprobar_politica_legacy', {
      p_empresa_id: empresaId,
      p_politica_id: politicaLegacy.id,
    });
    setProcesandoPoliticaLegacy(false);
    if (politicaError) {
      setMensajePoliticaLegacy(politicaError.message || 'No se pudo aprobar la política.');
      return;
    }
    setConfirmarPoliticaLegacy(false);
    setMensajePoliticaLegacy('Política aprobada. La aprobación no generó asientos.');
    await cargarTransicion();
  };

  const cargarMediciones = useCallback(async () => {
    setCargandoMediciones(true);
    setMensajeMedicion('');
    const [
      { data, error: medicionError },
      { data: parametroData, error: parametroError },
      { data: conciliacionData, error: conciliacionError },
      { data: costoHistoricoData, error: costoHistoricoError },
      { data: costosHistoricosDetalleData, error: costosHistoricosDetalleError },
      { data: saldosHistoricosEtapaData, error: saldosHistoricosEtapaError },
      { data: ventaHistoricaResumenData, error: ventaHistoricaResumenError },
      { data: ventasHistoricasProductoData, error: ventasHistoricasProductoError },
      { data: ventasHistoricasExcepcionData, error: ventasHistoricasExcepcionError },
      { data: calibracionVentasHistoricasData, error: calibracionVentasHistoricasError },
      { data: costosVentaHistoricosData, error: costosVentaHistoricosError },
      { data: costosVentaEscenariosData, error: costosVentaEscenariosError },
      { data: capacidadesMedicionData, error: capacidadesMedicionError },
      { data: configuracionMedicionData, error: configuracionMedicionError },
      { data: cortesMedicionData, error: cortesMedicionError },
    ] = await Promise.all([
      supabase
        .from('app_ab_mediciones_niif_control')
        .select('id,lote_num,ciclo,version,fecha_medicion,plantas,area_ha,costo_reconstruido_crc,frutos_por_planta,peso_kg_por_fruto,merma_pct,probabilidad_cosecha_pct,precio_crc_kg,costo_venta_crc_kg,evidencia_referencia,observacion,estado,valor_razonable_neto_crc,kg_esperados,datos_completos,parametro_id,densidad_plantas_ha,mortalidad_modelo_pct,empaque_pct,jugo_pct,peso_empaque_kg,peso_jugo_kg,precio_empaque_crc_kg,precio_jugo_crc_kg,costo_venta_empaque_crc_kg,costo_venta_jugo_crc_kg,plantas_sembradas_estimadas,plantas_productivas_estimadas,kg_empaque_estimados,kg_jugo_estimados,valor_bruto_modelo_crc,costos_venta_modelo_crc,valor_razonable_modelo_crc,modelo_productivo_completo,costos_venta_completos')
        .eq('empresa_id', empresaId)
        .neq('estado', 'sustituida')
        .order('lote_num', { ascending: true })
        .order('ciclo', { ascending: true }),
      supabase
        .from('app_ab_parametros_medicion')
        .select('id,version,vigencia_desde,densidad_plantas_ha,mortalidad_pct,empaque_pct,jugo_pct,peso_empaque_kg,peso_jugo_kg,precio_empaque_crc_kg,precio_jugo_crc_kg,costo_venta_empaque_crc_kg,costo_venta_jugo_crc_kg,evidencia_referencia,fundamento,estado,aprobado_at')
        .eq('empresa_id', empresaId)
        .order('version', { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from('app_ab_medicion_conciliaciones')
        .select('id,version,fecha_medicion,saldo_legacy_crc,costos_2026_crc,valor_libros_puente_crc,valor_bruto_crc,costos_venta_crc,valor_razonable_neto_crc,diferencia_medicion_crc,naturaleza_diferencia,cuenta_activo_ra_codigo,cuenta_resultado_ra_codigo,base_fiscal_estado,estado,criterio,limitaciones,motivo_revision')
        .eq('empresa_id', empresaId)
        .neq('estado', 'sustituida')
        .order('version', { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from('app_ab_costos_historicos_resumen')
        .select('fuente_id,periodo_desde,periodo_hasta,area_referencia_ha,documento_referencia,naturaleza,estado,total_documentado_crc,costo_campo_real_crc,costo_campo_crc_ha_anio,costo_empaque_referencia_crc,total_excluido_crc,habilita_medicion,bloqueo_medicion')
        .eq('empresa_id', empresaId)
        .neq('estado', 'sustituida')
        .order('periodo_hasta', { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from('app_ab_costos_historicos_detalle')
        .select('id,orden,codigo,concepto,importe_crc,uso_modelo,justificacion')
        .eq('empresa_id', empresaId)
        .order('orden', { ascending: true }),
      supabase
        .from('app_ab_saldos_historicos_etapa')
        .select('id,orden,etapa_codigo,etapa_nombre,ciclo,area_ha,saldo_crc,costo_crc_ha,advertencia')
        .eq('empresa_id', empresaId)
        .order('orden', { ascending: true }),
      supabase
        .from('app_ab_ventas_historicas_resumen')
        .select('fuente_id,periodo_desde,periodo_hasta,venta_contabilizada_crc,venta_aceptada_contabilizada_crc,venta_rechazada_contabilizada_crc,venta_procesando_no_mayorizada_crc,venta_operativa_valida_crc,documentos_contables,documentos_rechazados,documentos_procesando,fuente_contable,fuente_producto,fuente_fiscal,criterio,habilita_medicion')
        .eq('empresa_id', empresaId)
        .order('periodo_hasta', { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from('app_ab_ventas_historicas_producto')
        .select('id,producto_codigo,producto_nombre,categoria,estado_fiscal,documentos,cantidad_neta,importe_crc')
        .eq('empresa_id', empresaId)
        .order('estado_fiscal', { ascending: true })
        .order('importe_crc', { ascending: false }),
      supabase
        .from('app_ab_ventas_historicas_excepcion')
        .select('id,documento,fecha,estado_fiscal,categoria,importe_contable_crc,importe_operativo_crc,tratamiento')
        .eq('empresa_id', empresaId)
        .order('fecha', { ascending: true }),
      supabase
        .from('app_ab_calibracion_ventas_historicas')
        .select('id,codigo,destino,unidad_origen,cantidad_origen,peso_supuesto_kg,cantidad_estimada_kg,venta_aceptada_crc,venta_crc_unidad,costo_empaque_asignado_crc,costo_empaque_crc_kg,cobertura_dato_directo_pct,estado,advertencia,habilita_parametro')
        .eq('empresa_id', empresaId)
        .order('destino', { ascending: true })
        .order('codigo', { ascending: true }),
      supabase
        .from('app_ab_costos_venta_historicos')
        .select('id,orden,cuenta_codigo,concepto,importe_crc,clasificacion,tratamiento_nic41,requiere_contrato,incluido_rango,habilita_parametro')
        .eq('empresa_id', empresaId)
        .order('orden', { ascending: true }),
      supabase
        .from('app_ab_costos_venta_escenarios')
        .select('id,codigo,descripcion,kg_exportacion,costo_minimo_crc,costo_maximo_crc,costo_minimo_crc_kg,costo_maximo_crc_kg,criterio,estado,habilita_parametro')
        .eq('empresa_id', empresaId)
        .order('codigo', { ascending: true }),
      supabase.rpc('app_ab_capacidades_medicion', {
        p_empresa_id: empresaId,
      }),
      supabase
        .from('app_ab_medicion_configuracion')
        .select('cuenta_ajuste_activo_id,cuenta_ganancia_id,cuenta_perdida_id')
        .eq('empresa_id', empresaId)
        .maybeSingle(),
      supabase
        .from('app_ab_medicion_cortes')
        .select('id,fecha_medicion,version,valor_libros_crc,valor_bruto_crc,costos_venta_crc,valor_razonable_neto_crc,diferencia_crc,naturaleza,cuenta_ajuste_activo_id,cuenta_resultado_id,estado,asiento_id,preparado_at,revisado_at,aprobado_at,contabilizado_at,evidencia')
        .eq('empresa_id', empresaId)
        .order('fecha_medicion', { ascending: false })
        .order('version', { ascending: false })
        .limit(12),
    ]);
    setCargandoMediciones(false);
    if (medicionError || parametroError || conciliacionError || costoHistoricoError
      || costosHistoricosDetalleError || saldosHistoricosEtapaError
      || ventaHistoricaResumenError || ventasHistoricasProductoError || ventasHistoricasExcepcionError
      || calibracionVentasHistoricasError || costosVentaHistoricosError || costosVentaEscenariosError
      || capacidadesMedicionError || configuracionMedicionError || cortesMedicionError) {
      setMensajeMedicion(medicionError?.message || parametroError?.message || conciliacionError?.message
        || costoHistoricoError?.message || costosHistoricosDetalleError?.message || saldosHistoricosEtapaError?.message
        || ventaHistoricaResumenError?.message || ventasHistoricasProductoError?.message || ventasHistoricasExcepcionError?.message
        || calibracionVentasHistoricasError?.message || costosVentaHistoricosError?.message || costosVentaEscenariosError?.message
        || capacidadesMedicionError?.message || configuracionMedicionError?.message || cortesMedicionError?.message
        || 'No se pudo cargar el expediente de medición.');
      return;
    }
    setMediciones((data || []) as MedicionNIIF[]);
    setParametroMedicion((parametroData || null) as ParametroMedicion | null);
    setConciliacionMedicion((conciliacionData || null) as ConciliacionMedicion | null);
    setCostoHistoricoResumen((costoHistoricoData || null) as CostoHistoricoResumen | null);
    setCostosHistoricosDetalle((costosHistoricosDetalleData || []) as CostoHistoricoDetalle[]);
    setSaldosHistoricosEtapa((saldosHistoricosEtapaData || []) as SaldoHistoricoEtapa[]);
    setVentaHistoricaResumen((ventaHistoricaResumenData || null) as VentaHistoricaResumen | null);
    setVentasHistoricasProducto((ventasHistoricasProductoData || []) as VentaHistoricaProducto[]);
    setVentasHistoricasExcepcion((ventasHistoricasExcepcionData || []) as VentaHistoricaExcepcion[]);
    setCalibracionVentasHistoricas((calibracionVentasHistoricasData || []) as CalibracionVentaHistorica[]);
    setCostosVentaHistoricos((costosVentaHistoricosData || []) as CostoVentaHistorico[]);
    setCostosVentaEscenarios((costosVentaEscenariosData || []) as CostoVentaEscenario[]);
    setCapacidadesMedicion((capacidadesMedicionData || {
      puede_ver: false,
      puede_editar: false,
      puede_aprobar: false,
    }) as CapacidadesMedicion);
    const configuracion = (configuracionMedicionData || null) as ConfiguracionMedicion | null;
    setConfiguracionMedicion(configuracion);
    setCortesMedicion((cortesMedicionData || []) as CorteMedicion[]);
    const { data: modelosEtapaData, error: modelosEtapaError } = await supabase
      .from('app_ab_modelos_etapa_control')
      .select('id,version,vigencia_desde,estado,etapas,etapas_completas,fundamento,evidencia_referencia')
      .eq('empresa_id', empresaId)
      .order('version', { ascending: false })
      .limit(1);
    if (modelosEtapaError) {
      setMensajeMedicion(modelosEtapaError.message || 'No se pudo cargar el modelo de etapas.');
    } else {
      const modelo = ((modelosEtapaData || [])[0] || null) as ModeloEtapas | null;
      setModeloEtapas(modelo);
      if (modelo) {
        const { data: detalleEtapaData, error: detalleEtapaError } = await supabase
          .from('app_ab_modelos_etapa_detalle')
          .select('id,etapa_codigo,etapa_nombre,ciclo,orden,dias_restantes,metodo_dias,evento_ancla,duracion_desde_ancla_dias,tolerancia_dias,probabilidad_realizacion_pct,costo_completar_crc_ha,tasa_descuento_anual_pct,fundamento,evidencia_referencia')
          .eq('modelo_id', modelo.id)
          .order('orden', { ascending: true });
        if (detalleEtapaError) setMensajeMedicion(detalleEtapaError.message || 'No se pudo cargar el detalle por etapa.');
        setModeloEtapasDetalle((detalleEtapaData || []) as ModeloEtapaDetalle[]);
      } else {
        setModeloEtapasDetalle([]);
      }
    }
    const fechaCronologia = (conciliacionData as ConciliacionMedicion | null)?.fecha_medicion
      || ((data || [])[0] as MedicionNIIF | undefined)?.fecha_medicion;
    if (fechaCronologia) {
      const { data: cronologiaData, error: cronologiaError } = await supabase
        .from('app_ab_cronologia_lote_control')
        .select('medicion_id,fecha_medicion,lote_num,ciclo,area_ha,bloques_al_corte,bloques_fecha_inconsistente,bloques_sin_induccion,fecha_siembra_desde,fecha_siembra_hasta,fecha_induccion_desde,fecha_induccion_hasta,etapa_sugerida,dias_restantes_min_referencia,dias_restantes_max_referencia,control_cronologia')
        .eq('empresa_id', empresaId)
        .eq('fecha_medicion', fechaCronologia)
        .order('lote_num', { ascending: true });
      if (cronologiaError) setMensajeMedicion(cronologiaError.message || 'No se pudo cargar el control de cronología.');
      setCronologiaLotes((cronologiaData || []) as CronologiaLoteControl[]);
    } else {
      setCronologiaLotes([]);
    }
    const { data: programaCostoData, error: programaCostoError } = await supabase
      .from('app_ab_programas_costo_fuente')
      .select('id,codigo,version,ciclo,etapa_codigo,fecha_documento,documento_referencia,moneda,total_ha,estado,limitaciones')
      .eq('empresa_id', empresaId)
      .neq('estado', 'sustituido')
      .order('version', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (programaCostoError) setMensajeMedicion(programaCostoError.message || 'No se pudo cargar el programa técnico de costos.');
    setProgramaCostoFuente((programaCostoData || null) as ProgramaCostoFuente | null);
    const { data: planCostosData, error: planCostosError } = await supabase
      .from('app_ab_plan_costos_etapa_control')
      .select('etapa_codigo,etapa,ciclo_cosecha,ciclos,lineas,lineas_sin_costo,dia_minimo,dia_maximo,costo_programa_crc_ha,costo_programa_usd_ha,completo,advertencia')
      .eq('empresa_id', empresaId)
      .order('ciclo_cosecha', { ascending: true })
      .order('dia_minimo', { ascending: true });
    if (planCostosError) setMensajeMedicion(planCostosError.message || 'No se pudo conciliar el Plan de Manejo.');
    setPlanCostosEtapa((planCostosData || []) as PlanCostoEtapaControl[]);
    const { data: planSnapshotData, error: planSnapshotError } = await supabase
      .from('app_ab_plan_snapshots')
      .select('id,version,fecha_corte,estado,lineas,lineas_sin_costo,costo_total_crc_ha,fundamento,evidencia_referencia')
      .eq('empresa_id', empresaId)
      .order('version', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (planSnapshotError) setMensajeMedicion(planSnapshotError.message || 'No se pudo cargar la fotografía del Plan de Manejo.');
    setPlanSnapshot((planSnapshotData || null) as PlanSnapshot | null);
    const { data: fuentesCostoData, error: fuentesCostoError } = await supabase
      .from('app_ab_fuentes_costos_completar')
      .select('id,codigo,componente,fuente_erp,conductor_requerido,nivel_asignacion,tratamiento,estado,evidencia_referencia,observacion')
      .eq('empresa_id', empresaId)
      .order('codigo', { ascending: true });
    if (fuentesCostoError) setMensajeMedicion(fuentesCostoError.message || 'No se pudo cargar la matriz de costos futuros.');
    setFuentesCostoCompletar((fuentesCostoData || []) as FuenteCostoCompletar[]);
    const [{ data: moValidacionData, error: moValidacionError }, { data: moFasesData, error: moFasesError }] = await Promise.all([
      supabase
        .from('app_ab_mo_conductor_validaciones')
        .select('id,anio,claves_evaluadas,claves_distribucion_multiple,coincidencias_plantas,coincidencia_pct,desviacion_mediana,fuente,conclusion')
        .eq('empresa_id', empresaId)
        .order('anio', { ascending: true }),
      supabase
        .from('app_ab_mo_fase_calibracion')
        .select('id,fase_codigo,lineas,horas,monto_crc,uso_modelo,incluido_costo_cultivo')
        .eq('empresa_id', empresaId)
        .order('monto_crc', { ascending: false }),
    ]);
    if (moValidacionError || moFasesError) setMensajeMedicion(moValidacionError?.message || moFasesError?.message || 'No se pudo cargar la calibración histórica de mano de obra.');
    setMoConductorValidaciones((moValidacionData || []) as MoConductorValidacion[]);
    setMoFasesCalibracion((moFasesData || []) as MoFaseCalibracion[]);
    const { data: costosFuturosData, error: costosFuturosError } = await supabase
      .from('app_ab_medicion_integral_lote_control')
      .select('medicion_id,lote_num,ciclo,area_ha,etapas,dias_hasta_cosecha,productos_pendientes_crc,mano_obra_pendiente_crc,cosecha_crc,acarreo_central_crc,soporte_bajo_crc,soporte_central_crc,soporte_alto_crc,costo_futuro_bajo_crc,costo_futuro_central_crc,costo_futuro_alto_crc,valor_bruto_modelo_crc,costos_venta_modelo_crc,valor_neto_pre_descuento_bajo_crc,valor_neto_pre_descuento_central_crc,valor_neto_pre_descuento_alto_crc,tasa_fecha_referencia,tasa_base_anual_pct,prima_riesgo_pct,tasa_total_anual_pct,valor_presente_referencia_base_crc,tasa_referencia_id,tasa_estado,tasa_fuente,tasa_url_fuente,tasa_fundamento,tasa_aplicada_control_pct,valor_presente_tasa_control_crc,control_descuento,control_integral,base_calculable,control')
      .eq('empresa_id', empresaId)
      .order('costo_futuro_central_crc', { ascending: false, nullsFirst: false });
    if (costosFuturosError) setMensajeMedicion(costosFuturosError.message || 'No se pudo cargar la proyeccion de costos futuros por lote.');
    setCostosFuturosLote((costosFuturosData || []) as CostoFuturoLote[]);
    const tasaActual = ((costosFuturosData || [])[0] || null) as CostoFuturoLote | null;
    if (tasaActual) {
      setPrimaRiesgoTasa(tasaActual.prima_riesgo_pct == null ? '' : String(tasaActual.prima_riesgo_pct));
      setFundamentoTasa(tasaActual.tasa_fundamento || '');
    }
    const { data: pruebasData, error: pruebasError } = await supabase
      .from('app_ab_pruebas_integrales')
      .select('id,codigo,lote_num,ciclo,fecha_medicion,area_ha,plantas,plantas_productivas,kg_esperados,dias_hasta_cosecha,saldo_reconstruido_crc,valor_bruto_crc,costos_venta_crc,costo_futuro_bajo_crc,costo_futuro_central_crc,costo_futuro_alto_crc,tasa_control_pct,valor_presente_control_crc,diferencia_saldo_crc,naturaleza_diferencia,control_tasa,estado,fundamento,evidencia')
      .eq('empresa_id', empresaId)
      .order('creado_at', { ascending: false });
    if (pruebasError) setMensajeMedicion(pruebasError.message || 'No se pudieron cargar las pruebas integrales.');
    setPruebasIntegrales((pruebasData || []) as PruebaIntegral[]);
    setCuentaAjusteMedicion(configuracion ? String(configuracion.cuenta_ajuste_activo_id) : '');
    setCuentaGananciaMedicion(configuracion ? String(configuracion.cuenta_ganancia_id) : '');
    setCuentaPerdidaMedicion(configuracion ? String(configuracion.cuenta_perdida_id) : '');
  }, [empresaId]);

  const revisarTasaDescuento = async () => {
    const tasa = costosFuturosLote.find((item) => item.tasa_referencia_id);
    const prima = Number(primaRiesgoTasa);
    if (!tasa?.tasa_referencia_id || !Number.isFinite(prima) || prima < 0 || prima > 50) {
      setMensajeTasa('Ingrese una prima de riesgo válida entre 0% y 50%.');
      return;
    }
    if (fundamentoTasa.trim().length < 30) {
      setMensajeTasa('Documente en al menos 30 caracteres la evidencia de mercado utilizada.');
      return;
    }
    setProcesandoTasa(true); setMensajeTasa('');
    try {
      const { error } = await supabase.rpc('app_ab_revisar_tasa_descuento', {
        p_empresa_id: empresaId,
        p_tasa_id: tasa.tasa_referencia_id,
        p_prima_riesgo_pct: prima,
        p_fundamento: fundamentoTasa.trim(),
      });
      if (error) throw error;
      setMensajeTasa('Tasa documentada y enviada a revisión. Todavía no afecta un corte firme.');
      await cargar();
    } catch (err: any) {
      setMensajeTasa(err?.message || 'No se pudo revisar la tasa de descuento.');
    } finally { setProcesandoTasa(false); }
  };

  const aprobarTasaDescuento = async () => {
    const tasa = costosFuturosLote.find((item) => item.tasa_referencia_id);
    if (!tasa?.tasa_referencia_id) return;
    setProcesandoTasa(true); setMensajeTasa('');
    try {
      const { error } = await supabase.rpc('app_ab_aprobar_tasa_descuento', {
        p_empresa_id: empresaId,
        p_tasa_id: tasa.tasa_referencia_id,
      });
      if (error) throw error;
      setMensajeTasa('Tasa aprobada. El corte contable continúa requiriendo su revisión independiente.');
      await cargar();
    } catch (err: any) {
      setMensajeTasa(err?.message || 'No se pudo aprobar la tasa de descuento.');
    } finally { setProcesandoTasa(false); }
  };

  const iniciarEdicionParametros = () => {
    if (!parametroMedicion) return;
    setCostoVentaEmpaque(parametroMedicion.costo_venta_empaque_crc_kg == null ? '' : String(parametroMedicion.costo_venta_empaque_crc_kg));
    setCostoVentaJugo(parametroMedicion.costo_venta_jugo_crc_kg == null ? '' : String(parametroMedicion.costo_venta_jugo_crc_kg));
    setEvidenciaParametros(parametroMedicion.evidencia_referencia || '');
    setFundamentoParametros(parametroMedicion.fundamento || '');
    setMensajeMedicion('');
    setEditandoParametros(true);
  };

  const guardarParametrosMedicion = async () => {
    if (!parametroMedicion) return;
    setGuardandoParametros(true);
    setMensajeMedicion('');
    const { error: guardarError } = await supabase.rpc('app_ab_guardar_parametros_medicion', {
      p_empresa_id: empresaId,
      p_parametro_id: parametroMedicion.id,
      p_costo_venta_empaque_crc_kg: costoVentaEmpaque === '' ? null : Number(costoVentaEmpaque),
      p_costo_venta_jugo_crc_kg: costoVentaJugo === '' ? null : Number(costoVentaJugo),
      p_evidencia_referencia: evidenciaParametros,
      p_fundamento: fundamentoParametros,
    });
    setGuardandoParametros(false);
    if (guardarError) {
      setMensajeMedicion(guardarError.message || 'No se pudieron guardar los parámetros.');
      return;
    }
    setEditandoParametros(false);
    await cargarMediciones();
    setMensajeMedicion('Parámetros guardados. El valor neto fue recalculado sin generar asientos.');
  };

  const aprobarParametrosMedicion = async () => {
    if (!parametroMedicion) return;
    setGuardandoParametros(true);
    setMensajeMedicion('');
    const { error: aprobarError } = await supabase.rpc('app_ab_aprobar_parametros_medicion', {
      p_empresa_id: empresaId,
      p_parametro_id: parametroMedicion.id,
    });
    setGuardandoParametros(false);
    if (aprobarError) {
      setMensajeMedicion(aprobarError.message || 'No se pudo aprobar la versión.');
      return;
    }
    setConfirmandoParametros(false);
    await cargarMediciones();
    setMensajeMedicion('Versión productiva aprobada e inmutable. No se generó ningún asiento.');
  };

  const adoptarCostoVentaRecomendado = async () => {
    if (!parametroMedicion) return;
    setAdoptandoRecomendacion(true);
    setMensajeMedicion('');
    const { error: adoptarError } = await supabase.rpc('app_ab_adoptar_costo_venta_recomendado', {
      p_empresa_id: empresaId,
      p_parametro_id: parametroMedicion.id,
      p_escenario_codigo: 'EXPORT_11_5',
    });
    setAdoptandoRecomendacion(false);
    if (adoptarError) {
      setMensajeMedicion(adoptarError.message || 'No se pudo trasladar la recomendación a la propuesta.');
      return;
    }
    await cargarMediciones();
    setMensajeMedicion('Recomendación 11,5 kg trasladada a la propuesta. Queda pendiente de revisión y aprobación separada.');
  };

  const guardarCuentasMedicion = async () => {
    if (!cuentaAjusteMedicion || !cuentaGananciaMedicion || !cuentaPerdidaMedicion) {
      setMensajeMedicion('Seleccione las tres cuentas contables de la medición.');
      return;
    }
    setProcesandoCorteMedicion(true);
    setMensajeMedicion('');
    const { error: rpcError } = await supabase.rpc('app_ab_configurar_cuentas_medicion', {
      p_empresa_id: empresaId,
      p_cuenta_ajuste_activo_id: Number(cuentaAjusteMedicion),
      p_cuenta_ganancia_id: Number(cuentaGananciaMedicion),
      p_cuenta_perdida_id: Number(cuentaPerdidaMedicion),
    });
    setProcesandoCorteMedicion(false);
    if (rpcError) {
      setMensajeMedicion(rpcError.message || 'No se pudieron guardar las cuentas de medición.');
      return;
    }
    await cargarMediciones();
    setMensajeMedicion('Cuentas de medición guardadas. Se congelarán dentro de cada corte.');
  };

  const prepararCorteMedicion = async () => {
    setProcesandoCorteMedicion(true);
    setMensajeMedicion('');
    const { error: rpcError } = await supabase.rpc('app_ab_preparar_corte_medicion', {
      p_empresa_id: empresaId,
      p_fecha_medicion: fechaCorteMedicion,
    });
    setProcesandoCorteMedicion(false);
    if (rpcError) {
      setMensajeMedicion(rpcError.message || 'No se pudo preparar el corte de medición.');
      return;
    }
    await cargarMediciones();
    setMensajeMedicion('Corte NIC 41 preparado y congelado. Aún no genera asiento.');
  };

  const ejecutarAccionCorte = async (corte: CorteMedicion, accion: 'revisar' | 'aprobar' | 'contabilizar') => {
    const rpc = accion === 'revisar'
      ? 'app_ab_revisar_corte_medicion'
      : accion === 'aprobar'
        ? 'app_ab_aprobar_corte_medicion'
        : 'app_ab_contabilizar_corte_medicion';
    setProcesandoCorteMedicion(true);
    setMensajeMedicion('');
    const { error: rpcError } = await supabase.rpc(rpc, {
      p_empresa_id: empresaId,
      p_corte_id: corte.id,
    });
    setProcesandoCorteMedicion(false);
    if (rpcError) {
      setMensajeMedicion(rpcError.message || `No se pudo ${accion} el corte.`);
      return;
    }
    await cargarMediciones();
    setMensajeMedicion(accion === 'contabilizar'
      ? 'Medición NIC 41 contabilizada y vinculada al corte inmutable.'
      : `Corte ${accion === 'revisar' ? 'revisado' : 'aprobado'} correctamente.`);
  };

  const cargarEstadoCierre = useCallback(async (aplicarPeriodo = false) => {
    setCargandoCierre(true);
    const [
      { data, error: cierreError },
      { data: tomaData, error: tomaError },
      { data: controlData, error: controlError },
    ] = await Promise.all([
      supabase.rpc('app_ab_estado_cierre', { p_empresa_id: empresaId }),
      supabase.rpc('app_ab_estado_toma_finca', { p_empresa_id: empresaId }),
      supabase.rpc('app_ab_control_integral', { p_empresa_id: empresaId }),
    ]);
    setCargandoCierre(false);
    if (cierreError || tomaError || controlError) {
      setError(cierreError?.message || tomaError?.message || controlError?.message || 'No se pudo verificar el cierre.');
      return;
    }
    const estado = data as EstadoCierreAB;
    setEstadoCierre(estado);
    setEstadoToma(tomaData as EstadoTomaFinca);
    setControlIntegral(controlData as ControlIntegralAB);
    if (aplicarPeriodo && estado?.siguiente_desde && estado?.siguiente_hasta) {
      setFechaDesde(estado.siguiente_desde);
      setFechaHasta(estado.siguiente_hasta);
      setFechaContable(estado.siguiente_hasta);
      setCriterioFecha('aplicacion');
    }
  }, [empresaId]);

  const cargarReglas = useCallback(async () => {
    const { data, error: reglasError } = await supabase
      .from('app_ab_reglas_capitalizacion')
      .select('id, producto_id, producto_codigo, producto_nombre, version, metodo, porcentaje_pc, porcentaje_rc, vigencia_desde, vigencia_hasta, fundamento_tecnico, evidencia_referencia, estado, creado_at, aprobado_at, motivo_estado')
      .eq('empresa_id', empresaId)
      .order('creado_at', { ascending: false });
    if (reglasError) {
      setMensajeRegla(reglasError.message);
      setReglasCapitalizacion([]);
      return;
    }
    setReglasCapitalizacion((data || []) as ReglaCapitalizacion[]);
  }, [empresaId]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');

    const [boletasRes, cuentasRes, etapasRes, congeladosRes, preasientosRes, lotesRes, costosAcumuladosRes] = await Promise.all([
      supabase
        .from('app_boletas')
        .select('id, numero, lote_id, lote_num, finca_nombre, fecha_desde, fecha_aplicacion, etapa_id, etapa_nombre, area_ha, plantas, estado')
        .eq('empresa_id', empresaId)
        .in('estado', ['aplicada', 'archivada'])
        .order('fecha_aplicacion', { ascending: false }),
      supabase
        .from('plan_cuentas_empresa')
        .select('id, codigo, nombre, activo')
        .eq('empresa_id', empresaId)
        .eq('activo', true)
        .order('codigo'),
      supabase
        .from('app_plan_etapas')
        .select('id, nombre, ciclo_cosecha')
        .eq('empresa_id', empresaId),
      supabase
        .from('app_ab_costos_congelados')
        .select('id, inv_movimiento_id, lote_num, fecha_consumo, costo_total_crc, ciclo_cosecha')
        .eq('empresa_id', empresaId)
        .gte('fecha_consumo', fechaDesde)
        .lte('fecha_consumo', fechaHasta)
        .order('fecha_consumo', { ascending: false }),
      supabase
        .from('app_ab_preasientos')
        .select('id, referencia, periodo_desde, periodo_hasta, fecha_contable, estado, total_debito_crc, total_credito_crc, diferencia_crc, creado_at, motivo_anulacion, asiento_id, asiento_reversion_id, motivo_reversion')
        .eq('empresa_id', empresaId)
        .gte('periodo_hasta', fechaDesde)
        .lte('periodo_desde', fechaHasta)
        .order('creado_at', { ascending: false }),
      supabase
        .from('emp_lotes')
        .select('id, numero, nombre, area_ha, area_ha_sembrada, fecha_primera_siembra, variedad, activo, emp_fincas(nombre)')
        .eq('empresa_id', empresaId)
        .order('numero'),
      supabase
        .from('app_ab_costos_congelados')
        .select('id, lote_id, lote_num, fecha_consumo, costo_total_crc, ciclo_cosecha')
        .eq('empresa_id', empresaId)
        .order('fecha_consumo', { ascending: false }),
    ]);

    if (boletasRes.error || cuentasRes.error || etapasRes.error || congeladosRes.error || preasientosRes.error || lotesRes.error || costosAcumuladosRes.error) {
      setError(boletasRes.error?.message || cuentasRes.error?.message || etapasRes.error?.message || congeladosRes.error?.message || preasientosRes.error?.message || lotesRes.error?.message || costosAcumuladosRes.error?.message || 'No se pudo cargar la información.');
      setFilas([]);
      setCuentas([]);
      setSinMovimiento([]);
      setRegularizacion([]);
      setTrazabilidad([]);
      setCostosCongelados([]);
      setExpedientes([]);
      setPreasientos([]);
      setLineasPreasiento([]);
      setLoading(false);
      return;
    }
    setCostosCongelados((congeladosRes.data || []) as CostoCongelado[]);
    const preasientosData = (preasientosRes.data || []) as PreasientoAB[];
    setPreasientos(preasientosData);
    if (preasientosData.length > 0) {
      const { data: lineasPre, error: lineasPreError } = await supabase
        .from('app_ab_preasiento_lineas')
        .select('id, preasiento_id, linea, tipo, cuenta_codigo, cuenta_nombre, lote_num, ciclo_cosecha, descripcion, debito_crc, credito_crc')
        .in('preasiento_id', preasientosData.map((item) => item.id))
        .order('linea');
      if (lineasPreError) {
        setError(lineasPreError.message);
        setLineasPreasiento([]);
      } else {
        setLineasPreasiento((lineasPre || []) as LineaPreasientoAB[]);
      }
    } else {
      setLineasPreasiento([]);
    }

    const boletas = ((boletasRes.data || []) as Boleta[]).filter((boleta) => {
      const fecha = criterioFecha === 'programacion'
        ? boleta.fecha_desde
        : (boleta.fecha_aplicacion || boleta.fecha_desde);
      return fecha >= fechaDesde && fecha <= fechaHasta;
    });
    const cuentasData = (cuentasRes.data || []) as Cuenta[];
    setCuentas(cuentasData);
    const etapasData = (etapasRes.data || []) as Etapa[];
    const etapaPorId = new Map(etapasData.map((item) => [item.id, item]));
    const cuentaPorCodigo = new Map(cuentasData.map((item) => [item.codigo, item]));
    const ultimaBoletaPorLote = new Map<string, Boleta>();
    for (const boleta of (boletasRes.data || []) as Boleta[]) {
      for (const key of [boleta.lote_id, boleta.lote_num].filter(Boolean).map(String)) {
        if (!ultimaBoletaPorLote.has(key)) ultimaBoletaPorLote.set(key, boleta);
      }
    }
    const costosPorLote = new Map<string, CostoCongelado[]>();
    for (const costo of (costosAcumuladosRes.data || []) as CostoCongelado[]) {
      for (const key of [costo.lote_id, costo.lote_num].filter(Boolean).map(String)) {
        const actuales = costosPorLote.get(key) || [];
        if (!actuales.some((item) => item.id === costo.id)) costosPorLote.set(key, [...actuales, costo]);
      }
    }
    setExpedientes(((lotesRes.data || []) as unknown as LoteMaestro[]).map((lote) => {
      const ultima = ultimaBoletaPorLote.get(String(lote.id)) || ultimaBoletaPorLote.get(String(lote.numero));
      const costos = costosPorLote.get(String(lote.id)) || costosPorLote.get(String(lote.numero)) || [];
      const etapa = ultima?.etapa_id ? etapaPorId.get(ultima.etapa_id) : null;
      const ciclo = etapa?.ciclo_cosecha || costos[0]?.ciclo_cosecha || 'SIN DEFINIR';
      const costoAcumulado = costos.reduce((sum, item) => sum + Number(item.costo_total_crc || 0), 0);
      const area = Number(lote.area_ha_sembrada || lote.area_ha || 0);
      return {
        id: lote.id,
        numero: lote.numero,
        nombre: lote.nombre,
        finca: lote.emp_fincas?.nombre || ultima?.finca_nombre || 'Sin finca',
        area,
        variedad: lote.variedad || 'Sin definir',
        fechaPrimeraSiembra: lote.fecha_primera_siembra || '',
        ciclo,
        etapa: ultima?.etapa_nombre || etapa?.nombre || 'Sin aplicaciones',
        ultimaAplicacion: ultima?.fecha_aplicacion || '',
        movimientos: costos.length,
        costoAcumulado,
        costoHa: area > 0 ? costoAcumulado / area : 0,
        cuenta: ultima ? cuentaDestino(ultima.etapa_nombre || '', ciclo, cuentaPorCodigo) : null,
      };
    }));

    if (boletas.length === 0) {
      setFilas([]);
      setSinMovimiento([]);
      setRegularizacion([]);
      setTrazabilidad([]);
      setLoading(false);
      return;
    }

    // La salida real puede provenir del flujo normal o de "Aplicar directo".
    // Ambas rutas dejan una referencia APP-BOLETA[-DIRECTA]-{id}-PROD-{id}.
    const { data: movimientosData, error: movimientosError } = await supabase
      .from('inv_movimientos')
      .select('id, producto_id, cantidad, costo_unitario, costo_promedio_resultante, referencia')
      .eq('empresa_id', empresaId)
      .eq('tipo', 'salida')
      .ilike('referencia', 'APP-BOLETA-%');

    if (movimientosError) {
      setError(movimientosError.message);
      setFilas([]);
      setSinMovimiento([]);
      setRegularizacion([]);
      setLoading(false);
      return;
    }

    const boletasIds = new Set(boletas.map((boleta) => boleta.id));
    const movimientosPorBoleta = new Map<number, MovimientoInventario[]>();
    for (const movimiento of (movimientosData || []) as MovimientoInventario[]) {
      const match = String(movimiento.referencia || '').match(/^APP-BOLETA-(?:DIRECTA-)?(\d+)-PROD-/i);
      const boletaId = match ? Number(match[1]) : 0;
      if (!boletaId || !boletasIds.has(boletaId)) continue;
      const lista = movimientosPorBoleta.get(boletaId) || [];
      lista.push(movimiento);
      movimientosPorBoleta.set(boletaId, lista);
    }

    const movimientos = Array.from(movimientosPorBoleta.values()).flat();
    const { data: lineasData, error: lineasError } = await supabase
      .from('app_boleta_productos')
      .select('id, boleta_id, inv_producto_id, articulo, nombre_producto, medida, cantidad_aplicar, excluida, es_agua')
      .eq('empresa_id', empresaId)
      .in('boleta_id', Array.from(boletasIds))
      .or('excluida.is.null,excluida.eq.false');

    if (lineasError) {
      setError(lineasError.message);
      setFilas([]);
      setSinMovimiento([]);
      setRegularizacion([]);
      setTrazabilidad([]);
      setLoading(false);
      return;
    }

    // El agua es una linea tecnica para calcular la mezcla. Nunca forma parte
    // de inventario, trazabilidad de insumos ni costos del activo biologico.
    const lineas = ((lineasData || []) as LineaBoleta[]).filter((linea) => !linea.es_agua);
    const productoIds = Array.from(new Set([
      ...movimientos.map((m) => m.producto_id),
      ...lineas.map((linea) => linea.inv_producto_id),
    ].filter((id): id is number => id != null)));
    let productos: Producto[] = [];
    if (productoIds.length > 0) {
      const { data, error: productosError } = await supabase
        .from('inv_productos')
        .select('id, codigo, descripcion, unidad_medida, costo_promedio, costo_neto_unitario, precio_compra_ref, moneda_costo, tipo_cambio_costo')
        .eq('empresa_id', empresaId)
        .in('id', productoIds);
      if (productosError) {
        setError(productosError.message);
        setFilas([]);
        setLoading(false);
        return;
      }
      productos = (data || []) as Producto[];
    }

    const etapas = new Map(((etapasRes.data || []) as Etapa[]).map((etapa) => [etapa.id, etapa]));
    const cuentasMap = new Map(cuentasData.filter((c) => c.activo).map((cuenta) => [cuenta.codigo, cuenta]));
    const productosMap = new Map(productos.map((producto) => [producto.id, producto]));
    const boletasMap = new Map(boletas.map((boleta) => [boleta.id, boleta]));
    const lineasPorBoleta = new Map<number, LineaBoleta[]>();
    for (const linea of lineas) {
      const lista = lineasPorBoleta.get(linea.boleta_id) || [];
      lista.push(linea);
      lineasPorBoleta.set(linea.boleta_id, lista);
    }
    const movimientosPorLinea = new Map<number, MovimientoInventario>();
    for (const movimiento of movimientos) {
      const match = String(movimiento.referencia || '').match(/-PROD-(\d+)$/i);
      if (match) movimientosPorLinea.set(Number(match[1]), movimiento);
    }
    const detalleTrazabilidad: TrazabilidadProducto[] = lineas.map((linea) => {
      const boleta = boletasMap.get(linea.boleta_id);
      const etapa = boleta?.etapa_id ? etapas.get(boleta.etapa_id) : undefined;
      const ciclo = etapa?.ciclo_cosecha || (/\brc\b|segunda/i.test(boleta?.etapa_nombre || '') ? 'RC' : 'PC');
      const producto = linea.inv_producto_id ? productosMap.get(linea.inv_producto_id) : undefined;
      const movimiento = movimientosPorLinea.get(linea.id);
      const costoUnitario = Number(movimiento?.costo_unitario || 0);
      return {
        lineaId: linea.id,
        productoId: linea.inv_producto_id,
        codigo: producto?.codigo || linea.articulo || 'SIN-CÓDIGO',
        producto: producto?.descripcion || linea.nombre_producto || 'Producto sin identificar',
        boletaId: linea.boleta_id,
        boleta: boleta?.numero || String(linea.boleta_id),
        fecha: boleta?.fecha_aplicacion || boleta?.fecha_desde || '',
        lote: String(boleta?.lote_num || 'Sin lote'),
        finca: boleta?.finca_nombre || '',
        ciclo,
        cantidad: Number(movimiento?.cantidad ?? linea.cantidad_aplicar ?? 0),
        unidad: linea.medida || producto?.unidad_medida || '',
        movimientoId: movimiento?.id || null,
        costoUnitario,
        estado: !movimiento ? 'SIN_MOVIMIENTO' : costoUnitario > 0 ? 'VALORIZADO' : 'COSTO_CERO',
      };
    }).sort((a, b) => b.fecha.localeCompare(a.fecha) || b.boleta.localeCompare(a.boleta, 'es', { numeric: true }));
    setTrazabilidad(detalleTrazabilidad);
    const acumulado = new Map<string, Fila>();
    const pendientesMovimiento: AplicacionSinMovimiento[] = [];
    for (const boleta of boletas) {
      // Las aplicadas sin movimiento también deben aparecer: son una diferencia
      // de conciliación, no una razón para ocultar la cédula.
      const lista = movimientosPorBoleta.get(boleta.id) || [];
      const etapa = boleta.etapa_id ? etapas.get(boleta.etapa_id) : undefined;
      const ciclo = etapa?.ciclo_cosecha || (/\brc\b|segunda/i.test(boleta.etapa_nombre || '') ? 'RC' : 'PC');
      const cuenta = cuentaDestino(boleta.etapa_nombre || etapa?.nombre || '', ciclo, cuentasMap);
      const lote = String(boleta.lote_num || 'Sin lote');
      if (lista.length === 0) {
        pendientesMovimiento.push({
          id: boleta.id,
          numero: boleta.numero,
          lote,
          finca: boleta.finca_nombre || '',
          etapa: boleta.etapa_nombre || etapa?.nombre || 'Sin etapa',
          ciclo,
          fechaAplicacion: boleta.fecha_aplicacion || '',
          fechaProgramada: boleta.fecha_desde,
          referenciaEsperada: `APP-BOLETA-${boleta.id}-PROD-*`,
        });
      }
      const key = `${boleta.lote_id || lote}|${ciclo}|${cuenta?.codigo || 'sin-cuenta'}`;
      let costoReferencial = 0;
      let costoCapitalizable = 0;
      let movimientosSinValor = 0;
      for (const movimiento of lista) {
        const cantidad = Number(movimiento.cantidad || 0);
        const unitarioReferencial = costoCRC(movimiento.producto_id ? productosMap.get(movimiento.producto_id) : undefined);
        const unitarioMovimiento = Number(movimiento.costo_unitario || 0);
        costoReferencial += cantidad * unitarioReferencial;
        if (unitarioMovimiento > 0) costoCapitalizable += cantidad * unitarioMovimiento;
        else movimientosSinValor += 1;
      }
      const aplicacionValorizada = lista.length > 0 && movimientosSinValor === 0;

      const actual = acumulado.get(key) || {
        key,
        lote,
        finca: boleta.finca_nombre || '',
        ciclo,
        cuenta,
        area: Number(boleta.area_ha || 0),
        plantas: Number(boleta.plantas || 0),
        aplicaciones: 0,
        aplicacionesConMovimiento: 0,
        aplicacionesValorizadas: 0,
        insumosReferenciales: 0,
        costoCapitalizable: 0,
        movimientosSinValor: 0,
        aplicacionesSinMovimiento: 0,
      };
      actual.aplicaciones += 1;
      actual.aplicacionesConMovimiento += lista.length > 0 ? 1 : 0;
      actual.aplicacionesValorizadas += aplicacionValorizada ? 1 : 0;
      actual.insumosReferenciales += costoReferencial;
      actual.costoCapitalizable += costoCapitalizable;
      actual.movimientosSinValor += movimientosSinValor;
      actual.aplicacionesSinMovimiento += lista.length === 0 ? 1 : 0;
      actual.area = Math.max(actual.area, Number(boleta.area_ha || 0));
      actual.plantas = Math.max(actual.plantas, Number(boleta.plantas || 0));
      acumulado.set(key, actual);
    }

    setFilas(Array.from(acumulado.values()).sort((a, b) => a.lote.localeCompare(b.lote, 'es', { numeric: true })));
    setSinMovimiento(pendientesMovimiento.sort((a, b) =>
      b.fechaAplicacion.localeCompare(a.fechaAplicacion) || b.numero.localeCompare(a.numero, 'es', { numeric: true }),
    ));
    const corteERP = '2026-06-30';
    setRegularizacion(pendientesMovimiento
      .filter((item) => (item.fechaAplicacion || item.fechaProgramada) >= corteERP)
      .map((item) => {
        const lineasCedula = (lineasPorBoleta.get(item.id) || []).filter((linea) => !linea.es_agua);
        const costoReferencial = lineasCedula.reduce((total, linea) => {
          const producto = linea.inv_producto_id ? productosMap.get(linea.inv_producto_id) : undefined;
          return total + Number(linea.cantidad_aplicar || 0) * costoCRC(producto);
        }, 0);
        return {
          ...item,
          productos: lineasCedula.length,
          detalleProductos: lineasCedula
            .map((linea) => `${linea.articulo || ''} ${linea.nombre_producto || ''}`.trim())
            .join(' · '),
          costoReferencial,
          estadoRegularizacion: 'PENDIENTE_INVENTARIO_INICIAL' as const,
        };
      })
      .sort((a, b) => b.fechaAplicacion.localeCompare(a.fechaAplicacion) || b.numero.localeCompare(a.numero, 'es', { numeric: true })),
    );
    setLoading(false);
  }, [empresaId, fechaDesde, fechaHasta, criterioFecha]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    void cargarReglas();
  }, [cargarReglas]);

  useEffect(() => {
    if (tabActivo === 'cierre') void cargarEstadoCierre();
  }, [tabActivo, cargarEstadoCierre]);

  useEffect(() => {
    if (tabActivo === 'transicion') void cargarTransicion();
  }, [tabActivo, cargarTransicion]);

  useEffect(() => {
    if (tabActivo === 'medicion') void cargarMediciones();
  }, [tabActivo, cargarMediciones]);

  const resumen = useMemo(() => ({
    totalReferencial: filas.reduce((sum, fila) => sum + fila.insumosReferenciales, 0),
    totalCapitalizable: filas.reduce((sum, fila) => sum + fila.costoCapitalizable, 0),
    lotes: new Set(filas.map((fila) => fila.lote)).size,
    aplicaciones: filas.reduce((sum, fila) => sum + fila.aplicaciones, 0),
    aplicacionesConMovimiento: filas.reduce((sum, fila) => sum + fila.aplicacionesConMovimiento, 0),
    aplicacionesValorizadas: filas.reduce((sum, fila) => sum + fila.aplicacionesValorizadas, 0),
    movimientosSinValor: filas.reduce((sum, fila) => sum + fila.movimientosSinValor, 0),
    aplicacionesSinMovimiento: filas.reduce((sum, fila) => sum + fila.aplicacionesSinMovimiento, 0),
    alertas: filas.reduce((sum, fila) => sum + fila.movimientosSinValor + fila.aplicacionesSinMovimiento + (fila.cuenta ? 0 : 1), 0),
  }), [filas]);

  const trazabilidadVisible = useMemo(
    () => soloCostoCero
      ? trazabilidad.filter((item) => item.estado === 'COSTO_CERO')
      : trazabilidad,
    [soloCostoCero, trazabilidad],
  );

  const productosTrazables = useMemo(() => {
    const unicos = new Map<string, { key: string; codigo: string; producto: string; cedulas: number }>();
    for (const item of trazabilidadVisible) {
      const key = item.productoId ? String(item.productoId) : `codigo:${item.codigo}`;
      const actual = unicos.get(key);
      if (actual) actual.cedulas += 1;
      else unicos.set(key, { key, codigo: item.codigo, producto: item.producto, cedulas: 1 });
    }
    return Array.from(unicos.values()).sort((a, b) =>
      a.producto.localeCompare(b.producto, 'es', { numeric: true }),
    );
  }, [trazabilidadVisible]);

  const productosEncontrados = useMemo(() => {
    const normalizar = (value: string) => value
      .toLocaleLowerCase('es')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    const termino = normalizar(busquedaProducto.trim());
    if (!termino) return productosTrazables.slice(0, 40);
    return productosTrazables
      .filter((item) => normalizar(`${item.codigo} ${item.producto}`).includes(termino))
      .slice(0, 40);
  }, [busquedaProducto, productosTrazables]);

  const resultadoSimulacion = useMemo(() => {
    const costo = Math.max(Number(costoSimulado) || 0, 0);
    const pc = criterioDistribucion === 'directa'
      ? 100
      : Math.min(Math.max(Number(porcentajePC) || 0, 0), 100);
    return { costo, pc, rc: 100 - pc, costoPC: costo * pc / 100, costoRC: costo * (100 - pc) / 100 };
  }, [costoSimulado, porcentajePC, criterioDistribucion]);

  const impactoProspectivo = useMemo(() => {
    const movimientosFirmes = new Set(
      costosCongelados.map((item) => Number(item.inv_movimiento_id || 0)).filter(Boolean),
    );
    return trazabilidad.flatMap((item) => {
      if (!item.productoId || !item.movimientoId || item.ciclo !== 'PC' || item.estado !== 'VALORIZADO' || movimientosFirmes.has(item.movimientoId)) return [];
      const regla = reglasCapitalizacion
        .filter((r) => r.producto_id === item.productoId && r.estado === 'aprobada')
        .find((r) => {
          const aprobadaDesde = String(r.aprobado_at || r.vigencia_desde).slice(0, 10);
          const efectivaDesde = aprobadaDesde > r.vigencia_desde ? aprobadaDesde : r.vigencia_desde;
          return item.fecha >= efectivaDesde && (!r.vigencia_hasta || item.fecha <= r.vigencia_hasta);
        });
      if (!regla) return [];
      const costo = item.cantidad * item.costoUnitario;
      return [{
        ...item,
        regla,
        costo,
        costoPC: costo * Number(regla.porcentaje_pc) / 100,
        costoRC: costo * Number(regla.porcentaje_rc) / 100,
      }];
    });
  }, [costosCongelados, reglasCapitalizacion, trazabilidad]);

  const totalesImpacto = useMemo(() => impactoProspectivo.reduce((total, item) => ({
    costo: total.costo + item.costo,
    pc: total.pc + item.costoPC,
    rc: total.rc + item.costoRC,
  }), { costo: 0, pc: 0, rc: 0 }), [impactoProspectivo]);

  const guardarRegla = async () => {
    const producto = productosTrazables.find((item) => item.key === productoSimulado);
    const productoId = Number(producto?.key);
    if (!producto || !Number.isFinite(productoId)) {
      setMensajeRegla('Seleccione un producto ERP identificado.');
      return;
    }
    if (criterioDistribucion !== 'directa' && fundamentoSimulacion.trim().length < 20) {
      setMensajeRegla('La excepción requiere un fundamento técnico de al menos 20 caracteres.');
      return;
    }
    if (criterioDistribucion !== 'directa' && !evidenciaSimulacion.trim()) {
      setMensajeRegla('La excepción requiere una referencia de evidencia.');
      return;
    }
    setGuardandoRegla(true);
    setMensajeRegla('');
    const { error: reglaError } = await supabase.rpc('app_ab_crear_regla_capitalizacion', {
      p_empresa_id: empresaId,
      p_producto_id: productoId,
      p_producto_codigo: producto.codigo,
      p_producto_nombre: producto.producto,
      p_metodo: criterioDistribucion,
      p_porcentaje_pc: resultadoSimulacion.pc,
      p_vigencia_desde: vigenciaDesdeRegla,
      p_vigencia_hasta: vigenciaHastaRegla || null,
      p_fundamento_tecnico: criterioDistribucion === 'directa'
        ? 'Asignación directa al lote y a la cosecha destino.'
        : fundamentoSimulacion.trim(),
      p_evidencia_referencia: evidenciaSimulacion.trim() || null,
    });
    setGuardandoRegla(false);
    if (reglaError) {
      setMensajeRegla(reglaError.message);
      return;
    }
    setMensajeRegla('Regla guardada como borrador. Aún no tiene efecto operativo ni contable.');
    await cargarReglas();
  };

  const aprobarRegla = async (regla: ReglaCapitalizacion) => {
    setAccionRegla(regla.id);
    setMensajeRegla('');
    const { error: aprobarError } = await supabase.rpc('app_ab_aprobar_regla_capitalizacion', {
      p_empresa_id: empresaId,
      p_regla_id: regla.id,
    });
    setAccionRegla('');
    if (aprobarError) {
      setMensajeRegla(aprobarError.message);
      return;
    }
    setMensajeRegla(`Versión ${regla.version} aprobada. Queda vigente como política, sin recalcular información firme.`);
    await cargarReglas();
  };

  const trazabilidadFiltrada = useMemo(() => {
    if (!productoTrazado) return [];
    return trazabilidadVisible.filter((item) =>
      item.productoId
        ? String(item.productoId) === productoTrazado
        : `codigo:${item.codigo}` === productoTrazado,
    );
  }, [trazabilidadVisible, productoTrazado]);

  const regularizacionFiltrada = useMemo(() => {
    const normalizar = (value: string) => value.toLocaleLowerCase('es').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const termino = normalizar(busquedaRegularizacion.trim());
    if (!termino) return regularizacion;
    return regularizacion.filter((item) =>
      normalizar(`${item.numero} ${item.lote} ${item.finca} ${item.etapa} ${item.detalleProductos}`).includes(termino),
    );
  }, [busquedaRegularizacion, regularizacion]);

  const totalRegularizacion = useMemo(
    () => regularizacion.reduce((total, item) => total + item.costoReferencial, 0),
    [regularizacion],
  );

  const ventasHistoricasFiltradas = useMemo(() => {
    const termino = busquedaVentaHistorica.trim().toLocaleLowerCase('es')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return ventasHistoricasProducto.filter((item) => {
      if (estadoVentaHistorica !== 'TODOS' && item.estado_fiscal !== estadoVentaHistorica) return false;
      if (!termino) return true;
      return `${item.producto_codigo} ${item.producto_nombre} ${item.categoria}`
        .toLocaleLowerCase('es').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .includes(termino);
    });
  }, [busquedaVentaHistorica, estadoVentaHistorica, ventasHistoricasProducto]);

  const resumenCongelado = useMemo(() => ({
    movimientos: costosCongelados.length,
    lotes: new Set(costosCongelados.map((item) => item.lote_num)).size,
    total: costosCongelados.reduce((total, item) => total + Number(item.costo_total_crc || 0), 0),
  }), [costosCongelados]);

  const prepararCostos = async () => {
    setPreparando(true);
    setError('');
    setResultadoPreparacion(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('app_ab_preparar_costos', {
        p_empresa_id: empresaId,
        p_fecha_desde: fechaDesde,
        p_fecha_hasta: fechaHasta,
      });
      if (rpcError) throw rpcError;
      setResultadoPreparacion((data || null) as ResultadoPreparacion | null);
      setConfirmarPreparacion(false);
      await cargar();
      await cargarEstadoCierre();
    } catch (err: any) {
      setError(err?.message || 'No se pudo preparar el costo del activo biológico.');
    } finally {
      setPreparando(false);
    }
  };

  const generarPreasiento = async () => {
    setGenerandoPreasiento(true);
    setError('');
    try {
      const { data, error: rpcError } = await supabase.rpc('app_ab_generar_preasiento', {
        p_empresa_id: empresaId,
        p_fecha_desde: fechaDesde,
        p_fecha_hasta: fechaHasta,
        p_fecha_contable: fechaContable,
      });
      if (rpcError) throw rpcError;
      const respuesta = data as any;
      setConfirmarPreasiento(false);
      if (respuesta?.sin_nuevos) {
        setError('No hay costos congelados nuevos disponibles para otro preasiento en este período.');
      }
      await cargar();
      await cargarEstadoCierre();
      setTabActivo('preasiento');
    } catch (err: any) {
      setError(err?.message || 'No se pudo generar el preasiento.');
    } finally {
      setGenerandoPreasiento(false);
    }
  };

  const anularPreasiento = async () => {
    if (!preasientoAnular || motivoAnulacion.trim().length < 10) return;
    setAnulandoPreasiento(true);
    setError('');
    try {
      const { data, error: rpcError } = await supabase.rpc('app_ab_anular_preasiento', {
        p_empresa_id: empresaId,
        p_preasiento_id: preasientoAnular.id,
        p_motivo: motivoAnulacion.trim(),
      });
      if (rpcError || !(data as any)?.ok) throw rpcError || new Error('No se pudo anular el preasiento.');
      setPreasientoAnular(null);
      setMotivoAnulacion('');
      await cargar();
    } catch (err: any) {
      setError(err?.message || 'No se pudo anular el preasiento.');
    } finally {
      setAnulandoPreasiento(false);
    }
  };

  useEffect(() => {
    if (!editando || busquedaCorreccion.trim().length < 2) {
      setResultadosCorreccion([]);
      return;
    }
    const timer = window.setTimeout(async () => {
      const q = busquedaCorreccion.trim().replace(/[,%()]/g, ' ');
      const { data } = await supabase
        .from('inv_productos')
        .select('id, codigo, descripcion, unidad_medida, costo_promedio, costo_neto_unitario, precio_compra_ref, moneda_costo, tipo_cambio_costo')
        .eq('empresa_id', empresaId)
        .eq('activo', true)
        .or(`codigo.ilike.%${q}%,descripcion.ilike.%${q}%`)
        .order('descripcion')
        .limit(12);
      setResultadosCorreccion((data || []) as ProductoCorreccion[]);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [busquedaCorreccion, editando, empresaId]);

  const cerrarCorreccion = () => {
    if (guardandoCorreccion) return;
    setEditando(null);
    setBusquedaCorreccion('');
    setResultadosCorreccion([]);
    setProductoCorreccion(null);
    setMotivoCorreccion('');
    setErrorCorreccion('');
  };

  const guardarCorreccion = async () => {
    if (!editando?.movimientoId || !productoCorreccion) return;
    setGuardandoCorreccion(true);
    setErrorCorreccion('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('La sesión venció. Ingrese nuevamente.');
      const response = await fetch(buildApiUrl('/aplicaciones/activo-biologico/corregir-producto'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          'x-empresa-id': String(empresaId),
        },
        body: JSON.stringify({
          empresa_id: empresaId,
          linea_id: editando.lineaId,
          movimiento_id: editando.movimientoId,
          producto_nuevo_id: productoCorreccion.id,
          motivo: motivoCorreccion.trim(),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'No se pudo guardar la corrección.');
      setGuardandoCorreccion(false);
      cerrarCorreccion();
      await cargar();
    } catch (err: any) {
      setErrorCorreccion(err?.message || 'No se pudo guardar la corrección.');
    } finally {
      setGuardandoCorreccion(false);
    }
  };

  const cuentaInventario = cuentas.find((cuenta) => cuenta.codigo === CUENTA_INVENTARIO && cuenta.activo);
  const cuentasFaltantes = [CUENTA_INVENTARIO, ...Object.values(CUENTAS_ACTIVO)]
    .filter((codigo) => !cuentas.some((cuenta) => cuenta.codigo === codigo && cuenta.activo));
  const lotesTransicionFiltrados = lotesTransicion.filter((item) => {
    const clasificacion = item.clasificacion_efectiva || item.clasificacion;
    if (filtroClasificacion === 'todos') return true;
    if (filtroClasificacion === 'revision') {
      return ['mixto', 'sin_evidencia', 'barbecho_abierto'].includes(clasificacion);
    }
    return clasificacion === filtroClasificacion;
  });
  const textoFiltroMedicion = filtroMedicion.trim().toLowerCase();
  const medicionesFiltradas = mediciones.filter((item) =>
    !textoFiltroMedicion
    || item.lote_num.toLowerCase().includes(textoFiltroMedicion)
    || item.ciclo.toLowerCase().includes(textoFiltroMedicion)
  );
  const kgEmpaqueModelo = mediciones.reduce((total, item) => total + Number(item.kg_empaque_estimados || 0), 0);
  const kgJugoModelo = mediciones.reduce((total, item) => total + Number(item.kg_jugo_estimados || 0), 0);
  const valorBrutoModelo = mediciones.reduce((total, item) => total + Number(item.valor_bruto_modelo_crc || 0), 0);
  const valorRazonableModelo = mediciones.every((item) => item.costos_venta_completos)
    ? mediciones.reduce((total, item) => total + Number(item.valor_razonable_modelo_crc || 0), 0)
    : null;
  const cronologiaUtilizable = cronologiaLotes.filter((item) => item.control_cronologia === 'CRONOLOGIA_UTILIZABLE');
  const cronologiaPendiente = cronologiaLotes.filter((item) => item.control_cronologia !== 'CRONOLOGIA_UTILIZABLE');
  const areaCronologiaUtilizable = cronologiaUtilizable.reduce((total, item) => total + Number(item.area_ha || 0), 0);
  const areaCronologiaPendiente = cronologiaPendiente.reduce((total, item) => total + Number(item.area_ha || 0), 0);
  const costosFuturosCalculables = costosFuturosLote.filter((item) => item.base_calculable);
  const costosFuturosPendientes = costosFuturosLote.filter((item) => !item.base_calculable);
  const totalCostoFuturo = (campo: 'costo_futuro_bajo_crc' | 'costo_futuro_central_crc' | 'costo_futuro_alto_crc') =>
    costosFuturosCalculables.reduce((total, item) => total + Number(item[campo] || 0), 0);
  const totalNetoPreDescuento = (campo: 'valor_neto_pre_descuento_bajo_crc' | 'valor_neto_pre_descuento_central_crc' | 'valor_neto_pre_descuento_alto_crc') =>
    costosFuturosCalculables.reduce((total, item) => total + Number(item[campo] || 0), 0);
  const totalValorPresenteBase = costosFuturosCalculables.reduce((total, item) => total + Number(item.valor_presente_referencia_base_crc || 0), 0);
  const totalValorPresenteControl = costosFuturosCalculables.reduce((total, item) => total + Number(item.valor_presente_tasa_control_crc || 0), 0);
  const tasaBaseReferencia = costosFuturosCalculables.find((item) => item.tasa_base_anual_pct != null);
  const mensajeMedicionExitoso = mensajeMedicion.startsWith('Parámetros guardados')
    || mensajeMedicion.startsWith('Versión productiva aprobada')
    || mensajeMedicion.startsWith('Medición guardada');

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="border-b px-5 py-4" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className={`${pageTitleCls} flex items-center gap-2`}>
              <Sprout size={20} style={{ color: 'var(--tone-success-text)' }} />
              Activo biológico
            </h1>
            <p className={pageSubtitleCls}>Insumos reales aplicados por lote y ciclo · vista preliminar, sin generar asientos</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={btnSecondary}
              onClick={() => {
                const base = ['localhost', '127.0.0.1'].includes(window.location.hostname)
                  ? 'http://localhost:3000/'
                  : 'https://app.visionzn.net/';
                window.open(
                  `${base}?manual=aplicaciones.activo-biologico`,
                  '_blank',
                  'noopener,noreferrer',
                );
              }}
              title="Abrir el manual de esta pantalla"
            >
              <BookOpenCheck size={14} className="mr-2 inline" />
              Manual
            </button>
            <button className={btnSecondary} onClick={cargar} disabled={loading}>
              <RefreshCw size={14} className={`mr-2 inline ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <label className={labelCls}>Desde</label>
            <input className={inputCls} type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Hasta</label>
            <input className={inputCls} type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Criterio de fecha</label>
            <select className={selectCls} value={criterioFecha} onChange={(e) => setCriterioFecha(e.target.value as 'aplicacion' | 'programacion')}>
              <option value="aplicacion">Aplicación real (contable)</option>
              <option value="programacion">Programación (igual a Cédulas)</option>
            </select>
          </div>
          <div className="sm:col-span-2 rounded-lg border px-3 py-2" style={{ borderColor: 'var(--line)', background: 'var(--surface-overlay)' }}>
            <div className={labelCls}>Contrapartida inventario</div>
            <div className="text-xs font-semibold" style={{ color: cuentaInventario ? 'var(--ink)' : 'var(--tone-warn-text)' }}>
              {cuentaInventario ? `${cuentaInventario.codigo} · ${cuentaInventario.nombre}` : 'Cuenta de inventario no encontrada'}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-5">
        {error && (
          <div className="mb-4 rounded-lg border px-4 py-3 text-sm" style={{ color: '#f87171', borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)' }}>
            {error}
          </div>
        )}

        {cuentasFaltantes.length > 0 && !loading && (
          <div className="mb-4 flex gap-3 rounded-lg border px-4 py-3 text-xs" style={{ color: 'var(--tone-warn-text)', borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)' }}>
            <AlertTriangle size={17} className="mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-bold">Catálogo incompleto para este alcance</div>
              <div className="mt-1">Faltan cuentas activas: {cuentasFaltantes.join(', ')}. No se crearán automáticamente.</div>
            </div>
          </div>
        )}

        <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <Kpi label="Costo capitalizable" value={fmtCRC(resumen.totalCapitalizable)} icon={<BookOpenCheck size={15} />} tone="var(--tone-success-text)" />
          <Kpi label="Costo referencial" value={fmtCRC(resumen.totalReferencial)} icon={<BookOpenCheck size={15} />} tone="var(--tone-info-text)" />
          <Kpi label="Lotes" value={String(resumen.lotes)} icon={<Sprout size={15} />} tone="var(--tone-info-text)" />
          <Kpi label="Con movimiento / aplicadas" value={`${resumen.aplicacionesConMovimiento} / ${resumen.aplicaciones}`} icon={<CheckCircle2 size={15} />} tone="var(--tone-purple-text)" />
          <Kpi label="Sin valorar / alertas" value={`${resumen.movimientosSinValor} / ${resumen.alertas}`} icon={<AlertTriangle size={15} />} tone="var(--tone-warn-text)" />
        </div>

        <div className="mb-3 flex gap-1 overflow-x-auto border-b" style={{ borderColor: 'var(--line)' }}>
          {[
            { id: 'resumen' as const, label: 'Resumen', active: tabActivo === 'resumen' },
            { id: 'expediente' as const, label: 'Lotes', active: tabActivo === 'expediente' },
            { id: 'trazabilidad' as const, label: 'Trazabilidad', active: ['trazabilidad', 'sin-movimiento', 'regularizacion'].includes(tabActivo) },
            { id: 'cierre' as const, label: 'Cierre contable', active: ['cierre', 'preparacion', 'preasiento'].includes(tabActivo) },
            { id: 'transicion' as const, label: 'Transición 2026', active: tabActivo === 'transicion' },
            { id: 'medicion' as const, label: 'Medición NIIF', active: tabActivo === 'medicion' },
            { id: 'politica' as const, label: 'Política', active: ['politica', 'simulador'].includes(tabActivo) },
          ].map((item) => (
            <button key={item.id} type="button"
              className="whitespace-nowrap border-b-2 px-4 py-2.5 text-xs font-bold transition-colors"
              style={{
                color: item.active ? 'var(--emp-accent-txt)' : 'var(--ink-muted)',
                borderColor: item.active ? 'var(--emp-accent)' : 'transparent',
              }}
              onClick={() => setTabActivo(item.id)}>
              {item.label}
            </button>
          ))}
        </div>

        {['politica', 'simulador'].includes(tabActivo) && (
          <div className="mb-4 flex flex-wrap gap-2 rounded-lg border p-2" style={{ borderColor: 'var(--line)', background: 'var(--surface-overlay)' }}>
            {[
              { id: 'politica' as const, label: 'Criterio aprobado' },
              { id: 'simulador' as const, label: 'Reglas y simulador' },
            ].map((item) => (
              <button key={item.id} type="button" onClick={() => setTabActivo(item.id)}
                className="rounded-md px-3 py-2 text-xs font-bold"
                style={{
                  color: tabActivo === item.id ? 'var(--emp-accent-txt)' : 'var(--ink-muted)',
                  background: tabActivo === item.id ? 'var(--surface-raised)' : 'transparent',
                  border: `1px solid ${tabActivo === item.id ? 'var(--emp-accent)' : 'transparent'}`,
                }}>
                {item.label}
              </button>
            ))}
          </div>
        )}

        {['trazabilidad', 'sin-movimiento', 'regularizacion'].includes(tabActivo) && (
          <div className="mb-4 flex flex-wrap gap-2 rounded-lg border p-2" style={{ borderColor: 'var(--line)', background: 'var(--surface-overlay)' }}>
            {[
              { id: 'trazabilidad' as const, label: 'Por producto' },
              { id: 'sin-movimiento' as const, label: `Sin movimiento (${sinMovimiento.length})` },
              { id: 'regularizacion' as const, label: `Regularización histórica (${regularizacion.length})` },
            ].map((item) => (
              <button key={item.id} type="button" onClick={() => {
                setTabActivo(item.id);
                if (item.id === 'sin-movimiento') setMostrarSinMovimiento(true);
              }} className="rounded-md px-3 py-2 text-xs font-bold"
                style={{
                  color: tabActivo === item.id ? 'var(--emp-accent-txt)' : 'var(--ink-muted)',
                  background: tabActivo === item.id ? 'var(--surface-raised)' : 'transparent',
                  border: `1px solid ${tabActivo === item.id ? 'var(--emp-accent)' : 'transparent'}`,
                }}>
                {item.label}
              </button>
            ))}
          </div>
        )}

        {['cierre', 'preparacion', 'preasiento'].includes(tabActivo) && (
          <div className="mb-4 flex flex-wrap gap-2 rounded-lg border p-2" style={{ borderColor: 'var(--line)', background: 'var(--surface-overlay)' }}>
            {[
              { id: 'cierre' as const, label: 'Estado del cierre' },
              { id: 'preparacion' as const, label: `Costos preparados (${costosCongelados.length})` },
              { id: 'preasiento' as const, label: `Preasientos (${preasientos.length})` },
            ].map((item) => (
              <button key={item.id} type="button" onClick={() => setTabActivo(item.id)}
                className="rounded-md px-3 py-2 text-xs font-bold"
                style={{
                  color: tabActivo === item.id ? 'var(--emp-accent-txt)' : 'var(--ink-muted)',
                  background: tabActivo === item.id ? 'var(--surface-raised)' : 'transparent',
                  border: `1px solid ${tabActivo === item.id ? 'var(--emp-accent)' : 'transparent'}`,
                }}>
                {item.label}
              </button>
            ))}
          </div>
        )}

        {tabActivo === 'transicion' && (
          <div className="space-y-4">
            <div className="rounded-xl border p-5" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--ink)' }}>
                    <ShieldCheck size={17} style={{ color: 'var(--emp-accent-txt)' }} />
                    Puente histórico VFP 2026
                  </div>
                  <p className="mt-1 max-w-4xl text-xs" style={{ color: 'var(--ink-muted)' }}>
                    Fotografía separada e inmutable. Reconstruye aplicaciones anteriores al ERP; no descarga inventario, no modifica cédulas y no genera asientos.
                  </p>
                </div>
                <button type="button" className={btnSecondary} disabled={cargandoTransicion} onClick={() => void cargarTransicion()}>
                  {cargandoTransicion ? 'Verificando…' : 'Actualizar control'}
                </button>
              </div>

              {transicion?.configurado && (
                <>
                  <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                    <Kpi label="Período reconstruido" value={`${transicion.fecha_desde} / ${transicion.fecha_hasta}`} icon={<BookOpenCheck size={14} />} tone="var(--tone-info-text)" />
                    <Kpi label="Cédulas / líneas" value={`${transicion.cedulas} / ${transicion.lineas}`} icon={<CheckCircle2 size={14} />} tone="var(--tone-success-text)" />
                    <Kpi label="Valor VFP conciliable" value={fmtCRC(transicion.valor_vfp_crc)} icon={<BookOpenCheck size={14} />} tone="var(--emp-accent-txt)" />
                    <Kpi label="Lotes" value={String(transicion.lotes)} icon={<Sprout size={14} />} tone="var(--tone-purple-text)" />
                    <Kpi label="Excluidas controladas" value={String(transicion.excluidas)} icon={<ShieldCheck size={14} />} tone="var(--tone-warn-text)" />
                  </div>
                  <div className="mt-4 rounded-lg border p-4 text-xs" style={{ borderColor: 'var(--line)', background: 'var(--surface-overlay)', color: 'var(--ink-muted)' }}>
                    <b style={{ color: 'var(--ink)' }}>Saldo base al {transicion.fecha_saldo_inicial}:</b>{' '}
                    {transicion.saldo_libros_inicial_crc == null ? 'pendiente' : fmtCRC(transicion.saldo_libros_inicial_crc)} ·
                    {' '}{transicion.conciliables} líneas listas para conciliar ·
                    {' '}{transicion.solapadas_erp} cédulas coexistentes identificadas ·
                    {' '}{transicion.ya_capitalizadas} líneas excluidas por estar ya capitalizadas.
                    <div className="mt-2" style={{ color: 'var(--tone-warn-text)' }}>
                      Base bruta preliminar antes de cosechas, bajas y medición NIC 41:{' '}
                      <b>{fmtCRC(Number(transicion.saldo_libros_inicial_crc || 0) + Number(transicion.valor_vfp_crc || 0))}</b>.
                      No es todavía un saldo contabilizable.
                    </div>
                  </div>
                </>
              )}
            </div>

            {fondoSemillero && (
              <div className="rounded-xl border" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-4 p-4 text-left"
                  onClick={() => setMostrarDistribucionSemillero((valor) => !valor)}
                >
                  <div>
                    <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--ink)' }}>
                      {mostrarDistribucionSemillero ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
                      Fondo común de semillero
                      <span className="rounded-full px-2 py-1 text-[10px] uppercase" style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>
                        Propuesta sin efecto contable
                      </span>
                    </div>
                    <p className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>
                      {fmtCRC(fondoSemillero.valor_fondo_crc)} distribuidos entre {fondoSemillero.lotes_beneficiarios} lotes que iniciaron PC, según {Number(fondoSemillero.total_conductor).toLocaleString('es-CR')} plantas sembradas.
                    </p>
                  </div>
                  <div className="font-mono text-sm font-bold" style={{ color: 'var(--emp-accent-txt)' }}>
                    {fmtCRC(distribucionSemillero.reduce((total, fila) => total + Number(fila.valor_asignado_crc), 0))}
                  </div>
                </button>

                {mostrarDistribucionSemillero && (
                  <>
                    <div className="grid gap-3 border-t p-4 md:grid-cols-2" style={{ borderColor: 'var(--line)' }}>
                      <div className="rounded-lg border p-3 text-xs" style={{ borderColor: 'var(--line)', background: 'var(--surface-overlay)', color: 'var(--ink-muted)' }}>
                        <b style={{ color: 'var(--ink)' }}>Criterio:</b> {fondoSemillero.fundamento}
                      </div>
                      <div className="rounded-lg border p-3 text-xs" style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>
                        <b>Limitación documentada:</b> {fondoSemillero.limitacion_origen}
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className={theadCls}>
                          <tr>
                            <th className={thCls}>Lote PC</th>
                            <th className={thCls}>Inicio PC</th>
                            <th className={`${thCls} text-right`}>Bloques</th>
                            <th className={`${thCls} text-right`}>Plantas</th>
                            <th className={`${thCls} text-right`}>Área</th>
                            <th className={`${thCls} text-right`}>Participación</th>
                            <th className={`${thCls} text-right`}>Costo propuesto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {distribucionSemillero.map((item) => (
                            <tr key={item.id} className={trCls}>
                              <td className={`${tdCls} font-bold`}>Lote {item.lote_num}</td>
                              <td className={`${tdCls} font-mono`}>{item.fecha_inicio_pc}</td>
                              <td className={`${tdCls} text-right font-mono`}>{item.bloques}</td>
                              <td className={`${tdCls} text-right font-mono`}>{Number(item.plantas_sembradas).toLocaleString('es-CR')}</td>
                              <td className={`${tdCls} text-right font-mono`}>{fmtNumber(Number(item.area_ha), 2)} ha</td>
                              <td className={`${tdCls} text-right font-mono`}>{fmtNumber(Number(item.proporcion) * 100, 2)} %</td>
                              <td className={`${tdCls} text-right font-mono font-bold`}>{fmtCRC(Number(item.valor_asignado_crc))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {conciliacionTransicion && (
              <div className="rounded-xl border" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-4 p-4 text-left"
                  onClick={() => setMostrarConciliacion((valor) => !valor)}
                >
                  <div>
                    <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--ink)' }}>
                      {mostrarConciliacion ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
                      Conciliación contable de transición
                      <span className="rounded-full px-2 py-1 text-[10px] uppercase" style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>
                        Capa legacy pendiente
                      </span>
                    </div>
                    <p className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>
                      Separa el saldo heredado, las adiciones 2026 vivas y los costos de ciclos ya cosechados. No genera asientos.
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-bold uppercase" style={{ color: 'var(--ink-muted)' }}>Base puente</div>
                    <div className="font-mono text-sm font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{fmtCRC(conciliacionTransicion.base_puente_crc)}</div>
                  </div>
                </button>

                {mostrarConciliacion && (
                  <>
                    <div className="grid gap-3 border-t p-4 sm:grid-cols-2 xl:grid-cols-5" style={{ borderColor: 'var(--line)' }}>
                      <Kpi label="Saldo legacy 31/12/2025" value={fmtCRC(conciliacionTransicion.saldo_legacy_crc)} icon={<BookOpenCheck size={14} />} tone="var(--tone-info-text)" />
                      <Kpi label="Adiciones 2026 vivas" value={fmtCRC(conciliacionTransicion.adicion_viva_crc)} icon={<Sprout size={14} />} tone="var(--tone-success-text)" />
                      <Kpi label="Ciclos cosechados" value={fmtCRC(conciliacionTransicion.adicion_cosechada_crc)} icon={<CheckCircle2 size={14} />} tone="var(--tone-warn-text)" />
                      <Kpi label="Total reconstruido" value={fmtCRC(conciliacionTransicion.adicion_total_crc)} icon={<ShieldCheck size={14} />} tone="var(--tone-purple-text)" />
                      <Kpi label="Diferencia de control" value={fmtCRC(conciliacionTransicion.diferencia_adiciones_crc)} icon={<AlertTriangle size={14} />} tone={Number(conciliacionTransicion.diferencia_adiciones_crc) === 0 ? 'var(--tone-success-text)' : 'var(--tone-danger-text)'} />
                    </div>
                    <div className="mx-4 mb-4 rounded-lg border p-3 text-xs" style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>
                      La base puente de {fmtCRC(conciliacionTransicion.base_puente_crc)} no es todavía el saldo final NIC 41:
                      conserva íntegra la capa legacy porque el saldo al 31/12/2025 no tiene distribución confiable por lote.
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className={theadCls}>
                          <tr>
                            <th className={thCls}>Cuenta</th>
                            <th className={`${thCls} text-right`}>Saldo legacy</th>
                            <th className={`${thCls} text-right`}>Adición viva</th>
                            <th className={`${thCls} text-right`}>Cosechado 2026</th>
                            <th className={`${thCls} text-right`}>Base puente</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cuentasConciliacion.map((item) => (
                            <tr key={item.id} className={trCls}>
                              <td className={tdCls}>
                                <b className="font-mono" style={{ color: 'var(--emp-accent-txt)' }}>{item.cuenta_codigo}</b>
                                <div style={{ color: 'var(--ink-muted)' }}>{item.cuenta_nombre}</div>
                              </td>
                              <td className={`${tdCls} text-right font-mono`}>{fmtCRC(Number(item.saldo_legacy_crc))}</td>
                              <td className={`${tdCls} text-right font-mono`} style={{ color: 'var(--tone-success-text)' }}>{fmtCRC(Number(item.adicion_viva_crc))}</td>
                              <td className={`${tdCls} text-right font-mono`} style={{ color: 'var(--tone-warn-text)' }}>{fmtCRC(Number(item.adicion_cosechada_crc))}</td>
                              <td className={`${tdCls} text-right font-mono font-bold`}>{fmtCRC(Number(item.base_puente_crc))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {politicaLegacy && (
              <div className="rounded-xl border" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-4 p-4 text-left"
                  onClick={() => setMostrarPoliticaLegacy((valor) => !valor)}
                >
                  <div>
                    <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--ink)' }}>
                      {mostrarPoliticaLegacy ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
                      Política para la capa legacy
                      <span className="rounded-full px-2 py-1 text-[10px] uppercase" style={{
                        background: politicaLegacy.estado === 'aprobada' ? 'var(--tone-success-bg)' : 'var(--tone-warn-bg)',
                        color: politicaLegacy.estado === 'aprobada' ? 'var(--tone-success-text)' : 'var(--tone-warn-text)',
                      }}>
                        {politicaLegacy.estado}
                      </span>
                    </div>
                    <p className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>
                      Separa medición NIIF, base fiscal y control de costos; no distribuye artificialmente los CRC 677.137.621.
                    </p>
                  </div>
                  <ShieldCheck size={20} style={{ color: politicaLegacy.estado === 'aprobada' ? 'var(--tone-success-text)' : 'var(--tone-warn-text)' }} />
                </button>

                {mostrarPoliticaLegacy && (
                  <div className="space-y-4 border-t p-4" style={{ borderColor: 'var(--line)' }}>
                    <div className="grid gap-3 lg:grid-cols-2">
                      <div className="rounded-lg border p-4 text-xs" style={{ borderColor: 'var(--line)', background: 'var(--surface-overlay)', color: 'var(--ink-muted)' }}>
                        <div className="mb-2 font-bold uppercase" style={{ color: 'var(--tone-info-text)' }}>Base contable NIIF</div>
                        {politicaLegacy.enfoque_contable}
                      </div>
                      <div className="rounded-lg border p-4 text-xs" style={{ borderColor: 'var(--line)', background: 'var(--surface-overlay)', color: 'var(--ink-muted)' }}>
                        <div className="mb-2 font-bold uppercase" style={{ color: 'var(--tone-purple-text)' }}>Base fiscal Costa Rica</div>
                        {politicaLegacy.enfoque_fiscal}
                      </div>
                    </div>

                    <div className="rounded-lg border p-4 text-xs" style={{ borderColor: 'var(--tone-success-border)', background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)' }}>
                      <b>Decisión transitoria recomendada:</b> {politicaLegacy.decision_transitoria}
                    </div>

                    <div className="grid gap-3 lg:grid-cols-3">
                      {(politicaLegacy.alternativas || []).map((alternativa) => (
                        <div key={alternativa.codigo} className="rounded-lg border p-4 text-xs" style={{
                          borderColor: alternativa.recomendada ? 'var(--tone-success-border)' : 'var(--line)',
                          background: alternativa.recomendada ? 'var(--tone-success-bg)' : 'var(--surface-overlay)',
                          color: 'var(--ink-muted)',
                        }}>
                          <div className="font-bold" style={{ color: alternativa.recomendada ? 'var(--tone-success-text)' : 'var(--ink)' }}>
                            {alternativa.codigo}. {alternativa.nombre}
                          </div>
                          <div className="mt-2">{alternativa.efecto}</div>
                          {alternativa.importe_crc != null && <div className="mt-2 font-mono font-bold">{fmtCRC(Number(alternativa.importe_crc))}</div>}
                        </div>
                      ))}
                    </div>

                    <div>
                      <div className="mb-2 text-xs font-bold uppercase" style={{ color: 'var(--ink-muted)' }}>Insumos pendientes para valor razonable</div>
                      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
                        {(politicaLegacy.insumos_faltantes || []).map((insumo) => (
                          <div key={insumo.codigo} className="flex items-center gap-2 rounded-lg border p-3 text-xs" style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>
                            <AlertTriangle size={14} /> {insumo.nombre}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-lg border p-3 text-xs" style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
                      Evidencia física: {politicaLegacy.evidencia_calidad.con_plantas}/{politicaLegacy.evidencia_calidad.bloques} bloques con plantas,
                      {' '}{politicaLegacy.evidencia_calidad.con_area} con área y {politicaLegacy.evidencia_calidad.cronologia_invalida} con cronología incompatible.
                      <div className="mt-1">{politicaLegacy.evidencia_calidad.conclusion}</div>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex flex-wrap gap-3 text-xs">
                        {(politicaLegacy.fuentes || []).map((fuente) => (
                          <a key={fuente.url} href={fuente.url} target="_blank" rel="noreferrer" style={{ color: 'var(--emp-accent-txt)' }}>
                            {fuente.tipo}: {fuente.referencia}
                          </a>
                        ))}
                      </div>
                      {politicaLegacy.estado === 'propuesta' && (
                        <div className="flex gap-2">
                          {confirmarPoliticaLegacy ? (
                            <>
                              <button
                                type="button"
                                className="rounded-md px-3 py-2 text-xs font-bold"
                                style={{ background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)', border: '1px solid var(--tone-success-border)' }}
                                disabled={procesandoPoliticaLegacy}
                                onClick={() => void aprobarPoliticaLegacy()}
                              >
                                {procesandoPoliticaLegacy ? 'Guardando…' : 'Confirmar aprobación'}
                              </button>
                              <button type="button" className={btnSecondary} disabled={procesandoPoliticaLegacy} onClick={() => setConfirmarPoliticaLegacy(false)}>
                                Cancelar
                              </button>
                            </>
                          ) : (
                            <button type="button" className={btnSecondary} onClick={() => setConfirmarPoliticaLegacy(true)}>
                              Revisar y aprobar política
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {mensajePoliticaLegacy && (
                      <div className="rounded-lg border p-3 text-xs" style={{
                        borderColor: mensajePoliticaLegacy.startsWith('Política aprobada') ? 'var(--tone-success-border)' : 'var(--tone-danger-border)',
                        color: mensajePoliticaLegacy.startsWith('Política aprobada') ? 'var(--tone-success-text)' : 'var(--tone-danger-text)',
                      }}>
                        {mensajePoliticaLegacy}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {clasificacionTransicion && (
              <div className="rounded-xl border" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
                <div className="flex flex-wrap items-end justify-between gap-3 border-b p-4" style={{ borderColor: 'var(--line)' }}>
                  <div>
                    <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--ink)' }}>
                      <Sprout size={17} style={{ color: 'var(--tone-success-text)' }} />
                      Conciliación agrícola por lote y ciclo
                    </div>
                    <p className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>
                      Evidencia a la fecha de corte 27/05/2026. Es una clasificación diagnóstica: no elimina saldos ni genera asientos.
                    </p>
                  </div>
                  <label className="min-w-56">
                    <span className={labelCls}>Mostrar</span>
                    <select className={selectCls} value={filtroClasificacion} onChange={(e) => setFiltroClasificacion(e.target.value)}>
                      <option value="revision">Solo requiere revisión</option>
                      <option value="vivo">Vivo al corte</option>
                      <option value="cosechado">Cosechado al corte</option>
                      <option value="transferir_a_pc">Transferible a PC</option>
                      <option value="todos">Todos los grupos</option>
                    </select>
                  </label>
                </div>

                <div className="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-5">
                  <Kpi label="Grupos lote/ciclo" value={String(clasificacionTransicion.grupos)} icon={<Sprout size={14} />} tone="var(--tone-info-text)" />
                  <Kpi label="Vivos al corte" value={`${clasificacionTransicion.vivos} · ${fmtCRC(clasificacionTransicion.valor_vivo_crc)}`} icon={<CheckCircle2 size={14} />} tone="var(--tone-success-text)" />
                  <Kpi label="Cosechados" value={`${clasificacionTransicion.cosechados} · ${fmtCRC(clasificacionTransicion.valor_cosechado_crc)}`} icon={<BookOpenCheck size={14} />} tone="var(--tone-warn-text)" />
                  <Kpi label="Transferibles a PC" value={String(clasificacionTransicion.transferir_pc)} icon={<RefreshCw size={14} />} tone="var(--tone-purple-text)" />
                  <Kpi label="Requieren revisión" value={String(clasificacionTransicion.revision)} icon={<AlertTriangle size={14} />} tone="var(--tone-danger-text)" />
                </div>
                {mensajeDecision && (
                  <div className="mx-4 mb-4 rounded-lg border p-3 text-xs" style={{
                    borderColor: mensajeDecision.startsWith('Propuesta aprobada') ? 'var(--tone-success-border)' : 'var(--tone-danger-border)',
                    background: mensajeDecision.startsWith('Propuesta aprobada') ? 'var(--tone-success-bg)' : 'var(--tone-danger-bg)',
                    color: mensajeDecision.startsWith('Propuesta aprobada') ? 'var(--tone-success-text)' : 'var(--tone-danger-text)',
                  }}>
                    {mensajeDecision}
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className={theadCls}>
                      <tr>
                        <th className={thCls}>Lote / ciclo</th>
                        <th className={`${thCls} text-right`}>Cédulas / líneas</th>
                        <th className={`${thCls} text-right`}>Bloques vivos / cosechados</th>
                        <th className={`${thCls} text-right`}>Valor reconstruido</th>
                        <th className={thCls}>Clasificación y evidencia</th>
                        <th className={thCls}>Propuesta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lotesTransicionFiltrados.map((item) => (
                        <tr key={item.id} className={trCls}>
                          <td className={tdCls}>
                            <b>Lote {item.lote_num}</b>
                            <div className="font-mono" style={{ color: 'var(--ink-muted)' }}>{item.ciclo}</div>
                          </td>
                          <td className={`${tdCls} text-right font-mono`}>{item.cedulas} / {item.lineas}</td>
                          <td className={`${tdCls} text-right font-mono`}>{item.bloques_vivos} / {item.bloques_cosechados}</td>
                          <td className={`${tdCls} text-right font-mono font-bold`}>{fmtCRC(item.valor_vfp_crc)}</td>
                          <td className={tdCls}>
                            <b style={{ color: ['vivo', 'transferir_a_pc'].includes(item.clasificacion_efectiva || item.clasificacion) ? 'var(--tone-success-text)' : 'var(--tone-warn-text)' }}>
                              {item.clasificacion_efectiva === 'excluido' ? 'Excluido por decisión' : etiquetaClasificacionAB((item.clasificacion_efectiva || item.clasificacion) as TransicionABLoteCiclo['clasificacion'])}
                            </b>
                            <div className="mt-1 max-w-2xl" style={{ color: 'var(--ink-muted)' }}>{item.criterio}</div>
                          </td>
                          <td className={`${tdCls} min-w-72`}>
                            {item.decision_id ? (
                              <>
                                <div className="font-bold" style={{ color: item.decision_estado === 'aprobada' ? 'var(--tone-success-text)' : 'var(--emp-accent-txt)' }}>
                                  {item.decision_propuesta === 'reclasificar_ciclo'
                                    ? `Reclasificar a ${item.ciclo_destino}`
                                    : item.decision_propuesta === 'mantener_vivo'
                                      ? `Mantener ${item.ciclo_destino || item.ciclo} vivo`
                                      : item.decision_propuesta?.replaceAll('_', ' ')}
                                  {' · '}{item.decision_estado}
                                </div>
                                <div className="mt-1" style={{ color: 'var(--ink-muted)' }}>{item.decision_fundamento}</div>
                                {item.decision_estado === 'propuesta' && (
                                  <div className="mt-3 flex flex-wrap gap-2">
                                    {decisionPorConfirmar === item.decision_id ? (
                                      <>
                                        <button
                                          type="button"
                                          className="rounded-md px-3 py-2 text-xs font-bold"
                                          style={{ background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)', border: '1px solid var(--tone-success-border)' }}
                                          disabled={procesandoDecision}
                                          onClick={() => void aprobarDecisionTransicion(item.decision_id!)}
                                        >
                                          {procesandoDecision ? 'Guardando…' : 'Confirmar aprobación'}
                                        </button>
                                        <button type="button" className={btnSecondary} disabled={procesandoDecision} onClick={() => setDecisionPorConfirmar(null)}>
                                          Cancelar
                                        </button>
                                      </>
                                    ) : (
                                      <button type="button" className={btnSecondary} onClick={() => setDecisionPorConfirmar(item.decision_id!)}>
                                        Revisar y aprobar
                                      </button>
                                    )}
                                  </div>
                                )}
                              </>
                            ) : (
                              <span style={{ color: 'var(--ink-muted)' }}>Sin propuesta</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {lotesTransicionFiltrados.length === 0 && (
                        <tr><td className={`${tdCls} py-8 text-center`} colSpan={6}>No hay grupos con este criterio.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {observacionesTransicion.length > 0 && (
              <div className={tableWrapCls}>
                <div className="border-b px-4 py-3 text-sm font-bold" style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}>
                  Excepciones por revisar
                </div>
                <table className="w-full text-xs">
                  <thead className={theadCls}>
                    <tr>
                      <th className={thCls}>Cédula</th>
                      <th className={thCls}>Fecha</th>
                      <th className={thCls}>Lote</th>
                      <th className={thCls}>Producto</th>
                      <th className={`${thCls} text-right`}>Valor VFP</th>
                      <th className={thCls}>Control</th>
                    </tr>
                  </thead>
                  <tbody>
                    {observacionesTransicion.map((item) => (
                      <tr key={item.id} className={trCls}>
                        <td className={`${tdCls} font-mono font-bold`}>{item.nboleta}</td>
                        <td className={tdCls}>{item.fecha_aplicacion}</td>
                        <td className={tdCls}>{item.lote_num || '—'}</td>
                        <td className={tdCls}>
                          <b>{item.articulo || 'Sin código'}</b>
                          <div style={{ color: 'var(--ink-muted)' }}>{item.producto_nombre || 'Sin nombre'}</div>
                        </td>
                        <td className={`${tdCls} text-right font-mono`}>{fmtCRC(item.valor_vfp_crc)}</td>
                        <td className={tdCls}>
                          <span style={{ color: item.estado.startsWith('excluido_') ? 'var(--tone-warn-text)' : 'var(--tone-danger-text)' }}>
                            {item.estado.replaceAll('_', ' ')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tabActivo === 'medicion' && (
          <div className="space-y-4">
            <div className="rounded-xl border p-5" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--ink)' }}>
                    <Sprout size={17} style={{ color: 'var(--tone-success-text)' }} />
                    Expediente de medición NIC 41
                  </div>
                  <p className="mt-1 max-w-4xl text-xs" style={{ color: 'var(--ink-muted)' }}>
                    Valor razonable menos costos de venta por lote y ciclo. La población y el área provienen de Campo; las variables económicas exigen evidencia. Esta vista no crea asientos.
                  </p>
                </div>
                <button type="button" className={btnSecondary} disabled={cargandoMediciones} onClick={() => void cargarMediciones()}>
                  <RefreshCw size={14} className={`mr-2 inline ${cargandoMediciones ? 'animate-spin' : ''}`} />
                  Actualizar
                </button>
              </div>
              <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                <Kpi label="Expedientes lote/ciclo" value={String(mediciones.length)} icon={<Sprout size={14} />} tone="var(--tone-info-text)" />
                <Kpi label="Empaque proyectado" value={`${fmtNumber(kgEmpaqueModelo, 2)} kg`} icon={<CheckCircle2 size={14} />} tone="var(--tone-success-text)" />
                <Kpi label="Jugo proyectado" value={`${fmtNumber(kgJugoModelo, 2)} kg`} icon={<CheckCircle2 size={14} />} tone="var(--tone-info-text)" />
                <Kpi label="Valor bruto esperado" value={fmtCRC(valorBrutoModelo)} icon={<BookOpenCheck size={14} />} tone="var(--tone-purple-text)" />
                <Kpi label="VR menos costos de venta" value={valorRazonableModelo == null ? 'Pendiente' : fmtCRC(valorRazonableModelo)} icon={<ShieldCheck size={14} />} tone={valorRazonableModelo == null ? 'var(--tone-warn-text)' : 'var(--emp-accent-txt)'} />
              </div>
              {parametroMedicion && (
                <div className="mt-4 rounded-lg border p-4" style={{ borderColor: 'var(--line)', background: 'var(--surface-overlay)' }}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="text-xs font-bold" style={{ color: 'var(--ink)' }}>
                      Modelo productivo v{parametroMedicion.version}
                      <span className="ml-2 rounded-full px-2 py-1 text-[10px] uppercase" style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>{parametroMedicion.estado}</span>
                    </div>
                    <div className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>Vigencia desde {parametroMedicion.vigencia_desde}</div>
                  </div>
                  <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2 xl:grid-cols-4" style={{ color: 'var(--ink-muted)' }}>
                    <div><b style={{ color: 'var(--ink)' }}>Población:</b> {fmtNumber(Number(parametroMedicion.densidad_plantas_ha), 0)} plantas/ha · mortalidad {fmtNumber(Number(parametroMedicion.mortalidad_pct), 2)} %</div>
                    <div><b style={{ color: 'var(--tone-success-text)' }}>Empaque:</b> {fmtNumber(Number(parametroMedicion.empaque_pct), 2)} % · {fmtNumber(Number(parametroMedicion.peso_empaque_kg), 2)} kg · {fmtCRC(Number(parametroMedicion.precio_empaque_crc_kg))}/kg</div>
                    <div><b style={{ color: 'var(--tone-info-text)' }}>Jugo:</b> {fmtNumber(Number(parametroMedicion.jugo_pct), 2)} % · {fmtNumber(Number(parametroMedicion.peso_jugo_kg), 2)} kg · {fmtCRC(Number(parametroMedicion.precio_jugo_crc_kg))}/kg</div>
                    <div><b style={{ color: 'var(--tone-warn-text)' }}>Costos de venta:</b> pendientes de documentar</div>
                  </div>
                   <div className="mt-3 text-[11px]" style={{ color: 'var(--ink-muted)' }}>{parametroMedicion.evidencia_referencia}</div>
                   <div className="mt-3 flex flex-wrap gap-2 text-[10px] font-bold uppercase">
                     <span className="rounded-full px-2 py-1"
                       style={{
                         background: capacidadesMedicion.puede_editar ? 'var(--tone-info-bg)' : 'var(--surface-sunken)',
                         color: capacidadesMedicion.puede_editar ? 'var(--tone-info-text)' : 'var(--ink-muted)',
                       }}>
                       {capacidadesMedicion.puede_editar ? 'Puede preparar propuesta' : 'Consulta'}
                     </span>
                     <span className="rounded-full px-2 py-1"
                       style={{
                         background: capacidadesMedicion.puede_aprobar ? 'var(--tone-success-bg)' : 'var(--surface-sunken)',
                         color: capacidadesMedicion.puede_aprobar ? 'var(--tone-success-text)' : 'var(--ink-muted)',
                       }}>
                       {capacidadesMedicion.puede_aprobar ? 'Puede aprobar' : 'Sin aprobación'}
                     </span>
                   </div>
                   {parametroMedicion.estado === 'propuesta' && (
                    <div className="mt-4 border-t pt-4" style={{ borderColor: 'var(--line)' }}>
                      {editandoParametros ? (
                        <>
                          <div className="grid gap-3 sm:grid-cols-2">
                            <label>
                              <span className={labelCls}>Costo incremental venta empaque CRC/kg *</span>
                              <input className={inputCls} type="number" min="0" step="0.01" value={costoVentaEmpaque} onChange={(e) => setCostoVentaEmpaque(e.target.value)} />
                            </label>
                            <label>
                              <span className={labelCls}>Costo incremental venta jugo CRC/kg *</span>
                              <input className={inputCls} type="number" min="0" step="0.01" value={costoVentaJugo} onChange={(e) => setCostoVentaJugo(e.target.value)} />
                            </label>
                            <label className="sm:col-span-2">
                              <span className={labelCls}>Evidencia verificable *</span>
                              <input className={inputCls} value={evidenciaParametros} onChange={(e) => setEvidenciaParametros(e.target.value)}
                                placeholder="Documento, contrato, tarifa, fecha o referencia de cálculo" />
                            </label>
                            <label className="sm:col-span-2">
                              <span className={labelCls}>Fundamento técnico *</span>
                              <textarea className={inputCls} rows={3} value={fundamentoParametros} onChange={(e) => setFundamentoParametros(e.target.value)}
                                placeholder="Explique qué costos son incrementales y por qué corresponden a la venta." />
                            </label>
                          </div>
                          <div className="mt-3 flex justify-end gap-2">
                            <button type="button" className={btnSecondary} disabled={guardandoParametros} onClick={() => setEditandoParametros(false)}>Cancelar</button>
                            <button type="button" className="rounded-md px-4 py-2 text-xs font-bold" disabled={guardandoParametros}
                              style={{ background: 'var(--emp-accent)', color: 'var(--emp-accent-contrast)' }}
                              onClick={() => void guardarParametrosMedicion()}>
                              {guardandoParametros ? <Loader2 size={14} className="mr-2 inline animate-spin" /> : <Save size={14} className="mr-2 inline" />}
                              Guardar y recalcular
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-wrap justify-end gap-2">
                          {capacidadesMedicion.puede_editar && (
                            <button type="button" className={btnSecondary} onClick={iniciarEdicionParametros}>
                              <Pencil size={14} className="mr-2 inline" />Completar costos de venta
                            </button>
                          )}
                          {capacidadesMedicion.puede_aprobar
                            && parametroMedicion.costo_venta_empaque_crc_kg != null
                            && parametroMedicion.costo_venta_jugo_crc_kg != null && (
                            confirmandoParametros ? (
                              <>
                                <button type="button" className="rounded-md px-4 py-2 text-xs font-bold" disabled={guardandoParametros}
                                  style={{ background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)', border: '1px solid var(--tone-success-border)' }}
                                  onClick={() => void aprobarParametrosMedicion()}>
                                  Confirmar aprobación
                                </button>
                                <button type="button" className={btnSecondary} disabled={guardandoParametros} onClick={() => setConfirmandoParametros(false)}>Cancelar</button>
                              </>
                            ) : (
                              <button type="button" className={btnSecondary} onClick={() => setConfirmandoParametros(true)}>
                                <ShieldCheck size={14} className="mr-2 inline" />Revisar y aprobar versión
                              </button>
                            )
                          )}
                          {!capacidadesMedicion.puede_editar && !capacidadesMedicion.puede_aprobar && (
                            <span className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                              Su perfil puede consultar esta versión, pero no modificarla ni aprobarla.
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="mt-4 rounded-lg border p-3 text-xs" style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>
                <b>Las bases no tienen la misma composición:</b> el saldo histórico de CRC 677.137.621 incluye insumos, mano de obra, gastos directos y gastos indirectos acumulados bajo el método anterior. La reconstrucción 2026 de CRC 327.777.510,26 corresponde principalmente a aplicaciones de insumos identificadas. El saldo legacy permanece segregado y no se distribuye artificialmente entre lotes. La comparación y cualquier ajuste contable solo se habilitarán cuando el expediente esté completo, revisado y aprobado.
              </div>
            </div>

            {costoHistoricoResumen && (
              <div className="rounded-xl border" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
                <button type="button" className="flex w-full items-center justify-between gap-4 p-4 text-left"
                  onClick={() => setMostrarBaseHistorica((valor) => !valor)}>
                  <div>
                    <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--ink)' }}>
                      {mostrarBaseHistorica ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
                      Base histórica real 2025
                      <span className="rounded-full px-2 py-1 text-[10px] uppercase"
                        style={{ background: 'var(--tone-info-bg)', color: 'var(--tone-info-text)' }}>
                        referencia
                      </span>
                    </div>
                    <p className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>
                      Costos contables conciliados para calibrar el modelo v2; no se cargan automáticamente a los lotes.
                    </p>
                  </div>
                  <BookOpenCheck size={20} style={{ color: 'var(--tone-info-text)' }} />
                </button>
                {mostrarBaseHistorica && (
                  <div className="space-y-4 border-t p-4" style={{ borderColor: 'var(--line)' }}>
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <Kpi label="Costo real de campo" value={fmtCRC(Number(costoHistoricoResumen.costo_campo_real_crc))}
                        icon={<Sprout size={14} />} tone="var(--tone-success-text)" />
                      <Kpi label="Referencia campo / ha-año" value={fmtCRC(Number(costoHistoricoResumen.costo_campo_crc_ha_anio))}
                        icon={<BookOpenCheck size={14} />} tone="var(--tone-info-text)" />
                      <Kpi label="Empaque por evaluar" value={fmtCRC(Number(costoHistoricoResumen.costo_empaque_referencia_crc))}
                        icon={<AlertTriangle size={14} />} tone="var(--tone-warn-text)" />
                      <Kpi label="Excluido para no duplicar" value={fmtCRC(Number(costoHistoricoResumen.total_excluido_crc))}
                        icon={<ShieldCheck size={14} />} tone="var(--ink-muted)" />
                    </div>
                    <div className="rounded-lg border p-3 text-xs"
                      style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>
                      <b>Medición v2 bloqueada:</b> {costoHistoricoResumen.bloqueo_medicion}
                      <div className="mt-1">Área histórica de referencia: {fmtNumber(Number(costoHistoricoResumen.area_referencia_ha), 2)} ha.</div>
                    </div>
                    <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--line)' }}>
                      <table className="w-full text-xs">
                        <thead className={theadCls}>
                          <tr>
                            <th className={thCls}>Componente 2025</th>
                            <th className={`${thCls} text-right`}>Importe real</th>
                            <th className={thCls}>Tratamiento v2</th>
                            <th className={thCls}>Control</th>
                          </tr>
                        </thead>
                        <tbody>
                          {costosHistoricosDetalle.map((item) => {
                            const esReferenciaCampo = item.uso_modelo === 'referencia_campo';
                            const esCostoVenta = item.uso_modelo === 'referencia_costo_venta';
                            const tratamiento = esReferenciaCampo
                              ? 'Referencia de campo'
                              : esCostoVenta
                                ? 'Evaluar obligación de empaque'
                                : 'Excluido';
                            const tono = esReferenciaCampo
                              ? 'var(--tone-success-text)'
                              : esCostoVenta
                                ? 'var(--tone-warn-text)'
                                : 'var(--ink-muted)';
                            return (
                              <tr key={item.id} className={trCls}>
                                <td className={tdCls}><b>{item.concepto}</b><div className="font-mono text-[10px]" style={{ color: 'var(--ink-muted)' }}>{item.codigo}</div></td>
                                <td className={`${tdCls} text-right font-mono font-bold`}>{fmtCRC(Number(item.importe_crc))}</td>
                                <td className={tdCls}><span className="font-bold" style={{ color: tono }}>{tratamiento}</span></td>
                                <td className={tdCls} style={{ color: 'var(--ink-muted)' }}>{item.justificacion}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div>
                      <div className="mb-2 text-xs font-bold" style={{ color: 'var(--ink)' }}>
                        Fotografía del saldo anterior por etapa al 31/12/2025
                      </div>
                      <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--line)' }}>
                        <table className="w-full text-xs">
                          <thead className={theadCls}>
                            <tr>
                              <th className={thCls}>Etapa histórica</th>
                              <th className={`${thCls} text-right`}>Área</th>
                              <th className={`${thCls} text-right`}>Saldo acumulado</th>
                              <th className={`${thCls} text-right`}>Costo acumulado / ha</th>
                            </tr>
                          </thead>
                          <tbody>
                            {saldosHistoricosEtapa.map((item) => (
                              <tr key={item.id} className={trCls}>
                                <td className={tdCls}><b>{item.etapa_nombre}</b><div style={{ color: 'var(--ink-muted)' }}>{item.ciclo || 'Común'}</div></td>
                                <td className={`${tdCls} text-right font-mono`}>{fmtNumber(Number(item.area_ha), 2)} ha</td>
                                <td className={`${tdCls} text-right font-mono font-bold`}>{fmtCRC(Number(item.saldo_crc))}</td>
                                <td className={`${tdCls} text-right font-mono`}>{fmtCRC(Number(item.costo_crc_ha))}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="mt-2 text-[11px]" style={{ color: 'var(--tone-warn-text)' }}>
                        Estos importes describen el costo acumulado anterior; no son porcentajes de avance ni costos pendientes de terminar el cultivo.
                      </div>
                    </div>
                    {ventaHistoricaResumen && (
                      <div className="rounded-lg border" style={{ borderColor: 'var(--line)' }}>
                        <button type="button" className="flex w-full items-center justify-between gap-4 p-3 text-left"
                          onClick={() => setMostrarVentasHistoricas((valor) => !valor)}>
                          <div>
                            <div className="flex items-center gap-2 text-xs font-bold" style={{ color: 'var(--ink)' }}>
                              {mostrarVentasHistoricas ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                              Conciliación de ventas 2025
                            </div>
                            <div className="mt-1 text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                              HCONTA + MOV01/ART01 + estado fiscal de FAC_ELECTRO
                            </div>
                          </div>
                          <span className="rounded-full px-2 py-1 text-[10px] font-bold uppercase"
                            style={{ background: 'var(--tone-info-bg)', color: 'var(--tone-info-text)' }}>
                            solo referencia
                          </span>
                        </button>
                        {mostrarVentasHistoricas && (
                          <div className="space-y-4 border-t p-3" style={{ borderColor: 'var(--line)' }}>
                            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                              <Kpi label="Registrado en HCONTA" value={fmtCRC(Number(ventaHistoricaResumen.venta_contabilizada_crc))}
                                icon={<BookOpenCheck size={14} />} tone="var(--ink-muted)" />
                              <Kpi label="Aceptado y contabilizado" value={fmtCRC(Number(ventaHistoricaResumen.venta_aceptada_contabilizada_crc))}
                                icon={<CheckCircle2 size={14} />} tone="var(--tone-success-text)" />
                              <Kpi label="Rechazado dentro de libros" value={fmtCRC(Number(ventaHistoricaResumen.venta_rechazada_contabilizada_crc))}
                                icon={<AlertTriangle size={14} />} tone="var(--tone-danger-text)" />
                              <Kpi label="Operativo válido 2025" value={fmtCRC(Number(ventaHistoricaResumen.venta_operativa_valida_crc))}
                                icon={<ShieldCheck size={14} />} tone="var(--tone-info-text)" />
                            </div>
                            <div className="rounded-lg border p-3 text-xs"
                              style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>
                              <b>Separación obligatoria:</b> {ventaHistoricaResumen.criterio}
                              <div className="mt-1">
                                El comprobante en procesamiento suma {fmtCRC(Number(ventaHistoricaResumen.venta_procesando_no_mayorizada_crc))}.
                                Esta conciliación no corrige HCONTA ni habilita mediciones o asientos.
                              </div>
                            </div>
                            <div className="flex flex-wrap items-end justify-between gap-3">
                              <div className="flex flex-wrap gap-2">
                                {(['ACEPTADO', 'PROCESANDO', 'RECHAZADO', 'TODOS'] as const).map((estado) => (
                                  <button key={estado} type="button" className="rounded-md border px-3 py-2 text-[11px] font-bold"
                                    style={{
                                      borderColor: estadoVentaHistorica === estado ? 'var(--emp-accent)' : 'var(--line)',
                                      background: estadoVentaHistorica === estado ? 'var(--emp-accent-soft)' : 'var(--surface-raised)',
                                      color: estadoVentaHistorica === estado ? 'var(--emp-accent-txt)' : 'var(--ink-muted)',
                                    }}
                                    onClick={() => setEstadoVentaHistorica(estado)}>
                                    {estado}
                                  </button>
                                ))}
                              </div>
                              <label className="min-w-64">
                                <span className={labelCls}>Buscar producto histórico</span>
                                <div className="relative">
                                  <Search size={14} className="absolute left-3 top-3" style={{ color: 'var(--ink-muted)' }} />
                                  <input className={`${inputCls} pl-9`} value={busquedaVentaHistorica}
                                    onChange={(event) => setBusquedaVentaHistorica(event.target.value)}
                                    placeholder="Código o nombre..." />
                                </div>
                              </label>
                            </div>
                            <div className="max-h-96 overflow-auto rounded-lg border" style={{ borderColor: 'var(--line)' }}>
                              <table className="w-full text-xs">
                                <thead className={theadCls}>
                                  <tr>
                                    <th className={thCls}>Producto</th>
                                    <th className={thCls}>Destino</th>
                                    <th className={`${thCls} text-right`}>Documentos</th>
                                    <th className={`${thCls} text-right`}>Cantidad neta</th>
                                    <th className={`${thCls} text-right`}>Venta asignada</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {ventasHistoricasFiltradas.map((item) => (
                                    <tr key={item.id} className={trCls}>
                                      <td className={tdCls}>
                                        <b>{item.producto_nombre}</b>
                                        <div className="font-mono text-[10px]" style={{ color: 'var(--ink-muted)' }}>{item.producto_codigo}</div>
                                      </td>
                                      <td className={tdCls}>{item.categoria.replace('_', ' ')}</td>
                                      <td className={`${tdCls} text-right font-mono`}>{item.documentos}</td>
                                      <td className={`${tdCls} text-right font-mono`}>{fmtNumber(Number(item.cantidad_neta), 3)}</td>
                                      <td className={`${tdCls} text-right font-mono font-bold`}>{fmtCRC(Number(item.importe_crc))}</td>
                                    </tr>
                                  ))}
                                  {ventasHistoricasFiltradas.length === 0 && (
                                    <tr><td className={`${tdCls} text-center`} colSpan={5}>No hay productos para este filtro.</td></tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                            <div>
                              <div className="mb-2 text-xs font-bold" style={{ color: 'var(--ink)' }}>
                                Calibración de unidades y costo histórico
                              </div>
                              <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--line)' }}>
                                <table className="w-full text-xs">
                                  <thead className={theadCls}>
                                    <tr>
                                      <th className={thCls}>Destino / escenario</th>
                                      <th className={`${thCls} text-right`}>Cantidad origen</th>
                                      <th className={`${thCls} text-right`}>Kg estimados</th>
                                      <th className={`${thCls} text-right`}>Venta / unidad</th>
                                      <th className={`${thCls} text-right`}>Empaque / kg</th>
                                      <th className={thCls}>Control</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {calibracionVentasHistoricas.map((item) => (
                                      <tr key={item.id} className={trCls}>
                                        <td className={tdCls}>
                                          <b>{item.destino.replace('_', ' ')}</b>
                                          <div className="font-mono text-[10px]" style={{ color: 'var(--ink-muted)' }}>{item.codigo}</div>
                                        </td>
                                        <td className={`${tdCls} text-right font-mono`}>
                                          {fmtNumber(Number(item.cantidad_origen), 3)} {item.unidad_origen.toLowerCase()}
                                        </td>
                                        <td className={`${tdCls} text-right font-mono`}>
                                          {item.cantidad_estimada_kg == null ? '—' : fmtNumber(Number(item.cantidad_estimada_kg), 3)}
                                        </td>
                                        <td className={`${tdCls} text-right font-mono`}>{fmtCRC(Number(item.venta_crc_unidad))}</td>
                                        <td className={`${tdCls} text-right font-mono font-bold`}>
                                          {item.costo_empaque_crc_kg == null ? '—' : fmtCRC(Number(item.costo_empaque_crc_kg))}
                                        </td>
                                        <td className={tdCls}>
                                          <span className="font-bold" style={{ color: item.estado === 'dato_directo' ? 'var(--tone-success-text)' : 'var(--tone-warn-text)' }}>
                                            {item.estado === 'dato_directo' ? 'Dato directo' : `Escenario · ${fmtNumber(Number(item.cobertura_dato_directo_pct), 2)} % directo`}
                                          </span>
                                          <div className="mt-1 max-w-md text-[10px]" style={{ color: 'var(--ink-muted)' }}>{item.advertencia}</div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              <div className="mt-2 text-[11px]" style={{ color: 'var(--tone-warn-text)' }}>
                                Ningún escenario habilita automáticamente el costo de venta del modelo. Primero debe separarse qué parte del empaque es incremental y exigida por la venta en planta.
                              </div>
                            </div>
                            {costosVentaHistoricos.length > 0 && (
                              <div className="rounded-lg border" style={{ borderColor: 'var(--line)' }}>
                                <button type="button" className="flex w-full items-center justify-between gap-4 p-3 text-left"
                                  onClick={() => setMostrarCostosVentaHistoricos((valor) => !valor)}>
                                  <div>
                                    <div className="flex items-center gap-2 text-xs font-bold" style={{ color: 'var(--ink)' }}>
                                      {mostrarCostosVentaHistoricos ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                      Descomposición de empaque y costos de venta
                                    </div>
                                    <div className="mt-1 text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                                      Evidencia contable 2025 clasificada bajo NIC 41; venta informada en planta empacadora.
                                    </div>
                                  </div>
                                  <span className="rounded-full px-2 py-1 text-[10px] font-bold uppercase"
                                    style={{
                                      background: costosVentaEscenarios.every((item) => item.estado === 'revisado')
                                        ? 'var(--tone-success-bg)' : 'var(--tone-warn-bg)',
                                      color: costosVentaEscenarios.every((item) => item.estado === 'revisado')
                                        ? 'var(--tone-success-text)' : 'var(--tone-warn-text)',
                                    }}>
                                    {costosVentaEscenarios.every((item) => item.estado === 'revisado') ? 'revisado' : 'bloqueado'}
                                  </span>
                                </button>
                                {mostrarCostosVentaHistoricos && (
                                  <div className="space-y-3 border-t p-3" style={{ borderColor: 'var(--line)' }}>
                                    <div className="grid gap-3 md:grid-cols-2">
                                      {costosVentaEscenarios.map((item) => (
                                        <div key={item.id} className="rounded-lg border p-3"
                                          style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)' }}>
                                          <div className="text-xs font-bold" style={{ color: 'var(--tone-warn-text)' }}>{item.descripcion}</div>
                                          <div className="mt-2 font-mono text-lg font-bold" style={{ color: 'var(--ink)' }}>
                                            {fmtCRC(Number(item.costo_minimo_crc_kg))} – {fmtCRC(Number(item.costo_maximo_crc_kg))} / kg
                                          </div>
                                          <div className="mt-1 text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                                            {item.estado === 'revisado' ? 'Costo histórico clasificado' : 'Máximo bajo revisión'}: {fmtCRC(Number(item.costo_maximo_crc))} sobre {fmtNumber(Number(item.kg_exportacion), 0)} kg.
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="rounded-lg border p-3 text-xs"
                                      style={{ borderColor: 'var(--tone-info-border)', background: 'var(--tone-info-bg)', color: 'var(--tone-info-text)' }}>
                                      {costosVentaEscenarios.every((item) => item.estado === 'revisado') ? (
                                        <>
                                          <b>Recomendación revisada:</b> exportación entre CRC 3,9844 y CRC 4,0568 por kg, según el escenario de peso; jugo CRC 0/kg. Incluye trámites aduaneros y tomas/logística de exportación asumidos por el vendedor. El acarreo finca-planta, empaque y operación de planta no se descuentan nuevamente.
                                        </>
                                      ) : (
                                        <>
                                          <b>Conclusión provisional:</b> el costo incremental confirmado es cero. El extremo superior solo será admisible si la evidencia demuestra que aduanas, logística y flete eran obligación directa del vendedor. El empaque y la operación de planta no se trasladan automáticamente al costo de venta del activo biológico.
                                        </>
                                      )}
                                    </div>
                                    {costosVentaEscenarios.every((item) => item.estado === 'revisado')
                                      && capacidadesMedicion.puede_editar
                                      && parametroMedicion?.estado === 'propuesta' && (
                                      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3"
                                        style={{ borderColor: 'var(--line)', background: 'var(--surface-sunken)' }}>
                                        <div className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                                          Traslada CRC 4,0568/kg para exportación y CRC 0/kg para jugo a la propuesta vigente. No la aprueba.
                                        </div>
                                        <button type="button" className={btnSecondary}
                                          disabled={adoptandoRecomendacion}
                                          onClick={() => void adoptarCostoVentaRecomendado()}>
                                          {adoptandoRecomendacion
                                            ? <Loader2 size={14} className="mr-2 inline animate-spin" />
                                            : <BookOpenCheck size={14} className="mr-2 inline" />}
                                          Llevar escenario 11,5 kg a propuesta
                                        </button>
                                      </div>
                                    )}
                                    <div className="max-h-[32rem] overflow-auto rounded-lg border" style={{ borderColor: 'var(--line)' }}>
                                      <table className="w-full text-xs">
                                        <thead className={theadCls}>
                                          <tr>
                                            <th className={thCls}>Cuenta / concepto</th>
                                            <th className={`${thCls} text-right`}>Importe 2025</th>
                                            <th className={thCls}>Clasificación</th>
                                            <th className={thCls}>Tratamiento</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {costosVentaHistoricos.map((item) => {
                                            const etiquetas: Record<CostoVentaHistorico['clasificacion'], string> = {
                                              transformacion_empaque: 'Transformación / empaque',
                                              incremental_potencial: 'Incremental potencial',
                                              no_incremental: 'No incremental',
                                              posterior_anormal: 'Posterior / anormal',
                                            };
                                            const tono = item.clasificacion === 'incremental_potencial'
                                              ? 'var(--tone-warn-text)'
                                              : item.clasificacion === 'transformacion_empaque'
                                                ? 'var(--tone-info-text)'
                                                : 'var(--ink-muted)';
                                            return (
                                              <tr key={item.id} className={trCls}>
                                                <td className={tdCls}>
                                                  <b>{item.concepto}</b>
                                                  <div className="font-mono text-[10px]" style={{ color: 'var(--ink-muted)' }}>{item.cuenta_codigo}</div>
                                                </td>
                                                <td className={`${tdCls} text-right font-mono font-bold`}>{fmtCRC(Number(item.importe_crc))}</td>
                                                <td className={tdCls}>
                                                  <span className="font-bold" style={{ color: tono }}>{etiquetas[item.clasificacion]}</span>
                                                  {item.requiere_contrato && <div className="text-[10px]">Requiere contrato</div>}
                                                </td>
                                                <td className={tdCls} style={{ color: 'var(--ink-muted)' }}>{item.tratamiento_nic41}</td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                    <div className="text-[11px]" style={{ color: 'var(--tone-warn-text)' }}>
                                      Este análisis no habilita parámetros, no modifica mediciones y no genera asientos.
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            <div>
                              <div className="mb-2 text-xs font-bold" style={{ color: 'var(--ink)' }}>
                                Excepciones fiscales identificadas
                              </div>
                              <div className="space-y-2">
                                {ventasHistoricasExcepcion.map((item) => (
                                  <div key={item.id} className="grid gap-2 rounded-md border p-3 text-[11px] md:grid-cols-[1fr_auto]"
                                    style={{ borderColor: 'var(--line)', background: 'var(--surface-sunken)' }}>
                                    <div>
                                      <b>{item.documento}</b> · {item.fecha} · {item.estado_fiscal}
                                      <div className="mt-1" style={{ color: 'var(--ink-muted)' }}>{item.tratamiento}</div>
                                    </div>
                                    <div className="text-right font-mono font-bold">
                                      {fmtCRC(Number(item.importe_operativo_crc))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                      Fuente: {costoHistoricoResumen.documento_referencia}. Es información real del período, no una proyección ni una autorización contable.
                    </div>
                  </div>
                )}
              </div>
            )}

            {conciliacionMedicion && (
              <div className="rounded-xl border" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
                <button type="button" className="flex w-full items-center justify-between gap-4 p-4 text-left"
                  onClick={() => setMostrarConciliacionMedicion((valor) => !valor)}>
                  <div>
                    <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--ink)' }}>
                      {mostrarConciliacionMedicion ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
                      Conciliación preliminar de medición
                      <span className="rounded-full px-2 py-1 text-[10px] uppercase" style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>
                        {conciliacionMedicion.estado}
                      </span>
                    </div>
                    <p className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>
                      Diferencia estimada: <b style={{ color: conciliacionMedicion.naturaleza_diferencia === 'ganancia' ? 'var(--tone-success-text)' : 'var(--tone-danger-text)' }}>
                        {fmtCRC(Number(conciliacionMedicion.diferencia_medicion_crc))}
                      </b> · sin asiento y con base fiscal separada.
                    </p>
                  </div>
                  <ShieldCheck size={20} style={{ color: 'var(--tone-warn-text)' }} />
                </button>
                {mostrarConciliacionMedicion && (
                  <div className="space-y-4 border-t p-4" style={{ borderColor: 'var(--line)' }}>
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <Kpi label="Saldo histórico" value={fmtCRC(Number(conciliacionMedicion.saldo_legacy_crc))} icon={<BookOpenCheck size={14} />} tone="var(--ink-muted)" />
                      <Kpi label="Costos reconstruidos 2026" value={fmtCRC(Number(conciliacionMedicion.costos_2026_crc))} icon={<BookOpenCheck size={14} />} tone="var(--tone-info-text)" />
                      <Kpi label="Valor en libros puente" value={fmtCRC(Number(conciliacionMedicion.valor_libros_puente_crc))} icon={<BookOpenCheck size={14} />} tone="var(--tone-purple-text)" />
                      <Kpi label="Valor razonable neto" value={fmtCRC(Number(conciliacionMedicion.valor_razonable_neto_crc))} icon={<Sprout size={14} />} tone="var(--tone-success-text)" />
                    </div>
                    <div className="grid gap-3 lg:grid-cols-2">
                      <div className="rounded-lg border p-4 text-xs" style={{ borderColor: 'var(--tone-success-border)', background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)' }}>
                        <div className="font-bold uppercase">Diferencia NIIF preliminar</div>
                        <div className="mt-2 font-mono text-lg font-bold">{fmtCRC(Number(conciliacionMedicion.diferencia_medicion_crc))}</div>
                        <div className="mt-1">Naturaleza: {conciliacionMedicion.naturaleza_diferencia}. No está contabilizada.</div>
                      </div>
                      <div className="rounded-lg border p-4 text-xs" style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>
                        <div className="font-bold uppercase">Bloqueos vigentes</div>
                        <div className="mt-2">Falta una cuenta específica de resultado por valor razonable en el catálogo. La base fiscal permanece separada y pendiente de documentar.</div>
                      </div>
                    </div>
                    <div className="rounded-lg border p-3 text-xs" style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
                      <b style={{ color: 'var(--ink)' }}>Criterio:</b> {conciliacionMedicion.criterio}
                      <div className="mt-2"><b style={{ color: 'var(--ink)' }}>Limitaciones:</b> {conciliacionMedicion.limitaciones}</div>
                      {conciliacionMedicion.motivo_revision && (
                        <div className="mt-2" style={{ color: 'var(--tone-warn-text)' }}>
                          <b>Motivo de revisión:</b> {conciliacionMedicion.motivo_revision}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <details className="rounded-xl border" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
              <summary className="cursor-pointer list-none p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--ink)' }}>
                      <Sprout size={17} style={{ color: 'var(--tone-info-text)' }} />
                      Modelo de madurez y costos por etapa
                    </div>
                    <p className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>
                      Tiempo restante, riesgo, costos futuros y descuento. No confunde saldos historicos con costos por completar.
                    </p>
                  </div>
                  <span className="rounded-full px-2 py-1 text-[10px] font-bold uppercase"
                    style={{ background: modeloEtapas?.estado === 'aprobado' ? 'var(--tone-success-bg)' : 'var(--tone-warn-bg)', color: modeloEtapas?.estado === 'aprobado' ? 'var(--tone-success-text)' : 'var(--tone-warn-text)' }}>
                    {modeloEtapas ? `Version ${modeloEtapas.version} - ${modeloEtapas.estado} - ${modeloEtapas.etapas_completas}/${modeloEtapas.etapas}` : 'Sin modelo'}
                  </span>
                </div>
              </summary>
              <div className="border-t p-4" style={{ borderColor: 'var(--line)' }}>
                <div className="mb-3 rounded-lg border p-3 text-xs" style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>
                  El corte permanece bloqueado hasta documentar y aprobar las cinco etapas. Los costos acumulados de 2025 son referencia, no costo futuro.
                </div>
                <div className="mb-3 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-lg border p-3" style={{ borderColor: 'var(--tone-success-border)', background: 'var(--tone-success-bg)' }}>
                    <div className="text-[10px] font-bold uppercase" style={{ color: 'var(--tone-success-text)' }}>Cronología utilizable</div>
                    <div className="mt-1 font-mono text-lg font-bold" style={{ color: 'var(--ink)' }}>{cronologiaUtilizable.length} lotes</div>
                    <div className="text-xs" style={{ color: 'var(--ink-muted)' }}>{fmtNumber(areaCronologiaUtilizable, 2)} ha al corte</div>
                  </div>
                  <div className="rounded-lg border p-3" style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)' }}>
                    <div className="text-[10px] font-bold uppercase" style={{ color: 'var(--tone-warn-text)' }}>Requieren normalización</div>
                    <div className="mt-1 font-mono text-lg font-bold" style={{ color: 'var(--ink)' }}>{cronologiaPendiente.length} lotes</div>
                    <div className="text-xs" style={{ color: 'var(--ink-muted)' }}>{fmtNumber(areaCronologiaPendiente, 2)} ha sin fecha confiable</div>
                  </div>
                  <div className="rounded-lg border p-3" style={{ borderColor: 'var(--tone-info-border)', background: 'var(--tone-info-bg)' }}>
                    <div className="text-[10px] font-bold uppercase" style={{ color: 'var(--tone-info-text)' }}>Referencia técnica</div>
                    <div className="mt-1 font-mono text-lg font-bold" style={{ color: 'var(--ink)' }}>150 días</div>
                    <div className="text-xs" style={{ color: 'var(--ink-muted)' }}>inducción a madurez; validar por lote</div>
                  </div>
                </div>
                {cronologiaPendiente.length > 0 && (
                  <details className="mb-3 rounded-lg border" style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--surface)' }}>
                    <summary className="cursor-pointer list-none p-3 text-xs font-bold" style={{ color: 'var(--tone-warn-text)' }}>
                      Revisar {cronologiaPendiente.length} lotes antes de valorar
                    </summary>
                    <div className="max-h-80 overflow-auto border-t" style={{ borderColor: 'var(--line)' }}>
                      <table className="w-full min-w-[920px] text-xs">
                        <thead className={theadCls}><tr>
                          <th className={thCls}>Lote</th><th className={thCls}>Control</th><th className={thCls}>Etapa sugerida</th>
                          <th className={thCls}>Siembra disponible</th><th className={thCls}>Inducción disponible</th>
                          <th className={`${thCls} text-right`}>Bloques</th><th className={`${thCls} text-right`}>Área</th>
                        </tr></thead>
                        <tbody>{cronologiaPendiente.map((item) => (
                          <tr key={item.medicion_id} className={trCls}>
                            <td className={tdCls}><b>Lote {item.lote_num}</b><div style={{ color: 'var(--ink-muted)' }}>{item.ciclo}</div></td>
                            <td className={tdCls} style={{ color: 'var(--tone-warn-text)' }}><b>{cronologiaControlLabel(item.control_cronologia)}</b>
                              {item.bloques_fecha_inconsistente > 0 && <div>{item.bloques_fecha_inconsistente} bloques incompatibles</div>}
                            </td>
                            <td className={tdCls}>{item.etapa_sugerida || 'Sin determinar'}</td>
                            <td className={tdCls}>{item.fecha_siembra_desde
                              ? `${item.fecha_siembra_desde}${item.fecha_siembra_hasta !== item.fecha_siembra_desde ? ` → ${item.fecha_siembra_hasta}` : ''}`
                              : '—'}</td>
                            <td className={tdCls}>{item.fecha_induccion_desde
                              ? `${item.fecha_induccion_desde}${item.fecha_induccion_hasta !== item.fecha_induccion_desde ? ` → ${item.fecha_induccion_hasta}` : ''}`
                              : '—'}</td>
                            <td className={`${tdCls} text-right font-mono`}>{item.bloques_al_corte}</td>
                            <td className={`${tdCls} text-right font-mono`}>{fmtNumber(Number(item.area_ha || 0), 2)} ha</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                    <div className="border-t p-3 text-xs" style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
                      Las fechas heredadas solo sirven como evidencia de revisión. La corrección debe identificar fuente, usuario y fecha; nunca sobrescribirá un corte firme.
                    </div>
                  </details>
                )}
                {programaCostoFuente && (
                  <div className="mb-3 rounded-lg border p-3 text-xs" style={{ borderColor: 'var(--tone-info-border)', background: 'var(--tone-info-bg)' }}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="font-bold" style={{ color: 'var(--tone-info-text)' }}>Programa técnico de insumos · {programaCostoFuente.codigo}</div>
                        <div className="mt-1" style={{ color: 'var(--ink)' }}>{programaCostoFuente.documento_referencia}</div>
                        <div className="mt-1" style={{ color: 'var(--ink-muted)' }}>{programaCostoFuente.limitaciones}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-lg font-bold" style={{ color: 'var(--ink)' }}>{programaCostoFuente.moneda} {fmtNumber(Number(programaCostoFuente.total_ha), 2)}</div>
                        <div style={{ color: 'var(--ink-muted)' }}>por ha · días -28 a 252 · referencia</div>
                      </div>
                    </div>
                  </div>
                )}
                {planCostosEtapa.length > 0 && (
                  <details className="mb-3 rounded-lg border" style={{ borderColor: 'var(--line)', background: 'var(--surface)' }}>
                    <summary className="cursor-pointer list-none p-3 text-xs font-bold" style={{ color: 'var(--ink)' }}>
                      <span className="flex flex-wrap items-center justify-between gap-2">
                        <span>Conciliación con Plan de Manejo · {planCostosEtapa.filter((item) => item.completo).length}/{planCostosEtapa.length} etapas sin costos faltantes</span>
                        {planSnapshot && <span className="rounded-full px-2 py-1 text-[10px] uppercase"
                          style={{ background: planSnapshot.estado === 'aprobado' ? 'var(--tone-success-bg)' : 'var(--tone-warn-bg)', color: planSnapshot.estado === 'aprobado' ? 'var(--tone-success-text)' : 'var(--tone-warn-text)' }}>
                          Foto v{planSnapshot.version} · {planSnapshot.estado}
                        </span>}
                      </span>
                    </summary>
                    {planSnapshot && <div className="grid gap-2 border-t p-3 sm:grid-cols-4" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
                      <div><span style={{ color: 'var(--ink-muted)' }}>Corte</span><div className="font-mono font-bold">{planSnapshot.fecha_corte}</div></div>
                      <div><span style={{ color: 'var(--ink-muted)' }}>Líneas congeladas</span><div className="font-mono font-bold">{planSnapshot.lineas}</div></div>
                      <div><span style={{ color: 'var(--ink-muted)' }}>Sin costo</span><div className="font-mono font-bold" style={{ color: planSnapshot.lineas_sin_costo ? 'var(--tone-warn-text)' : 'var(--tone-success-text)' }}>{planSnapshot.lineas_sin_costo}</div></div>
                      <div><span style={{ color: 'var(--ink-muted)' }}>Productos / ha</span><div className="font-mono font-bold">{fmtCRC(Number(planSnapshot.costo_total_crc_ha || 0))}</div></div>
                    </div>}
                    <div className="overflow-x-auto border-t" style={{ borderColor: 'var(--line)' }}>
                      <table className="w-full min-w-[760px] text-xs">
                        <thead className={theadCls}><tr>
                          <th className={thCls}>Etapa</th><th className={`${thCls} text-right`}>Ciclos / líneas</th>
                          <th className={`${thCls} text-right`}>Rango de días</th><th className={`${thCls} text-right`}>Productos / ha</th><th className={thCls}>Control</th>
                        </tr></thead>
                        <tbody>{planCostosEtapa.map((item) => (
                          <tr key={`${item.etapa_codigo}-${item.ciclo_cosecha}`} className={trCls}>
                            <td className={tdCls}><b>{item.etapa}</b><div style={{ color: 'var(--ink-muted)' }}>{item.ciclo_cosecha}</div></td>
                            <td className={`${tdCls} text-right font-mono`}>{item.ciclos} / {item.lineas}</td>
                            <td className={`${tdCls} text-right font-mono`}>{item.dia_minimo ?? '—'} → {item.dia_maximo ?? '—'}</td>
                            <td className={`${tdCls} text-right font-mono`}>{fmtCRC(Number(item.costo_programa_crc_ha || 0))}</td>
                            <td className={tdCls} style={{ color: item.completo ? 'var(--tone-success-text)' : 'var(--tone-warn-text)' }}>
                              <b>{item.completo ? 'Costos disponibles' : `${item.lineas_sin_costo} líneas sin costo`}</b>
                            </td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                    {(moConductorValidaciones.length > 0 || moFasesCalibracion.length > 0) && (
                      <details className="border-t" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
                        <summary className="cursor-pointer list-none p-3 text-xs font-bold" style={{ color: 'var(--ink)' }}>
                          Mano de obra VFP · evidencia del conductor y perfil 2025
                        </summary>
                        <div className="grid gap-3 border-t p-3 md:grid-cols-2" style={{ borderColor: 'var(--line)' }}>
                          {moConductorValidaciones.map((item) => (
                            <div key={item.id} className="rounded-lg border p-3" style={{ borderColor: 'var(--line)', background: 'var(--surface)' }}>
                              <div className="flex items-center justify-between gap-2"><b>Reparto {item.anio}</b><span className="font-mono font-bold" style={{ color: 'var(--tone-success-text)' }}>{Number(item.coincidencia_pct).toLocaleString('es-CR')}%</span></div>
                              <div className="mt-1" style={{ color: 'var(--ink-muted)' }}>{Number(item.coincidencias_plantas).toLocaleString('es-CR')} de {Number(item.claves_distribucion_multiple).toLocaleString('es-CR')} distribuciones múltiples coinciden con plantas por bloque.</div>
                              <div className="mt-2">{item.conclusion}</div>
                            </div>
                          ))}
                        </div>
                        <div className="overflow-x-auto border-t" style={{ borderColor: 'var(--line)' }}>
                          <table className="w-full min-w-[760px] text-xs">
                            <thead className={theadCls}><tr><th className={thCls}>Fase 2025</th><th className={`${thCls} text-right`}>Líneas</th><th className={`${thCls} text-right`}>Horas</th><th className={`${thCls} text-right`}>Monto histórico</th><th className={thCls}>Uso</th></tr></thead>
                            <tbody>{moFasesCalibracion.map((item) => <tr key={item.id} className={trCls}>
                              <td className={tdCls}><b>{item.fase_codigo.replaceAll('_', ' ')}</b></td>
                              <td className={`${tdCls} text-right font-mono`}>{Number(item.lineas).toLocaleString('es-CR')}</td>
                              <td className={`${tdCls} text-right font-mono`}>{Number(item.horas).toLocaleString('es-CR', { maximumFractionDigits: 2 })}</td>
                              <td className={`${tdCls} text-right font-mono`}>{fmtCRC(Number(item.monto_crc))}</td>
                              <td className={tdCls}><span style={{ color: item.incluido_costo_cultivo ? 'var(--tone-success-text)' : 'var(--tone-warn-text)' }}>{item.incluido_costo_cultivo ? 'Calibración del cultivo' : 'Separado / revisar'}</span><div style={{ color: 'var(--ink-muted)' }}>{item.uso_modelo}</div></td>
                            </tr>)}</tbody>
                          </table>
                        </div>
                        <div className="border-t p-3 text-xs" style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
                          El perfil 2025 calibra importes por labor, pero no demuestra costos reales por lote. La aplicación prospectiva exigirá plantas y cronología del bloque.
                        </div>
                      </details>
                    )}
                    <div className="border-t p-3 text-xs" style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
                      Valores vivos del Plan de Manejo. Incluyen productos; todavía no incluyen mano de obra, servicios, indirectos, cosecha ni transporte a planta.
                    </div>
                  </details>
                )}
                {fuentesCostoCompletar.length > 0 && (
                  <details className="mb-3 rounded-lg border" style={{ borderColor: 'var(--line)', background: 'var(--surface)' }}>
                    <summary className="cursor-pointer list-none p-3 text-xs font-bold" style={{ color: 'var(--ink)' }}>
                      <span className="flex flex-wrap items-center justify-between gap-2">
                        <span>Fuentes y conductores de costos futuros</span>
                        <span style={{ color: 'var(--ink-muted)' }}>
                          {fuentesCostoCompletar.filter((item) => ['documentado', 'aprobado'].includes(item.estado)).length}/{fuentesCostoCompletar.length} documentados
                        </span>
                      </span>
                    </summary>
                    <div className="overflow-x-auto border-t" style={{ borderColor: 'var(--line)' }}>
                      <table className="w-full min-w-[980px] text-xs">
                        <thead className={theadCls}><tr>
                          <th className={thCls}>Componente</th><th className={thCls}>Fuente ERP</th>
                          <th className={thCls}>Conductor exigido</th><th className={thCls}>Tratamiento</th><th className={thCls}>Estado</th>
                        </tr></thead>
                        <tbody>{fuentesCostoCompletar.map((item) => {
                          const listo = ['documentado', 'aprobado'].includes(item.estado);
                          return <tr key={item.id} className={trCls}>
                            <td className={tdCls}><b>{item.componente}</b><div className="font-mono" style={{ color: 'var(--ink-muted)' }}>{item.codigo}</div></td>
                            <td className={tdCls}>{item.fuente_erp}</td>
                            <td className={tdCls}>{item.conductor_requerido}<div style={{ color: 'var(--ink-muted)' }}>Nivel: {item.nivel_asignacion.replace('_', ' + ')}</div></td>
                            <td className={tdCls}>{item.tratamiento}<div style={{ color: 'var(--ink-muted)' }}>{item.observacion}</div></td>
                            <td className={tdCls}><span className="rounded-full px-2 py-1 text-[10px] font-bold uppercase"
                              style={{ background: listo ? 'var(--tone-success-bg)' : 'var(--tone-warn-bg)', color: listo ? 'var(--tone-success-text)' : 'var(--tone-warn-text)' }}>
                              {item.estado.replace('_', ' ')}
                            </span></td>
                          </tr>;
                        })}</tbody>
                      </table>
                    </div>
                    <div className="border-t p-3 text-xs" style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
                      Esta matriz controla la evidencia requerida. No distribuye costos ni habilita asientos por sí sola.
                    </div>
                  </details>
                )}
                {costosFuturosLote.length > 0 && (
                  <details className="mb-3 rounded-lg border" style={{ borderColor: 'var(--tone-info-border)', background: 'var(--surface)' }}>
                    <summary className="cursor-pointer list-none p-3 text-xs font-bold" style={{ color: 'var(--ink)' }}>
                      <span className="flex flex-wrap items-center justify-between gap-2">
                        <span>Proyección referencial de costos futuros por lote</span>
                        <span style={{ color: costosFuturosPendientes.length ? 'var(--tone-warn-text)' : 'var(--tone-success-text)' }}>
                          {costosFuturosCalculables.length} calculables · {costosFuturosPendientes.length} por normalizar
                        </span>
                      </span>
                    </summary>
                    <div className="grid gap-2 border-t p-3 sm:grid-cols-3" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
                      <div className="rounded-lg border p-3" style={{ borderColor: 'var(--line)' }}><div className={labelCls}>Escenario bajo</div><b className="font-mono">{fmtCRC(totalCostoFuturo('costo_futuro_bajo_crc'))}</b></div>
                      <div className="rounded-lg border p-3" style={{ borderColor: 'var(--tone-info-border)', background: 'var(--tone-info-bg)' }}><div className={labelCls}>Escenario central</div><b className="font-mono" style={{ color: 'var(--tone-info-text)' }}>{fmtCRC(totalCostoFuturo('costo_futuro_central_crc'))}</b></div>
                      <div className="rounded-lg border p-3" style={{ borderColor: 'var(--line)' }}><div className={labelCls}>Escenario alto</div><b className="font-mono">{fmtCRC(totalCostoFuturo('costo_futuro_alto_crc'))}</b></div>
                    </div>
                    <div className="border-t p-3 text-xs" style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>
                      <b>Valor neto previo a descuento:</b> {fmtCRC(totalNetoPreDescuento('valor_neto_pre_descuento_bajo_crc'))} · {fmtCRC(totalNetoPreDescuento('valor_neto_pre_descuento_central_crc'))} central · {fmtCRC(totalNetoPreDescuento('valor_neto_pre_descuento_alto_crc'))}. No es una medición firme: falta aprobar la tasa de descuento y el corte.
                      {tasaBaseReferencia && <div className="mt-2">
                        <b>Referencia temporal BCCR:</b> TBP {fmtNumber(Number(tasaBaseReferencia.tasa_base_anual_pct), 2)}% al {tasaBaseReferencia.tasa_fecha_referencia} · valor presente central {fmtCRC(totalValorPresenteBase)} · efecto {fmtCRC(totalNetoPreDescuento('valor_neto_pre_descuento_central_crc') - totalValorPresenteBase)}. La prima de riesgo requiere revisión y aprobación.
                        {tasaBaseReferencia.prima_riesgo_pct != null && <div className="mt-1"><b>Propuesta documentada:</b> prima {fmtNumber(Number(tasaBaseReferencia.prima_riesgo_pct), 4)}% · tasa total {fmtNumber(Number(tasaBaseReferencia.tasa_aplicada_control_pct), 4)}% · valor presente {fmtCRC(totalValorPresenteControl)} · estado {tasaBaseReferencia.tasa_estado}.</div>}
                      </div>}
                    </div>
                    {tasaBaseReferencia && (
                      <details className="border-t" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
                        <summary className="cursor-pointer list-none p-3 text-xs font-bold" style={{ color: 'var(--ink)' }}>
                          Documentar tasa de mercado · estado {tasaBaseReferencia.tasa_estado || 'referencia'}
                        </summary>
                        <div className="grid gap-3 border-t p-3 lg:grid-cols-[180px_1fr_auto]" style={{ borderColor: 'var(--line)' }}>
                          <div><label className={labelCls}>Prima de riesgo %</label><input className={inputCls} type="number" min="0" max="50" step="0.01" value={primaRiesgoTasa} onChange={(e) => setPrimaRiesgoTasa(e.target.value)} disabled={procesandoTasa || tasaBaseReferencia.tasa_estado === 'aprobada'} /></div>
                          <div><label className={labelCls}>Fundamento y evidencia de mercado</label><textarea className={inputCls} rows={2} value={fundamentoTasa} onChange={(e) => setFundamentoTasa(e.target.value)} disabled={procesandoTasa || tasaBaseReferencia.tasa_estado === 'aprobada'} placeholder="Fuente, fecha, moneda, plazo y justificación de la prima sin duplicar riesgos incluidos en los flujos." /></div>
                          <div className="flex items-end gap-2">
                            {tasaBaseReferencia.tasa_estado !== 'aprobada' && <button type="button" className={btnSecondary} disabled={procesandoTasa || !capacidadesMedicion.puede_editar} onClick={() => void revisarTasaDescuento()}>{procesandoTasa ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Revisar</button>}
                            {tasaBaseReferencia.tasa_estado === 'revisada' && capacidadesMedicion.puede_aprobar && <button type="button" className={btnSecondary} disabled={procesandoTasa} onClick={() => void aprobarTasaDescuento()}><ShieldCheck size={14} /> Aprobar tasa</button>}
                          </div>
                        </div>
                        <div className="border-t p-3 text-xs" style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
                          Tasa aplicada al control: {fmtNumber(Number(tasaBaseReferencia.tasa_aplicada_control_pct || 0), 2)}%. Fuente base: {tasaBaseReferencia.tasa_fuente}. {mensajeTasa && <span className="ml-2 font-bold" style={{ color: mensajeTasa.includes('No se pudo') || mensajeTasa.includes('Ingrese') || mensajeTasa.includes('Documente') ? 'var(--tone-danger-text)' : 'var(--tone-success-text)' }}>{mensajeTasa}</span>}
                        </div>
                      </details>
                    )}
                    <div className="max-h-[430px] overflow-auto border-t" style={{ borderColor: 'var(--line)' }}>
                      <table className="w-full min-w-[1180px] text-xs">
                        <thead className={theadCls}><tr>
                          <th className={thCls}>Lote / etapa</th><th className={`${thCls} text-right`}>Área / días</th>
                          <th className={`${thCls} text-right`}>Productos</th><th className={`${thCls} text-right`}>Mano de obra</th>
                          <th className={`${thCls} text-right`}>Cosecha</th><th className={`${thCls} text-right`}>Acarreo</th>
                          <th className={`${thCls} text-right`}>Soporte central</th><th className={`${thCls} text-right`}>Costo futuro</th><th className={`${thCls} text-right`}>Neto pre-desc.</th><th className={`${thCls} text-right`}>VP tasa control</th><th className={thCls}>Control</th>
                        </tr></thead>
                        <tbody>{costosFuturosLote.map((item) => <tr key={item.medicion_id} className={trCls}>
                          <td className={tdCls}><b>Lote {item.lote_num} · {item.ciclo}</b><div style={{ color: 'var(--ink-muted)' }}>{item.etapas || 'Sin etapa calculable'}</div></td>
                          <td className={`${tdCls} text-right font-mono`}>{fmtNumber(Number(item.area_ha || 0), 2)} ha<div style={{ color: 'var(--ink-muted)' }}>{item.dias_hasta_cosecha == null ? '—' : `${item.dias_hasta_cosecha} días`}</div></td>
                          <td className={`${tdCls} text-right font-mono`}>{fmtCRC(Number(item.productos_pendientes_crc || 0))}</td>
                          <td className={`${tdCls} text-right font-mono`}>{fmtCRC(Number(item.mano_obra_pendiente_crc || 0))}</td>
                          <td className={`${tdCls} text-right font-mono`}>{fmtCRC(Number(item.cosecha_crc || 0))}</td>
                          <td className={`${tdCls} text-right font-mono`}>{fmtCRC(Number(item.acarreo_central_crc || 0))}</td>
                          <td className={`${tdCls} text-right font-mono`}>{fmtCRC(Number(item.soporte_central_crc || 0))}</td>
                          <td className={`${tdCls} text-right font-mono font-bold`}>{item.costo_futuro_central_crc == null ? 'Pendiente' : fmtCRC(Number(item.costo_futuro_central_crc))}</td>
                          <td className={`${tdCls} text-right font-mono font-bold`}>{item.valor_neto_pre_descuento_central_crc == null ? 'Pendiente' : fmtCRC(Number(item.valor_neto_pre_descuento_central_crc))}</td>
                          <td className={`${tdCls} text-right font-mono`}>{item.valor_presente_tasa_control_crc == null ? 'Pendiente' : fmtCRC(Number(item.valor_presente_tasa_control_crc))}</td>
                          <td className={tdCls} style={{ color: 'var(--tone-warn-text)' }}><b>{item.control_descuento === 'TASA_BASE_SIN_PRIMA_RIESGO' ? 'Falta prima de riesgo' : item.control_descuento.replaceAll('_', ' ')}</b></td>
                        </tr>)}</tbody>
                      </table>
                    </div>
                    <div className="border-t p-3 text-xs" style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
                      Incluye insumos pendientes del plan, mano de obra, cosecha, acarreo finca-planta y una banda de soporte por área-tiempo. No incluye empaque, transporte a puerto ni genera asientos. Mortalidad ya está incorporada en las plantas productivas y no se descuenta dos veces.
                    </div>
                  </details>
                )}
                {pruebasIntegrales.length > 0 && (
                  <details className="mb-3 rounded-lg border" style={{ borderColor: 'var(--tone-success-border)', background: 'var(--surface)' }}>
                    <summary className="cursor-pointer list-none p-3 text-xs font-bold" style={{ color: 'var(--ink)' }}>
                      Pruebas integrales congeladas · {pruebasIntegrales.length}
                    </summary>
                    <div className="space-y-3 border-t p-3" style={{ borderColor: 'var(--line)' }}>
                      {pruebasIntegrales.map((prueba) => <div key={prueba.id} className="rounded-lg border" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
                        <div className="flex flex-wrap items-center justify-between gap-2 border-b p-3" style={{ borderColor: 'var(--line)' }}>
                          <div><b>Lote {prueba.lote_num} · {prueba.ciclo}</b><div className="font-mono text-[10px]" style={{ color: 'var(--ink-muted)' }}>{prueba.codigo} · corte {prueba.fecha_medicion}</div></div>
                          <span className="rounded-full px-2 py-1 text-[10px] font-bold uppercase" style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>{prueba.estado} · sin asiento</span>
                        </div>
                        <div className="grid gap-2 p-3 sm:grid-cols-2 lg:grid-cols-5">
                          <div><span className={labelCls}>Saldo reconstruido</span><b className="block font-mono">{fmtCRC(Number(prueba.saldo_reconstruido_crc))}</b></div>
                          <div><span className={labelCls}>Valor bruto</span><b className="block font-mono">{fmtCRC(Number(prueba.valor_bruto_crc))}</b></div>
                          <div><span className={labelCls}>Costos de venta</span><b className="block font-mono">{fmtCRC(Number(prueba.costos_venta_crc))}</b></div>
                          <div><span className={labelCls}>Costo futuro central</span><b className="block font-mono">{fmtCRC(Number(prueba.costo_futuro_central_crc))}</b></div>
                          <div><span className={labelCls}>Valor presente</span><b className="block font-mono" style={{ color: 'var(--tone-info-text)' }}>{fmtCRC(Number(prueba.valor_presente_control_crc))}</b></div>
                        </div>
                        <div className="grid gap-2 border-t p-3 sm:grid-cols-3" style={{ borderColor: 'var(--line)' }}>
                          <div><span className={labelCls}>Banda futura</span><div className="font-mono">{fmtCRC(Number(prueba.costo_futuro_bajo_crc))} → {fmtCRC(Number(prueba.costo_futuro_alto_crc))}</div></div>
                          <div><span className={labelCls}>Tasa / plazo</span><div className="font-mono">{fmtNumber(Number(prueba.tasa_control_pct), 4)}% · {prueba.dias_hasta_cosecha} días</div></div>
                          <div><span className={labelCls}>Diferencia preliminar</span><div className="font-mono font-bold" style={{ color: 'var(--tone-warn-text)' }}>{fmtCRC(Number(prueba.diferencia_saldo_crc))}</div></div>
                        </div>
                        <div className="border-t p-3 text-xs" style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>{prueba.fundamento}</div>
                      </div>)}
                    </div>
                  </details>
                )}
                <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--line)' }}>
                  <table className="w-full text-xs">
                    <thead className={theadCls}><tr>
                      <th className={thCls}>Etapa</th><th className={thCls}>Método de tiempo</th><th className={`${thCls} text-right`}>Días restantes</th>
                      <th className={`${thCls} text-right`}>Probabilidad</th><th className={`${thCls} text-right`}>Costo futuro / ha</th>
                      <th className={`${thCls} text-right`}>Tasa anual</th><th className={thCls}>Control</th>
                    </tr></thead>
                    <tbody>{modeloEtapasDetalle.map((etapa) => {
                      const completa = etapa.dias_restantes != null && etapa.probabilidad_realizacion_pct != null
                        && etapa.costo_completar_crc_ha != null && etapa.tasa_descuento_anual_pct != null
                        && (etapa.fundamento || '').trim().length >= 20 && (etapa.evidencia_referencia || '').trim().length >= 8;
                      return <tr key={etapa.id} className={trCls}>
                        <td className={tdCls}><b>{etapa.etapa_nombre}</b><div style={{ color: 'var(--ink-muted)' }}>{etapa.ciclo}</div></td>
                        <td className={tdCls}>{etapa.metodo_dias === 'evento_lote'
                          ? <><b>Evento por lote</b><div style={{ color: 'var(--ink-muted)' }}>{etapa.evento_ancla || 'Sin evento'} · {etapa.duracion_desde_ancla_dias ?? '-'} días</div></>
                          : 'Promedio de etapa'}</td>
                        <td className={`${tdCls} text-right font-mono`}>{etapa.metodo_dias === 'evento_lote' ? 'Por lote' : (etapa.dias_restantes ?? '-')}</td>
                        <td className={`${tdCls} text-right font-mono`}>{etapa.probabilidad_realizacion_pct == null ? '-' : `${etapa.probabilidad_realizacion_pct}%`}</td>
                        <td className={`${tdCls} text-right font-mono`}>{etapa.costo_completar_crc_ha == null ? '-' : fmtCRC(Number(etapa.costo_completar_crc_ha))}</td>
                        <td className={`${tdCls} text-right font-mono`}>{etapa.tasa_descuento_anual_pct == null ? '-' : `${etapa.tasa_descuento_anual_pct}%`}</td>
                        <td className={tdCls}><span style={{ color: completa ? 'var(--tone-success-text)' : 'var(--tone-warn-text)' }}>{completa ? 'Documentada' : 'Pendiente de evidencia'}</span></td>
                      </tr>;
                    })}</tbody>
                  </table>
                </div>
              </div>
            </details>

            <div className="rounded-xl border" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4" style={{ borderColor: 'var(--line)' }}>
                <div>
                  <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--ink)' }}>
                    <ShieldCheck size={17} style={{ color: 'var(--tone-success-text)' }} />
                    Corte contable NIC 41
                  </div>
                  <p className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>
                    Congela la medición completa, exige revisión y aprobación, y mantiene la base fiscal separada.
                  </p>
                </div>
                <span className="rounded-full px-2 py-1 text-[10px] font-bold uppercase"
                  style={{ background: configuracionMedicion ? 'var(--tone-success-bg)' : 'var(--tone-warn-bg)', color: configuracionMedicion ? 'var(--tone-success-text)' : 'var(--tone-warn-text)' }}>
                  {configuracionMedicion ? 'Cuentas configuradas' : 'Requiere cuentas'}
                </span>
              </div>
              <div className="space-y-4 p-4">
                <div className="grid gap-3 lg:grid-cols-3">
                  {[
                    { label: 'Ajuste del activo biológico', value: cuentaAjusteMedicion, setter: setCuentaAjusteMedicion },
                    { label: 'Ganancia por valor razonable', value: cuentaGananciaMedicion, setter: setCuentaGananciaMedicion },
                    { label: 'Pérdida por valor razonable', value: cuentaPerdidaMedicion, setter: setCuentaPerdidaMedicion },
                  ].map((campo) => (
                    <label key={campo.label}>
                      <span className={labelCls}>{campo.label}</span>
                      <select className={selectCls} value={campo.value} disabled={!capacidadesMedicion.puede_editar || procesandoCorteMedicion}
                        onChange={(e) => campo.setter(e.target.value)}>
                        <option value="">— Seleccionar cuenta activa —</option>
                        {cuentas.filter((cuenta) => cuenta.activo).map((cuenta) => (
                          <option key={cuenta.id} value={cuenta.id}>{cuenta.codigo} · {cuenta.nombre}</option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>
                {capacidadesMedicion.puede_editar && (
                  <div className="flex justify-end">
                    <button type="button" className={btnSecondary} disabled={procesandoCorteMedicion}
                      onClick={() => void guardarCuentasMedicion()}>
                      <Save size={14} className="mr-2 inline" /> Guardar cuentas
                    </button>
                  </div>
                )}
                <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg border p-3"
                  style={{ borderColor: 'var(--line)', background: 'var(--surface-sunken)' }}>
                  <label className="min-w-56">
                    <span className={labelCls}>Fecha del corte</span>
                    <input type="date" className={inputCls} value={fechaCorteMedicion}
                      onChange={(e) => setFechaCorteMedicion(e.target.value)} />
                  </label>
                  {capacidadesMedicion.puede_editar && (
                    <button type="button" className={btnSecondary}
                      disabled={!configuracionMedicion || parametroMedicion?.estado !== 'aprobada' || procesandoCorteMedicion}
                      onClick={() => void prepararCorteMedicion()}>
                      {procesandoCorteMedicion ? <Loader2 size={14} className="mr-2 inline animate-spin" /> : <BookOpenCheck size={14} className="mr-2 inline" />}
                      Preparar fotografía
                    </button>
                  )}
                </div>
                {cortesMedicion.length === 0 ? (
                  <div className="rounded-lg border border-dashed p-5 text-center text-xs" style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
                    Todavía no existen cortes contables de medición.
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--line)' }}>
                    <table className="w-full text-xs">
                      <thead className={theadCls}><tr>
                        <th className={thCls}>Corte</th><th className={`${thCls} text-right`}>Libros</th>
                        <th className={`${thCls} text-right`}>VR neto</th><th className={`${thCls} text-right`}>Diferencia</th>
                        <th className={thCls}>Estado</th><th className={`${thCls} text-right`}>Acción</th>
                      </tr></thead>
                      <tbody>{cortesMedicion.map((corte) => (
                        <React.Fragment key={corte.id}>
                        <tr className={trCls}>
                          <td className={tdCls}><b>{corte.fecha_medicion}</b><div style={{ color: 'var(--ink-muted)' }}>Versión {corte.version}{corte.asiento_id ? ` · asiento #${corte.asiento_id}` : ''}</div></td>
                          <td className={`${tdCls} text-right font-mono`}>{fmtCRC(Number(corte.valor_libros_crc))}</td>
                          <td className={`${tdCls} text-right font-mono`}>{fmtCRC(Number(corte.valor_razonable_neto_crc))}</td>
                          <td className={`${tdCls} text-right font-mono font-bold`} style={{ color: corte.naturaleza === 'ganancia' ? 'var(--tone-success-text)' : 'var(--tone-danger-text)' }}>{fmtCRC(Number(corte.diferencia_crc))}</td>
                          <td className={tdCls}><span className="rounded-full px-2 py-1 text-[10px] font-bold uppercase" style={{ background: 'var(--surface-overlay)', color: 'var(--ink)' }}>{corte.estado}</span></td>
                          <td className={`${tdCls} text-right`}>
                            {corte.estado === 'preparado' && capacidadesMedicion.puede_editar && <button type="button" className={btnSecondary} disabled={procesandoCorteMedicion || (corte.evidencia?.requiere_modelo_madurez && !corte.evidencia?.validacion_etapas)} onClick={() => void ejecutarAccionCorte(corte, 'revisar')}>{corte.evidencia?.requiere_modelo_madurez && !corte.evidencia?.validacion_etapas ? 'Bloqueado por madurez' : 'Revisar'}</button>}
                            {corte.estado === 'revisado' && capacidadesMedicion.puede_aprobar && <button type="button" className={btnSecondary} disabled={procesandoCorteMedicion} onClick={() => void ejecutarAccionCorte(corte, 'aprobar')}>Aprobar</button>}
                            {corte.estado === 'aprobado' && capacidadesMedicion.puede_aprobar && <button type="button" className={btnSecondary} disabled={procesandoCorteMedicion || corte.naturaleza === 'sin_diferencia'} onClick={() => void ejecutarAccionCorte(corte, 'contabilizar')}>Contabilizar</button>}
                            {['contabilizado', 'reversado', 'anulado'].includes(corte.estado) && <span style={{ color: 'var(--ink-muted)' }}>Sin acciones</span>}
                          </td>
                        </tr>
                        {corte.evidencia?.requiere_modelo_madurez && !corte.evidencia?.validacion_etapas && (
                          <tr><td colSpan={6} className="border-b px-4 py-3 text-[11px]" style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>
                            <AlertTriangle size={14} className="mr-2 inline" />{corte.evidencia.motivo_bloqueo}
                          </td></tr>
                        )}
                        </React.Fragment>
                      ))}</tbody>
                    </table>
                  </div>
                )}
                <div className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                  La cuenta de ajuste mantiene visible la diferencia NIC 41 sin redistribuir el saldo histórico entre lotes.
                </div>
              </div>
            </div>

            <div className="rounded-xl border" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
              <div className="flex flex-wrap items-end justify-between gap-3 border-b p-4" style={{ borderColor: 'var(--line)' }}>
                <div>
                  <div className="text-sm font-bold" style={{ color: 'var(--ink)' }}>Lotes vivos al 27/05/2026</div>
                  <div className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>PC incluye la preparación transferible y el fondo común de semillero aprobado.</div>
                </div>
                <label className="min-w-64">
                  <span className={labelCls}>Buscar lote o ciclo</span>
                  <div className="relative">
                    <Search size={15} className="absolute left-3 top-3" style={{ color: 'var(--ink-muted)' }} />
                    <input className={`${inputCls} pl-9`} value={filtroMedicion} onChange={(e) => setFiltroMedicion(e.target.value)} placeholder="Ej. 56 o PC" />
                  </div>
                </label>
              </div>
              {mensajeMedicion && (
                <div className="m-4 rounded-lg border p-3 text-xs" style={{
                  borderColor: mensajeMedicionExitoso ? 'var(--tone-success-border)' : 'var(--tone-danger-border)',
                  background: mensajeMedicionExitoso ? 'var(--tone-success-bg)' : 'var(--tone-danger-bg)',
                  color: mensajeMedicionExitoso ? 'var(--tone-success-text)' : 'var(--tone-danger-text)',
                }}>{mensajeMedicion}</div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className={theadCls}>
                    <tr>
                      <th className={thCls}>Lote / ciclo</th>
                      <th className={`${thCls} text-right`}>Plantas</th>
                      <th className={`${thCls} text-right`}>Área</th>
                      <th className={`${thCls} text-right`}>Costo 2026</th>
                      <th className={`${thCls} text-right`}>Kg empaque</th>
                      <th className={`${thCls} text-right`}>Kg jugo</th>
                      <th className={`${thCls} text-right`}>Valor bruto</th>
                      <th className={`${thCls} text-right`}>VR neto</th>
                      <th className={thCls}>Estado</th>
                      <th className={`${thCls} text-right`}>Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {medicionesFiltradas.map((item) => {
                      const editando = medicionEditando?.id === item.id;
                      return (
                        <React.Fragment key={item.id}>
                          <tr className={trCls}>
                            <td className={tdCls}><b>Lote {item.lote_num}</b><div style={{ color: 'var(--ink-muted)' }}>{item.ciclo} · versión {item.version}</div></td>
                            <td className={`${tdCls} text-right font-mono`}>{Number(item.plantas_productivas_estimadas).toLocaleString('es-CR')}</td>
                            <td className={`${tdCls} text-right font-mono`}>{fmtNumber(Number(item.area_ha), 2)} ha</td>
                            <td className={`${tdCls} text-right font-mono`}>{fmtCRC(Number(item.costo_reconstruido_crc))}</td>
                            <td className={`${tdCls} text-right font-mono`}>{fmtNumber(Number(item.kg_empaque_estimados), 2)}</td>
                            <td className={`${tdCls} text-right font-mono`}>{fmtNumber(Number(item.kg_jugo_estimados), 2)}</td>
                            <td className={`${tdCls} text-right font-mono font-bold`}>{fmtCRC(Number(item.valor_bruto_modelo_crc))}</td>
                            <td className={`${tdCls} text-right font-mono font-bold`}>{item.valor_razonable_modelo_crc == null ? 'Pendiente' : fmtCRC(Number(item.valor_razonable_modelo_crc))}</td>
                            <td className={tdCls}><span className="rounded-full px-2 py-1 text-[10px] font-bold uppercase" style={{
                              background: item.costos_venta_completos ? 'var(--tone-success-bg)' : 'var(--tone-warn-bg)',
                              color: item.costos_venta_completos ? 'var(--tone-success-text)' : 'var(--tone-warn-text)',
                            }}>{item.costos_venta_completos ? 'Neto completo' : 'Falta costo venta'}</span></td>
                            <td className={`${tdCls} text-right`}>
                              <button type="button" className={btnSecondary} onClick={() => {
                                setMensajeMedicion('');
                                setMedicionEditando(editando ? null : { ...item });
                              }}>
                                {editando ? <X size={14} /> : <ChevronRight size={14} />}
                              </button>
                            </td>
                          </tr>
                          {editando && medicionEditando && (
                            <tr>
                              <td colSpan={10} className="border-b p-4" style={{ borderColor: 'var(--line)', background: 'var(--surface-overlay)' }}>
                                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                                  <div className="rounded-lg border p-3" style={{ borderColor: 'var(--line)' }}><div className={labelCls}>Siembra estimada</div><b className="font-mono">{Number(item.plantas_sembradas_estimadas).toLocaleString('es-CR')} plantas</b></div>
                                  <div className="rounded-lg border p-3" style={{ borderColor: 'var(--line)' }}><div className={labelCls}>Después de mortalidad</div><b className="font-mono">{Number(item.plantas_productivas_estimadas).toLocaleString('es-CR')} plantas</b></div>
                                  <div className="rounded-lg border p-3" style={{ borderColor: 'var(--line)' }}><div className={labelCls}>Empaque</div><b className="font-mono">{fmtNumber(Number(item.kg_empaque_estimados), 2)} kg</b><div style={{ color: 'var(--ink-muted)' }}>{fmtCRC(Number(item.precio_empaque_crc_kg))}/kg</div></div>
                                  <div className="rounded-lg border p-3" style={{ borderColor: 'var(--line)' }}><div className={labelCls}>Jugo</div><b className="font-mono">{fmtNumber(Number(item.kg_jugo_estimados), 2)} kg</b><div style={{ color: 'var(--ink-muted)' }}>{fmtCRC(Number(item.precio_jugo_crc_kg))}/kg</div></div>
                                  <div className="rounded-lg border p-3" style={{ borderColor: item.costos_venta_completos ? 'var(--tone-success-border)' : 'var(--tone-warn-border)', background: item.costos_venta_completos ? 'var(--tone-success-bg)' : 'var(--tone-warn-bg)' }}><div className={labelCls}>Valor neto</div><b className="font-mono" style={{ color: item.costos_venta_completos ? 'var(--tone-success-text)' : 'var(--tone-warn-text)' }}>{item.valor_razonable_modelo_crc == null ? 'Pendiente' : fmtCRC(Number(item.valor_razonable_modelo_crc))}</b><div style={{ color: item.costos_venta_completos ? 'var(--tone-success-text)' : 'var(--tone-warn-text)' }}>{item.costos_venta_completos ? 'Valor razonable menos costos de venta' : 'Faltan costos de venta'}</div></div>
                                </div>
                                <div className="mt-3 text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                                  Densidad {fmtNumber(Number(item.densidad_plantas_ha), 0)} plantas/ha · mortalidad {fmtNumber(Number(item.mortalidad_modelo_pct), 2)} % · modelo productivo propuesto, sin efecto contable.
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tabActivo === 'cierre' && (
          <div className="space-y-4">
            <div className="rounded-xl border p-5" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--ink)' }}>
                    <BookOpenCheck size={17} style={{ color: 'var(--emp-accent-txt)' }} />
                    Siguiente corte incremental
                  </div>
                  <p className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>
                    El sistema continúa después del último período contabilizado. Los movimientos ya congelados o incluidos no se duplican.
                  </p>
                </div>
                <button type="button" className={btnSecondary} disabled={cargandoCierre}
                  onClick={() => void cargarEstadoCierre()}>
                  {cargandoCierre ? 'Verificando…' : 'Verificar estado'}
                </button>
              </div>

              {estadoCierre && (
                <>
                  <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <Kpi label="Último corte firme" value={estadoCierre.ultimo_corte || 'Sin corte'} tone="var(--ink-muted)" icon={<ShieldCheck size={14} />} />
                    <Kpi label="Siguiente período" value={`${estadoCierre.siguiente_desde} / ${estadoCierre.siguiente_hasta}`} tone="var(--emp-accent-txt)" icon={<BookOpenCheck size={14} />} />
                    <Kpi label="Por preparar" value={String(estadoCierre.movimientos_por_preparar)} tone="var(--tone-warn-text)" icon={<AlertTriangle size={14} />} />
                    <Kpi label="Disponibles" value={String(estadoCierre.costos_disponibles)} tone="var(--tone-success-text)" icon={<CheckCircle2 size={14} />} />
                  </div>
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4"
                    style={{ borderColor: estadoCierre.al_dia ? 'var(--tone-success-border)' : 'var(--line)', background: 'var(--surface-overlay)' }}>
                    <div className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                      <b style={{ color: 'var(--ink)' }}>{fmtCRC(estadoCierre.total_crc)}</b> congelados en el período ·
                      {' '}{estadoCierre.preasientos_pendientes} preasiento(s) pendiente(s) en el ERP.
                      {estadoCierre.incluye_cedulas_tardias && (
                        <span style={{ color: 'var(--tone-warn-text)' }}> El período incluye cédulas tardías de un corte anterior.</span>
                      )}
                      {estadoCierre.al_dia && <span style={{ color: 'var(--tone-success-text)' }}> El módulo está al día.</span>}
                    </div>
                    <button type="button" onClick={() => { void cargarEstadoCierre(true); setTabActivo('preparacion'); }}
                      className="rounded-md px-4 py-2 text-xs font-bold text-white" style={{ background: 'var(--emp-accent)' }}>
                      Trabajar este período
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="rounded-lg border p-4 text-xs leading-relaxed" style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
              Secuencia de cierre: <b>preparar costos</b> → <b>generar preasiento</b> → revisar y contabilizar en ERP.
              Si después aparecen cédulas pendientes del mismo rango, entran en una preparación incremental posterior sin modificar el asiento firme.
            </div>
            {controlIntegral && (
              <div className="rounded-xl border p-5"
                style={{
                  borderColor: controlIntegral.ok ? 'var(--tone-success-border)' : 'var(--tone-danger-border)',
                  background: 'var(--surface-raised)',
                }}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--ink)' }}>
                      <ShieldCheck size={17} style={{ color: controlIntegral.ok ? 'var(--tone-success-text)' : 'var(--tone-danger-text)' }} />
                      Control integral del ciclo
                    </div>
                    <p className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>
                      Concilia la fotografía de costos, distribuciones, preasientos, asientos y contraasientos sin modificar datos.
                    </p>
                  </div>
                  <span className="rounded-full border px-3 py-1 text-xs font-bold"
                    style={{
                      borderColor: controlIntegral.ok ? 'var(--tone-success-border)' : 'var(--tone-danger-border)',
                      color: controlIntegral.ok ? 'var(--tone-success-text)' : 'var(--tone-danger-text)',
                    }}>
                    {controlIntegral.ok ? 'CICLO ÍNTEGRO' : `${controlIntegral.problemas} PROBLEMA(S)`}
                  </span>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <Kpi label="Costos conciliados" value={`${controlIntegral.costos.asignados} / ${controlIntegral.costos.total}`}
                    tone="var(--tone-success-text)" icon={<CheckCircle2 size={14} />} />
                  <Kpi label="Duplicados" value={String(controlIntegral.costos.duplicados)}
                    tone={controlIntegral.costos.duplicados ? 'var(--tone-danger-text)' : 'var(--tone-success-text)'} icon={<ShieldCheck size={14} />} />
                  <Kpi label="Distribuciones con error" value={String(controlIntegral.distribucion.desbalanceadas)}
                    tone={controlIntegral.distribucion.desbalanceadas ? 'var(--tone-danger-text)' : 'var(--tone-success-text)'} icon={<Sprout size={14} />} />
                  <Kpi label="Descuadres contables"
                    value={String(
                      controlIntegral.contabilidad.preasientos_desbalanceados
                      + controlIntegral.contabilidad.asientos_desbalanceados
                      + controlIntegral.contabilidad.reversiones_desbalanceadas
                    )}
                    tone="var(--tone-success-text)" icon={<BookOpenCheck size={14} />} />
                </div>
                <div className="mt-3 text-xs" style={{ color: 'var(--ink-muted)' }}>
                  {fmtCRC(controlIntegral.costos.monto_crc)} auditados ·
                  {' '}{controlIntegral.distribucion.reglas_aprobadas} regla(s) prospectiva(s) aprobada(s) ·
                  {' '}{controlIntegral.siguiente_corte.movimientos_pendientes} movimiento(s) pendiente(s).
                </div>
              </div>
            )}
            {estadoToma && (
              <div className="rounded-xl border p-5" style={{ borderColor: estadoToma.conciliada ? 'var(--tone-success-border)' : 'var(--tone-warn-border)', background: 'var(--surface-raised)' }}>
                <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--ink)' }}>
                  <ShieldCheck size={17} style={{ color: estadoToma.conciliada ? 'var(--tone-success-text)' : 'var(--tone-warn-text)' }} />
                  Conciliación de inventario · Bodega Finca
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                  <Kpi label="Última toma" value={estadoToma.descripcion || 'Sin toma física'} tone="var(--ink-muted)" icon={<BookOpenCheck size={14} />} />
                  <Kpi label="Estado" value={estadoToma.estado || 'sin_toma'} tone={estadoToma.conciliada ? 'var(--tone-success-text)' : 'var(--tone-warn-text)'} icon={<ShieldCheck size={14} />} />
                  <Kpi label="Diferencias" value={String(estadoToma.diferencias || 0)} tone="var(--emp-accent-txt)" icon={<AlertTriangle size={14} />} />
                </div>
                <p className="mt-3 text-xs" style={{ color: 'var(--ink-muted)' }}>
                  {estadoToma.conciliada
                    ? `Toma aplicada y trazable${estadoToma.asiento_id ? ` mediante asiento #${estadoToma.asiento_id}` : ''}.`
                    : 'La toma física todavía no constituye saldo firme. Debe terminarse en Aplicaciones y aplicarse de forma atómica en el ERP.'}
                </p>
              </div>
            )}
          </div>
        )}

        {tabActivo === 'preasiento' && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-4 rounded-lg border p-4" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
              <div className="max-w-3xl">
                <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--ink)' }}>
                  <BookOpenCheck size={17} style={{ color: 'var(--tone-purple-text)' }} />
                  Preasiento de insumos del activo biológico
                </div>
                <p className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>
                  Débitos agrupados por cuenta, lote y ciclo; crédito a Inventario de Insumos.
                  Se envía pendiente al ERP para revisión. Esta vista no contabiliza.
                </p>
              </div>
              <div className="flex items-end gap-2">
                <div>
                  <label className={labelCls}>Fecha contable</label>
                  <input type="date" className={inputCls} value={fechaContable} onChange={(e) => setFechaContable(e.target.value)} />
                </div>
                <button type="button" disabled={loading || generandoPreasiento || resumenCongelado.movimientos === 0}
                  onClick={() => setConfirmarPreasiento(true)}
                  className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"
                  style={{ background: 'var(--emp-accent)' }}>
                  {generandoPreasiento ? <Loader2 size={14} className="animate-spin" /> : <BookOpenCheck size={14} />}
                  Generar preasiento
                </button>
              </div>
            </div>

            {preasientos.length === 0 ? (
              <div className="rounded-lg border py-12 text-center text-sm" style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)', background: 'var(--surface-raised)' }}>
                No hay preasientos para el período seleccionado.
              </div>
            ) : preasientos.map((pre) => {
              const detalle = lineasPreasiento.filter((linea) => linea.preasiento_id === pre.id);
              return (
                <details key={pre.id} open={pre === preasientos[0]} className="overflow-hidden rounded-lg border" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
                  <summary className="cursor-pointer px-4 py-3">
                    <div className="inline-flex w-[calc(100%-1rem)] flex-wrap items-center gap-x-5 gap-y-2 align-middle">
                      <span className="font-mono text-xs font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{pre.referencia}</span>
                      <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>{pre.periodo_desde} al {pre.periodo_hasta}</span>
                      <span className="rounded-full px-2 py-1 text-[10px] font-bold uppercase" style={{ color: 'var(--tone-warn-text)', background: 'var(--tone-warn-bg)' }}>{pre.estado}</span>
                      <span className="ml-auto font-mono text-sm font-bold" style={{ color: 'var(--ink)' }}>{fmtCRC(Number(pre.total_debito_crc))}</span>
                      {pre.estado !== 'anulado' && pre.estado !== 'contabilizado' && pre.estado !== 'reversado' && (
                        <button type="button" className="rounded border px-3 py-1.5 text-[10px] font-bold"
                          style={{ color: '#f87171', borderColor: '#7f1d1d' }}
                          onClick={(event) => { event.preventDefault(); event.stopPropagation(); setPreasientoAnular(pre); setMotivoAnulacion(''); }}>
                          Anular preasiento
                        </button>
                      )}
                    </div>
                  </summary>
                  {pre.estado === 'anulado' && pre.motivo_anulacion && (
                    <div className="border-t px-4 py-2 text-xs" style={{ borderColor: 'var(--line)', color: 'var(--tone-warn-text)' }}>
                      Anulado: {pre.motivo_anulacion}
                    </div>
                  )}
                  {pre.estado === 'reversado' && (
                    <div className="border-t px-4 py-3 text-xs" style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>
                      <b>Reversado contablemente.</b> La cédula y la fotografía permanecen históricas y bloqueadas.
                      {pre.asiento_id && <> Asiento original #{pre.asiento_id}.</>}
                      {pre.asiento_reversion_id && <> Contraasiento #{pre.asiento_reversion_id}.</>}
                      {pre.motivo_reversion && <div className="mt-1">Motivo: {pre.motivo_reversion}</div>}
                    </div>
                  )}
                  <div className={tableWrapCls}>
                    <table className="min-w-full">
                      <thead className={theadCls}><tr>
                        <th className={thCls}>Cuenta</th><th className={thCls}>Detalle</th>
                        <th className={`${thCls} text-right`}>Débito</th><th className={`${thCls} text-right`}>Crédito</th>
                      </tr></thead>
                      <tbody>{detalle.map((linea) => (
                        <tr key={linea.id} className={trCls}>
                          <td className={tdCls}><div className="font-mono text-xs font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{linea.cuenta_codigo}</div><div className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{linea.cuenta_nombre}</div></td>
                          <td className={tdCls}><div>{linea.descripcion}</div>{linea.lote_num && <div className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>Lote {linea.lote_num} · {linea.ciclo_cosecha}</div>}</td>
                          <td className={`${tdCls} text-right font-mono`}>{Number(linea.debito_crc) > 0 ? fmtCRC(Number(linea.debito_crc)) : '—'}</td>
                          <td className={`${tdCls} text-right font-mono`}>{Number(linea.credito_crc) > 0 ? fmtCRC(Number(linea.credito_crc)) : '—'}</td>
                        </tr>
                      ))}</tbody>
                      <tfoot><tr className="border-t font-bold" style={{ borderColor: 'var(--line)' }}>
                        <td className={tdCls} colSpan={2}>Totales · diferencia {fmtCRC(Number(pre.diferencia_crc))}</td>
                        <td className={`${tdCls} text-right font-mono`}>{fmtCRC(Number(pre.total_debito_crc))}</td>
                        <td className={`${tdCls} text-right font-mono`}>{fmtCRC(Number(pre.total_credito_crc))}</td>
                      </tr></tfoot>
                    </table>
                  </div>
                </details>
              );
            })}
          </div>
        )}

        {tabActivo === 'preparacion' && (
          <div className="space-y-4">
            <div className="rounded-lg border p-4" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="max-w-3xl">
                  <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--ink)' }}>
                    <ShieldCheck size={17} style={{ color: 'var(--tone-success-text)' }} />
                    Costos reales congelados
                  </div>
                  <p className="mt-1 text-xs leading-relaxed" style={{ color: 'var(--ink-muted)' }}>
                    Congela la cantidad, costo CRC, cédula, lote, ciclo y cuentas de cada salida válida.
                    Es idempotente: una salida no puede incluirse dos veces. No crea asientos.
                  </p>
                </div>
                <button
                  type="button"
                  disabled={loading || preparando || cuentasFaltantes.length > 0 || criterioFecha !== 'aplicacion'}
                  onClick={() => setConfirmarPreparacion(true)}
                  className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"
                  style={{ background: 'var(--emp-accent)' }}
                >
                  {preparando ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                  Preparar costos del período
                </button>
              </div>
              {criterioFecha !== 'aplicacion' && (
                <div className="mt-3 text-xs" style={{ color: 'var(--tone-warn-text)' }}>
                  Para preparar costos seleccione “Aplicación real (contable)” como criterio de fecha.
                </div>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <Kpi label="Movimientos congelados" value={String(resumenCongelado.movimientos)} icon={<ShieldCheck size={15} />} tone="var(--tone-success-text)" />
              <Kpi label="Lotes incluidos" value={String(resumenCongelado.lotes)} icon={<Sprout size={15} />} tone="var(--tone-info-text)" />
              <Kpi label="Costo preparado" value={fmtCRC(resumenCongelado.total)} icon={<BookOpenCheck size={15} />} tone="var(--tone-success-text)" />
            </div>

            {resultadoPreparacion && (
              <div className="rounded-lg border px-4 py-3 text-xs" style={{ borderColor: 'var(--tone-success-border)', background: 'var(--tone-success-bg)', color: 'var(--ink)' }}>
                <div className="font-bold" style={{ color: 'var(--tone-success-text)' }}>Preparación finalizada</div>
                <div className="mt-2 grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
                  <span>Revisados: <b>{resultadoPreparacion.movimientos_en_periodo}</b></span>
                  <span>Nuevos: <b>{resultadoPreparacion.insertados}</b></span>
                  <span>Distribuciones: <b>{resultadoPreparacion.distribuciones_creadas || 0}</b></span>
                  <span>Sin valor: <b>{resultadoPreparacion.sin_valor}</b></span>
                  <span>Sin vínculo: <b>{resultadoPreparacion.sin_vinculo}</b></span>
                  <span>Sin cuenta: <b>{resultadoPreparacion.sin_cuenta}</b></span>
                </div>
              </div>
            )}

            <div className="rounded-lg border px-4 py-3 text-xs" style={{ borderColor: 'var(--line)', background: 'var(--surface-overlay)', color: 'var(--ink-muted)' }}>
              Solo los movimientos congelados estarán disponibles para el futuro preasiento.
              Los consumos sin movimiento, costo cero, vínculo inválido o cuenta faltante permanecen excluidos.
            </div>
          </div>
        )}

        {tabActivo === 'trazabilidad' && (
        <div className="mb-4 rounded-lg border" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
          <div className="flex flex-wrap items-end justify-between gap-3 border-b px-4 py-3" style={{ borderColor: 'var(--line)' }}>
            <div>
              <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--ink)' }}>
                <Search size={16} style={{ color: 'var(--emp-accent-txt)' }} />
                Trazabilidad por producto
              </div>
              <div className="mt-1 text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                Consulte en cuáles cédulas se aplicó un producto y si tiene salida de inventario valorizada.
              </div>
            </div>
            <div className="w-full sm:w-[430px]">
              <div className="mb-1 flex items-center justify-between gap-2">
                <label className={labelCls}>Buscar producto</label>
                <button
                  type="button"
                  className="rounded-full border px-2.5 py-1 text-[10px] font-bold"
                  style={{
                    color: soloCostoCero ? 'var(--tone-warn-text)' : 'var(--ink-muted)',
                    borderColor: soloCostoCero ? 'var(--tone-warn-border)' : 'var(--line)',
                    background: soloCostoCero ? 'var(--tone-warn-bg)' : 'transparent',
                  }}
                  onClick={() => {
                    setSoloCostoCero((actual) => !actual);
                    setProductoTrazado('');
                  }}
                >
                  {soloCostoCero ? `Costo cero (${resumen.movimientosSinValor}) ×` : 'Filtrar costo cero'}
                </button>
              </div>
              <div className="relative">
                <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-faint)' }} />
                <input
                  className={`${inputCls} pl-9`}
                  value={busquedaProducto}
                  onChange={(e) => setBusquedaProducto(e.target.value)}
                  placeholder="Buscar por código o nombre…"
                />
              </div>
            </div>
          </div>

          <div className="max-h-[520px] overflow-auto">
            {productosEncontrados.length === 0 ? (
              <div className="px-4 py-10 text-center text-xs" style={{ color: 'var(--ink-muted)' }}>
                No hay productos que coincidan con “{busquedaProducto}”.
              </div>
            ) : productosEncontrados.map((producto) => {
              const abierto = productoTrazado === producto.key;
              const alertas = trazabilidad.filter((item) => {
                const key = item.productoId ? String(item.productoId) : `codigo:${item.codigo}`;
                return key === producto.key && item.estado !== 'VALORIZADO';
              }).length;
              return (
                <div key={producto.key} className="border-b last:border-b-0" style={{ borderColor: 'var(--line)' }}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-4 py-3 text-left"
                    style={{ background: abierto ? 'var(--emp-accent-bg)' : 'transparent' }}
                    onClick={() => setProductoTrazado(abierto ? '' : producto.key)}
                  >
                    {abierto ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <span className="min-w-[64px] font-mono text-xs font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{producto.codigo}</span>
                    <span className="min-w-0 flex-1 truncate text-xs font-semibold" style={{ color: 'var(--ink)' }}>{producto.producto}</span>
                    <span className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>{producto.cedulas} cédula{producto.cedulas === 1 ? '' : 's'}</span>
                    {alertas > 0 && (
                      <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: 'var(--tone-warn-text)' }}>
                        <AlertTriangle size={12} /> {alertas}
                      </span>
                    )}
                  </button>
                  {abierto && (
                    <div className="overflow-auto border-t" style={{ borderColor: 'var(--line)' }}>
              <table className="min-w-full">
                <thead className={`${theadCls} sticky top-0`}>
                  <tr>
                    <th className={thCls}>Cédula</th>
                    <th className={thCls}>Fecha</th>
                    <th className={thCls}>Lote</th>
                    <th className={`${thCls} text-right`}>Cantidad</th>
                    <th className={thCls}>Movimiento</th>
                            <th className={`${thCls} text-right`}>Costo unitario</th>
                            <th className={thCls}>Control</th>
                            <th className={`${thCls} text-right`}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {trazabilidadFiltrada.map((item) => (
                    <tr className={trCls} key={item.lineaId}>
                              <td className={tdCls}>
                        <div className="font-mono font-bold" style={{ color: 'var(--ink)' }}>{item.boleta}</div>
                        <div className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>Línea {item.lineaId}</div>
                      </td>
                      <td className={`${tdCls} font-mono`}>{item.fecha || '—'}</td>
                      <td className={tdCls}>
                        <div className="font-bold" style={{ color: 'var(--ink)' }}>Lote {item.lote}</div>
                        <div className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>{item.finca || '—'}</div>
                      </td>
                      <td className={`${tdCls} text-right font-mono`}>{fmtNumber(item.cantidad, 3)} {item.unidad}</td>
                      <td className={`${tdCls} font-mono`}>{item.movimientoId ? `#${item.movimientoId}` : 'Sin salida'}</td>
                      <td className={`${tdCls} text-right font-mono`}>
                        {item.movimientoId ? fmtCRC(item.costoUnitario) : '—'}
                      </td>
                      <td className={tdCls}>
                        {item.estado === 'VALORIZADO' ? (
                          <span className="inline-flex items-center gap-1" style={{ color: 'var(--tone-success-text)' }}>
                            <CheckCircle2 size={13} /> Valorizado
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1" style={{ color: 'var(--tone-warn-text)' }}>
                            <AlertTriangle size={13} />
                            {item.estado === 'COSTO_CERO' ? 'Costo cero' : 'Sin movimiento'}
                          </span>
                                )}
                              </td>
                              <td className={`${tdCls} text-right`}>
                                {item.estado === 'COSTO_CERO' && item.movimientoId && (
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-[11px] font-bold"
                                    style={{ color: 'var(--emp-accent-txt)', borderColor: 'var(--line)', background: 'var(--surface-raised)' }}
                                    onClick={() => {
                                      setEditando(item);
                                      setBusquedaCorreccion('');
                                      setProductoCorreccion(null);
                                      setMotivoCorreccion('');
                                      setErrorCorreccion('');
                                    }}
                                  >
                                    <Pencil size={12} /> Editar
                                  </button>
                                )}
                              </td>
                    </tr>
                  ))}
                </tbody>
              </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        )}

        {tabActivo === 'regularizacion' && (
          <div className="mb-4 space-y-3">
            <div className="flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3" style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)' }}>
              <AlertTriangle size={18} style={{ color: 'var(--tone-warn-text)' }} />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold" style={{ color: 'var(--tone-warn-text)' }}>Cola de transición desde el 30/06/2026</div>
                <div className="mt-1 text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                  Estos consumos se conservan para valoración histórica. No generan salidas ni afectan el inventario operativo antes de la toma física.
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono text-lg font-bold" style={{ color: 'var(--ink)' }}>{regularizacion.length}</div>
                <div className="text-[9px] uppercase" style={{ color: 'var(--ink-muted)' }}>cédulas pendientes</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-lg font-bold" style={{ color: 'var(--tone-info-text)' }}>{fmtCRC(totalRegularizacion)}</div>
                <div className="text-[9px] uppercase" style={{ color: 'var(--ink-muted)' }}>referencial, no contabilizado</div>
              </div>
            </div>

            <div className="rounded-lg border" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
              <div className="flex flex-wrap items-end justify-between gap-3 border-b px-4 py-3" style={{ borderColor: 'var(--line)' }}>
                <div>
                  <div className="text-sm font-bold" style={{ color: 'var(--ink)' }}>Consumos pendientes de regularización</div>
                  <div className="mt-1 text-[10px]" style={{ color: 'var(--ink-muted)' }}>Fuente: cédulas aplicadas del ERP sin salida de inventario vinculada.</div>
                </div>
                <div className="w-full sm:w-[410px]">
                  <label className={labelCls}>Filtrar cédula, lote o producto</label>
                  <div className="relative">
                    <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-faint)' }} />
                    <input className={`${inputCls} pl-9`} value={busquedaRegularizacion} onChange={(e) => setBusquedaRegularizacion(e.target.value)} placeholder="Buscar por número, lote, código o nombre…" />
                  </div>
                </div>
              </div>
              <div className="max-h-[520px] overflow-auto">
                <table className="min-w-full">
                  <thead className={`${theadCls} sticky top-0`}>
                    <tr>
                      <th className={thCls}>Cédula</th>
                      <th className={thCls}>Fecha real</th>
                      <th className={thCls}>Lote / etapa</th>
                      <th className={thCls}>Productos</th>
                      <th className={`${thCls} text-right`}>Costo referencial</th>
                      <th className={thCls}>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regularizacionFiltrada.map((item) => (
                      <tr className={trCls} key={item.id}>
                        <td className={tdCls}>
                          <div className="font-mono font-bold" style={{ color: 'var(--ink)' }}>{item.numero}</div>
                          <div className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>ID {item.id}</div>
                        </td>
                        <td className={`${tdCls} font-mono`}>{item.fechaAplicacion || '—'}</td>
                        <td className={tdCls}>
                          <div className="font-bold" style={{ color: 'var(--ink)' }}>Lote {item.lote}</div>
                          <div className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{item.etapa} · {item.ciclo}</div>
                        </td>
                        <td className={tdCls}>
                          <div className="font-bold" style={{ color: 'var(--ink)' }}>{item.productos} producto{item.productos === 1 ? '' : 's'}</div>
                          <div className="max-w-[430px] truncate text-[10px]" title={item.detalleProductos} style={{ color: 'var(--ink-muted)' }}>{item.detalleProductos || 'Sin detalle de productos'}</div>
                        </td>
                        <td className={`${tdCls} text-right font-mono`}>{fmtCRC(item.costoReferencial)}</td>
                        <td className={tdCls}>
                          <span className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[9px] font-bold" style={{ color: 'var(--tone-warn-text)', borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)' }}>
                            <AlertTriangle size={11} /> Pendiente inventario inicial
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {regularizacionFiltrada.length === 0 && (
                  <div className="py-12 text-center text-xs" style={{ color: 'var(--ink-muted)' }}>No hay coincidencias para el filtro.</div>
                )}
              </div>
            </div>
          </div>
        )}

        {tabActivo === 'simulador' && (
          <div className="space-y-4">
            <div className="flex gap-3 rounded-xl border p-4" style={{ borderColor: 'var(--tone-info-border)', background: 'var(--surface-raised)' }}>
              <ShieldCheck size={20} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--tone-info-text)' }} />
              <div>
                <div className="text-sm font-bold" style={{ color: 'var(--ink)' }}>Entorno de análisis — sin efecto contable</div>
                <p className="mt-1 text-xs leading-relaxed" style={{ color: 'var(--ink-muted)' }}>
                  Esta simulación no modifica cédulas, movimientos, costos congelados ni asientos. La asignación directa continúa siendo la regla normal.
                </p>
              </div>
            </div>

            <div className="grid gap-4 xl:grid-cols-[1.15fr_.85fr]">
              <div className="rounded-xl border p-5" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
                <h2 className="text-sm font-bold" style={{ color: 'var(--ink)' }}>Definir escenario</h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Producto ERP</label>
                    <select className={selectCls} value={productoSimulado} onChange={(e) => setProductoSimulado(e.target.value)}>
                      <option value="">Seleccione un producto aplicado…</option>
                      {productosTrazables.map((producto) => (
                        <option key={producto.key} value={producto.key}>{producto.codigo} · {producto.producto}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Costo por analizar (CRC)</label>
                    <input className={inputCls} type="number" min="0" step="0.01" value={costoSimulado}
                      onChange={(e) => setCostoSimulado(e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>Método</label>
                    <select className={selectCls} value={criterioDistribucion}
                      onChange={(e) => setCriterioDistribucion(e.target.value as typeof criterioDistribucion)}>
                      <option value="directa">Asignación directa (100 %)</option>
                      <option value="porcentaje">Porcentaje PC / RC documentado</option>
                      <option value="dosis">Dosis técnicamente medida</option>
                      <option value="hectareas">Hectáreas beneficiadas</option>
                      <option value="plantas">Plantas beneficiadas</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Porcentaje para PC</label>
                    <div className="relative">
                      <input className={`${inputCls} pr-9`} type="number" min="0" max="100" step="0.01"
                        disabled={criterioDistribucion === 'directa'} value={criterioDistribucion === 'directa' ? '100' : porcentajePC}
                        onChange={(e) => setPorcentajePC(e.target.value)} />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs" style={{ color: 'var(--ink-muted)' }}>%</span>
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Porcentaje para RC</label>
                    <div className={`${inputCls} flex items-center justify-between`} style={{ color: 'var(--ink-muted)', background: 'var(--surface-overlay)' }}>
                      <span>{fmtNumber(resultadoSimulacion.rc, 2)}</span><span>%</span>
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Fundamento técnico de la excepción</label>
                    <textarea className={inputCls} rows={3} value={fundamentoSimulacion}
                      onChange={(e) => setFundamentoSimulacion(e.target.value)}
                      disabled={criterioDistribucion === 'directa'}
                      placeholder="Evidencia agronómica, cosechas beneficiadas, fuente y responsable…" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Referencia de evidencia</label>
                    <input className={inputCls} value={evidenciaSimulacion}
                      onChange={(e) => setEvidenciaSimulacion(e.target.value)}
                      placeholder="Informe, ensayo, recomendación técnica o documento de respaldo" />
                  </div>
                  <div>
                    <label className={labelCls}>Vigente desde</label>
                    <input className={inputCls} type="date" value={vigenciaDesdeRegla}
                      onChange={(e) => setVigenciaDesdeRegla(e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>Vigente hasta (opcional)</label>
                    <input className={inputCls} type="date" value={vigenciaHastaRegla}
                      min={vigenciaDesdeRegla} onChange={(e) => setVigenciaHastaRegla(e.target.value)} />
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-4" style={{ borderColor: 'var(--line)' }}>
                  <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>Se creará una nueva versión en estado borrador.</span>
                  <button type="button" onClick={() => void guardarRegla()} disabled={guardandoRegla || !productoSimulado}
                    className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"
                    style={{ background: 'var(--emp-accent)' }}>
                    {guardandoRegla ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Guardar borrador
                  </button>
                </div>
              </div>

              <div className="rounded-xl border p-5" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
                <h2 className="text-sm font-bold" style={{ color: 'var(--ink)' }}>Resultado proyectado</h2>
                <div className="mt-4 space-y-3">
                  <div className="rounded-lg border p-4" style={{ borderColor: 'var(--tone-success-border)', background: 'var(--tone-success-bg)' }}>
                    <div className="text-[11px] font-bold uppercase" style={{ color: 'var(--tone-success-text)' }}>Primera cosecha (PC)</div>
                    <div className="mt-2 font-mono text-xl font-bold" style={{ color: 'var(--ink)' }}>{fmtCRC(resultadoSimulacion.costoPC)}</div>
                    <div className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>{fmtNumber(resultadoSimulacion.pc, 2)} % del costo</div>
                  </div>
                  <div className="rounded-lg border p-4" style={{ borderColor: 'var(--line)', background: 'var(--surface-overlay)' }}>
                    <div className="text-[11px] font-bold uppercase" style={{ color: 'var(--tone-purple-text)' }}>Retoño / segunda cosecha (RC)</div>
                    <div className="mt-2 font-mono text-xl font-bold" style={{ color: 'var(--ink)' }}>{fmtCRC(resultadoSimulacion.costoRC)}</div>
                    <div className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>{fmtNumber(resultadoSimulacion.rc, 2)} % del costo</div>
                  </div>
                  <div className="flex items-center justify-between border-t pt-3 text-xs" style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
                    <span>Control de integridad</span>
                    <b style={{ color: resultadoSimulacion.pc + resultadoSimulacion.rc === 100 ? 'var(--tone-success-text)' : 'var(--tone-warn-text)' }}>
                      {fmtNumber(resultadoSimulacion.pc + resultadoSimulacion.rc, 2)} % · {fmtCRC(resultadoSimulacion.costo)}
                    </b>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg border p-4 text-xs leading-relaxed" style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>
              Una excepción solo podrá guardarse cuando incluya producto, conductor medible, fundamento técnico, vigencia y aprobación. Nunca se aplicará retroactivamente a fotografías o asientos firmes.
            </div>

            {mensajeRegla && (
              <div className="rounded-lg border px-4 py-3 text-xs" style={{ borderColor: 'var(--line)', background: 'var(--surface-overlay)', color: 'var(--ink)' }}>
                {mensajeRegla}
              </div>
            )}

            <div className="overflow-hidden rounded-xl border" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
              <div className="flex items-center justify-between gap-3 border-b px-4 py-3" style={{ borderColor: 'var(--line)' }}>
                <div>
                  <h2 className="text-sm font-bold" style={{ color: 'var(--ink)' }}>Versiones registradas</h2>
                  <p className="mt-1 text-[11px]" style={{ color: 'var(--ink-muted)' }}>La aprobación exige permiso contable. Las versiones aprobadas conservan su historial.</p>
                </div>
                <button type="button" className={btnSecondary} onClick={() => void cargarReglas()}><RefreshCw size={13} className="mr-2 inline" />Actualizar</button>
              </div>
              {reglasCapitalizacion.length === 0 ? (
                <div className="py-10 text-center text-xs" style={{ color: 'var(--ink-muted)' }}>Todavía no hay reglas versionadas.</div>
              ) : (
                <div className="overflow-auto">
                  <table className="min-w-[980px] w-full">
                    <thead className={theadCls}><tr>
                      <th className={thCls}>Producto / versión</th><th className={thCls}>Método</th>
                      <th className={thCls}>Distribución</th><th className={thCls}>Vigencia</th>
                      <th className={thCls}>Evidencia</th><th className={thCls}>Estado</th><th className={`${thCls} text-right`}>Acción</th>
                    </tr></thead>
                    <tbody>{reglasCapitalizacion.map((regla) => (
                      <tr key={regla.id} className={trCls}>
                        <td className={tdCls}><div className="font-semibold">{regla.producto_codigo} · {regla.producto_nombre}</div><div className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>Versión {regla.version} · {new Date(regla.creado_at).toLocaleString('es-CR')}</div></td>
                        <td className={`${tdCls} capitalize`}>{regla.metodo}</td>
                        <td className={`${tdCls} font-mono`}>PC {fmtNumber(Number(regla.porcentaje_pc), 2)} % · RC {fmtNumber(Number(regla.porcentaje_rc), 2)} %</td>
                        <td className={`${tdCls} font-mono`}>{regla.vigencia_desde} → {regla.vigencia_hasta || 'abierta'}</td>
                        <td className={tdCls}><div className="max-w-[220px] truncate" title={regla.fundamento_tecnico}>{regla.evidencia_referencia || 'Asignación directa'}</div></td>
                        <td className={tdCls}><span className="rounded-full border px-2 py-1 text-[10px] font-bold uppercase"
                          style={{
                            color: regla.estado === 'aprobada' ? 'var(--tone-success-text)' : regla.estado === 'borrador' ? 'var(--tone-warn-text)' : 'var(--ink-muted)',
                            borderColor: regla.estado === 'aprobada' ? 'var(--tone-success-border)' : 'var(--line)',
                          }}>{regla.estado}</span></td>
                        <td className={`${tdCls} text-right`}>
                          {regla.estado === 'borrador' && (
                            <button type="button" disabled={accionRegla === regla.id} onClick={() => void aprobarRegla(regla)}
                              className="rounded-md border px-3 py-1.5 text-[10px] font-bold disabled:opacity-40"
                              style={{ color: 'var(--tone-success-text)', borderColor: 'var(--tone-success-border)' }}>
                              {accionRegla === regla.id ? 'Aprobando…' : 'Aprobar'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="overflow-hidden rounded-xl border" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
              <div className="border-b px-4 py-3" style={{ borderColor: 'var(--line)' }}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-sm font-bold" style={{ color: 'var(--ink)' }}>Impacto prospectivo</h2>
                    <p className="mt-1 text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                      Aplicaciones valorizadas, posteriores a la aprobación y todavía no congeladas. Es una vista previa; no genera movimientos ni asientos.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 text-[11px]">
                    <span className="rounded-md border px-3 py-2" style={{ borderColor: 'var(--line)' }}>Total <b>{fmtCRC(totalesImpacto.costo)}</b></span>
                    <span className="rounded-md border px-3 py-2" style={{ borderColor: 'var(--tone-success-border)', color: 'var(--tone-success-text)' }}>PC <b>{fmtCRC(totalesImpacto.pc)}</b></span>
                    <span className="rounded-md border px-3 py-2" style={{ borderColor: 'var(--line)', color: 'var(--tone-purple-text)' }}>RC <b>{fmtCRC(totalesImpacto.rc)}</b></span>
                  </div>
                </div>
              </div>
              {impactoProspectivo.length === 0 ? (
                <div className="py-10 text-center text-xs" style={{ color: 'var(--ink-muted)' }}>
                  No hay aplicaciones nuevas elegibles bajo las reglas aprobadas en el período seleccionado.
                </div>
              ) : (
                <div className="max-h-[420px] overflow-auto">
                  <table className="min-w-[1080px] w-full">
                    <thead className={`${theadCls} sticky top-0`}><tr>
                      <th className={thCls}>Cédula / movimiento</th><th className={thCls}>Fecha</th>
                      <th className={thCls}>Producto</th><th className={thCls}>Lote</th>
                      <th className={`${thCls} text-right`}>Costo real</th><th className={`${thCls} text-right`}>PC</th>
                      <th className={`${thCls} text-right`}>RC</th><th className={thCls}>Regla</th>
                    </tr></thead>
                    <tbody>{impactoProspectivo.map((item) => (
                      <tr key={item.lineaId} className={trCls}>
                        <td className={tdCls}><div className="font-mono font-bold">{item.boleta}</div><div className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>Salida #{item.movimientoId}</div></td>
                        <td className={`${tdCls} font-mono`}>{item.fecha}</td>
                        <td className={tdCls}><div className="font-semibold">{item.codigo} · {item.producto}</div><div className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{fmtNumber(item.cantidad, 3)} {item.unidad}</div></td>
                        <td className={tdCls}><div className="font-semibold">Lote {item.lote}</div><div className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{item.finca}</div></td>
                        <td className={`${tdCls} text-right font-mono`}>{fmtCRC(item.costo)}</td>
                        <td className={`${tdCls} text-right font-mono`} style={{ color: 'var(--tone-success-text)' }}>{fmtCRC(item.costoPC)}</td>
                        <td className={`${tdCls} text-right font-mono`} style={{ color: 'var(--tone-purple-text)' }}>{fmtCRC(item.costoRC)}</td>
                        <td className={tdCls}><div className="text-[10px] font-bold uppercase">{item.regla.metodo} · v{item.regla.version}</div><div className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>PC {fmtNumber(Number(item.regla.porcentaje_pc), 2)} % / RC {fmtNumber(Number(item.regla.porcentaje_rc), 2)} %</div></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {tabActivo === 'politica' && (
          <div className="space-y-4">
            <div className="rounded-xl border p-5" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
              <div className="flex items-start gap-3">
                <ShieldCheck size={22} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--tone-success-text)' }} />
                <div>
                  <h2 className="text-base font-bold" style={{ color: 'var(--ink)' }}>Regla general aprobada</h2>
                  <p className="mt-2 text-sm leading-relaxed" style={{ color: 'var(--ink-muted)' }}>
                    Todo insumo con trazabilidad directa se asigna en un <b style={{ color: 'var(--ink)' }}>100 % al lote y a la cosecha destino</b>.
                    La etapa documenta cuándo se aplicó; no divide automáticamente el costo durante los meses estimados de efecto.
                  </p>
                </div>
              </div>
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-xl border p-5" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
                <div className="mb-3 text-sm font-bold" style={{ color: 'var(--ink)' }}>Asignación directa — regla normal</div>
                <ul className="space-y-2 text-xs leading-relaxed" style={{ color: 'var(--ink-muted)' }}>
                  <li>• Conserva cédula, salida real, producto, lote, ciclo y costo congelado.</li>
                  <li>• Un producto aplicado para PC se carga completamente a PC.</li>
                  <li>• No requiere porcentajes ni una estimación de residualidad.</li>
                  <li>• Es el método prioritario por ser medible y verificable.</li>
                </ul>
              </div>
              <div className="rounded-xl border p-5" style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--surface-raised)' }}>
                <div className="mb-3 text-sm font-bold" style={{ color: 'var(--tone-warn-text)' }}>Distribución — excepción controlada</div>
                <ul className="space-y-2 text-xs leading-relaxed" style={{ color: 'var(--ink-muted)' }}>
                  <li>• Solo cuando existe beneficio material para más de una cosecha.</li>
                  <li>• Exige fundamento técnico, conductor, porcentajes y aprobación.</li>
                  <li>• Puede usar dosis, hectáreas, plantas o porcentaje PC/RC documentado.</li>
                  <li>• No se admite distribuir únicamente por una duración estimada del efecto.</li>
                </ul>
              </div>
            </div>
            <div className="rounded-xl border p-5" style={{ borderColor: 'var(--line)', background: 'var(--surface-overlay)' }}>
              <div className="mb-3 text-sm font-bold" style={{ color: 'var(--ink)' }}>Separación obligatoria</div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg border p-3" style={{ borderColor: 'var(--line)' }}><b className="text-xs" style={{ color: 'var(--tone-info-text)' }}>Costo agrícola</b><p className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>Acumulación real para gestión y trazabilidad.</p></div>
                <div className="rounded-lg border p-3" style={{ borderColor: 'var(--line)' }}><b className="text-xs" style={{ color: 'var(--tone-success-text)' }}>Medición contable</b><p className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>Política NIC 41 y valor razonable cuando corresponda.</p></div>
                <div className="rounded-lg border p-3" style={{ borderColor: 'var(--line)' }}><b className="text-xs" style={{ color: 'var(--tone-purple-text)' }}>Base tributaria</b><p className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>Conciliación fiscal separada por cosecha.</p></div>
              </div>
            </div>
            <div className="rounded-lg border p-4 text-xs leading-relaxed" style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
              El agua requerida permanece como línea técnica de cálculo. No genera salida de inventario. Los costos de bombeo o energía, si se incorporan, se tratarán como servicios agrícolas separados.
              Ninguna regla futura recalculará automáticamente fotografías o asientos firmes anteriores.
            </div>
          </div>
        )}

        {tabActivo === 'expediente' && (
          <div className="space-y-4">
            <div className="rounded-lg border p-4" style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-sm font-bold" style={{ color: 'var(--ink)' }}>
                    <Sprout size={17} style={{ color: 'var(--emp-accent-txt)' }} />
                    Expediente contable por lote
                  </div>
                  <p className="mt-1 text-xs" style={{ color: 'var(--ink-muted)' }}>
                    Saldo acumulado de costos reales congelados. Esta consulta no reclasifica cuentas ni genera asientos.
                  </p>
                </div>
                <div className="w-full sm:w-80">
                  <label className={labelCls}>Buscar lote, finca o etapa</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2" size={15} style={{ color: 'var(--ink-muted)' }} />
                    <input className={`${inputCls} pl-9`} value={busquedaExpediente}
                      onChange={(event) => setBusquedaExpediente(event.target.value)}
                      placeholder="Ej. 58, Guayabito, Forza..." />
                  </div>
                </div>
              </div>
            </div>
            <div className={tableWrapCls}>
              <table className="min-w-[1180px] w-full text-xs">
                <thead className={theadCls}><tr>
                  <th className={thCls}>Lote / finca</th>
                  <th className={thCls}>Área / variedad</th>
                  <th className={thCls}>Ciclo / etapa actual</th>
                  <th className={thCls}>Cuenta estimada</th>
                  <th className={`${thCls} text-right`}>Movimientos</th>
                  <th className={`${thCls} text-right`}>Costo acumulado</th>
                  <th className={`${thCls} text-right`}>Costo / ha</th>
                  <th className={thCls}>Última aplicación</th>
                </tr></thead>
                <tbody>
                  {expedientes.filter((item) => {
                    const needle = busquedaExpediente.trim().toLowerCase();
                    return !needle || `${item.numero} ${item.nombre} ${item.finca} ${item.ciclo} ${item.etapa}`.toLowerCase().includes(needle);
                  }).map((item) => (
                    <tr key={item.id} className={trCls}>
                      <td className={tdCls}><b>Lote {item.numero}</b><div style={{ color: 'var(--ink-muted)' }}>{item.finca}</div></td>
                      <td className={tdCls}>{fmtNumber(item.area, 2)} ha<div style={{ color: 'var(--ink-muted)' }}>{item.variedad}</div></td>
                      <td className={tdCls}><b>{item.ciclo}</b><div style={{ color: 'var(--ink-muted)' }}>{item.etapa}</div></td>
                      <td className={tdCls}>{item.cuenta ? <><span className="font-mono">{item.cuenta.codigo}</span><div style={{ color: 'var(--ink-muted)' }}>{item.cuenta.nombre}</div></> : <span style={{ color: 'var(--tone-warn-text)' }}>Pendiente de clasificación</span>}</td>
                      <td className={`${tdCls} text-right font-mono`}>{item.movimientos}</td>
                      <td className={`${tdCls} text-right font-mono font-bold`}>{fmtCRC(item.costoAcumulado)}</td>
                      <td className={`${tdCls} text-right font-mono`}>{fmtCRC(item.costoHa)}</td>
                      <td className={tdCls}>{item.ultimaAplicacion || 'Sin aplicaciones'}{item.fechaPrimeraSiembra && <div style={{ color: 'var(--ink-muted)' }}>Primera siembra: {item.fechaPrimeraSiembra}</div>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!loading && expedientes.length === 0 && <div className="p-10 text-center text-xs" style={{ color: 'var(--ink-muted)' }}>No hay lotes maestros disponibles.</div>}
            </div>
            <div className="rounded-lg border p-4 text-xs leading-relaxed" style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
              La cuenta mostrada es una clasificación operativa según la última etapa registrada. Cualquier reclasificación o medición a valor razonable requerirá revisión y aprobación contable.
            </div>
          </div>
        )}

        {(tabActivo === 'resumen' || tabActivo === 'sin-movimiento') && (
        <>
        {tabActivo === 'resumen' && !loading && resumen.movimientosSinValor > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 text-xs" style={{ color: 'var(--tone-warn-text)', borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)' }}>
            <AlertTriangle size={17} className="mt-0.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-bold">{resumen.movimientosSinValor} movimientos tienen cantidad real, pero costo unitario cero.</div>
              <div className="mt-1">Se muestran en el valor referencial para análisis, pero permanecen fuera del costo capitalizable y de cualquier preasiento.</div>
            </div>
            <button
              type="button"
              className="rounded-md border px-3 py-2 text-[11px] font-bold"
              style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--surface-raised)' }}
              onClick={() => {
                setSoloCostoCero(true);
                setProductoTrazado('');
                setBusquedaProducto('');
                setTabActivo('trazabilidad');
              }}
            >
              Ver productos / cédulas
            </button>
          </div>
        )}

        {tabActivo === 'sin-movimiento' && !loading && sinMovimiento.length > 0 && (
          <div className="mb-4 rounded-lg border" style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--surface-raised)' }}>
            <button
              type="button"
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
              onClick={() => setMostrarSinMovimiento((actual) => !actual)}
            >
              <span className="flex items-center gap-2 text-xs font-bold" style={{ color: 'var(--tone-warn-text)' }}>
                <AlertTriangle size={15} />
                Verificar {sinMovimiento.length} aplicaciones sin movimiento de inventario
              </span>
              <span className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                {mostrarSinMovimiento ? 'Ocultar detalle' : 'Mostrar detalle'}
              </span>
            </button>
            {mostrarSinMovimiento && (
              <div className="max-h-[360px] overflow-auto border-t" style={{ borderColor: 'var(--line)' }}>
                <table className="min-w-full">
                  <thead className={`${theadCls} sticky top-0`}>
                    <tr>
                      <th className={thCls}>Boleta</th>
                      <th className={thCls}>Lote</th>
                      <th className={thCls}>Etapa / ciclo</th>
                      <th className={thCls}>Aplicación real</th>
                      <th className={thCls}>Programada</th>
                      <th className={thCls}>Verificación</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sinMovimiento.map((item) => (
                      <tr className={trCls} key={item.id}>
                        <td className={`${tdCls} font-mono font-bold`} style={{ color: 'var(--ink)' }}>{item.numero}</td>
                        <td className={tdCls}>
                          <div className="font-bold" style={{ color: 'var(--ink)' }}>Lote {item.lote}</div>
                          <div className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>{item.finca || '—'}</div>
                        </td>
                        <td className={tdCls}>
                          <div>{item.etapa}</div>
                          <div className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>{item.ciclo}</div>
                        </td>
                        <td className={`${tdCls} font-mono`}>{item.fechaAplicacion || '—'}</td>
                        <td className={`${tdCls} font-mono`}>{item.fechaProgramada || '—'}</td>
                        <td className={tdCls}>
                          <div style={{ color: 'var(--tone-warn-text)' }}>Sin salida vinculada</div>
                          <div className="font-mono text-[10px]" style={{ color: 'var(--ink-faint)' }}>
                            No existe {item.referenciaEsperada}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tabActivo === 'resumen' && (loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm" style={{ color: 'var(--ink-muted)' }}>
            <Loader2 size={18} className="animate-spin" />
            Calculando insumos aplicados…
          </div>
        ) : filas.length === 0 ? (
          <div className="rounded-lg border py-14 text-center text-sm" style={{ color: 'var(--ink-muted)', borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
            No hay aplicaciones finalizadas en el período seleccionado.
          </div>
        ) : (
          <div className={tableWrapCls}>
            <table className="min-w-full">
              <thead className={theadCls}>
                <tr>
                  <th className={thCls}>Lote</th>
                  <th className={thCls}>Ciclo</th>
                  <th className={thCls}>Cuenta destino</th>
                  <th className={`${thCls} text-right`}>Área</th>
                  <th className={`${thCls} text-right`}>Con mov. / aplicadas</th>
                  <th className={`${thCls} text-right`}>Capitalizable</th>
                  <th className={`${thCls} text-right`}>Referencial</th>
                  <th className={thCls}>Control</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((fila) => {
                  const alertas = fila.movimientosSinValor + fila.aplicacionesSinMovimiento + (fila.cuenta ? 0 : 1);
                  return (
                    <tr className={trCls} key={fila.key}>
                      <td className={tdCls}>
                        <div className="font-bold" style={{ color: 'var(--ink)' }}>Lote {fila.lote}</div>
                        <div className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>{fila.finca || '—'}</div>
                      </td>
                      <td className={tdCls}>{fila.ciclo}</td>
                      <td className={tdCls}>
                        {fila.cuenta ? (
                          <>
                            <div className="font-mono text-[11px]" style={{ color: 'var(--emp-accent-txt)' }}>{fila.cuenta.codigo}</div>
                            <div className="max-w-[240px] truncate text-[10px]">{fila.cuenta.nombre}</div>
                          </>
                        ) : (
                          <span style={{ color: 'var(--tone-warn-text)' }}>Sin cuenta configurada</span>
                        )}
                      </td>
                      <td className={`${tdCls} text-right font-mono`}>{fmtNumber(fila.area)} ha</td>
                      <td className={`${tdCls} text-right font-mono`}>{fila.aplicacionesConMovimiento} / {fila.aplicaciones}</td>
                      <td className={`${tdCls} text-right font-mono font-bold`} style={{ color: 'var(--tone-success-text)' }}>{fmtCRC(fila.costoCapitalizable)}</td>
                      <td className={`${tdCls} text-right font-mono`}>{fmtCRC(fila.insumosReferenciales)}</td>
                      <td className={tdCls}>
                        {alertas === 0 ? (
                          <span className="inline-flex items-center gap-1" style={{ color: 'var(--tone-success-text)' }}>
                            <CheckCircle2 size={13} /> Listo
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1" style={{ color: 'var(--tone-warn-text)' }} title={`${fila.movimientosSinValor} movimientos sin valorar; ${fila.aplicacionesSinMovimiento} aplicaciones sin movimiento`}>
                            <AlertTriangle size={13} /> {alertas} alerta{alertas === 1 ? '' : 's'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
        </>
        )}

        <p className="mt-3 text-[10px]" style={{ color: 'var(--ink-faint)' }}>
          Capitalizable usa exclusivamente el costo guardado en la salida de inventario. Referencial usa el costo vigente del producto y no genera asientos.
        </p>
      </div>

      {confirmarPreparacion && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4" style={{ background: 'rgba(8, 12, 22, 0.72)' }}>
          <div className="w-full max-w-lg overflow-hidden rounded-xl border shadow-2xl" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
            <div className="flex items-center gap-3 border-b px-5 py-4" style={{ borderColor: 'var(--line)' }}>
              <ShieldCheck size={19} style={{ color: 'var(--tone-success-text)' }} />
              <div>
                <div className="text-sm font-bold" style={{ color: 'var(--ink)' }}>Preparar costos del activo biológico</div>
                <div className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{fechaDesde} al {fechaHasta}</div>
              </div>
            </div>
            <div className="space-y-3 px-5 py-4 text-xs leading-relaxed" style={{ color: 'var(--ink-muted)' }}>
              <p>Se congelarán únicamente salidas reales con costo positivo, vínculo válido a la cédula y cuentas contables configuradas.</p>
              <div className="rounded-lg border px-3 py-2" style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>
                El costo congelado será inmutable. No podrá cambiarse el producto, la cantidad ni su valoración después de esta operación.
              </div>
              <p>No se generará ningún asiento contable en esta etapa.</p>
            </div>
            <div className="flex justify-end gap-2 border-t px-5 py-4" style={{ borderColor: 'var(--line)' }}>
              <button type="button" className={btnSecondary} disabled={preparando} onClick={() => setConfirmarPreparacion(false)}>Cancelar</button>
              <button type="button" disabled={preparando} onClick={prepararCostos}
                className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs font-bold text-white disabled:opacity-40"
                style={{ background: 'var(--emp-accent)' }}>
                {preparando ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                Congelar costos válidos
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmarPreasiento && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4" style={{ background: 'rgba(8, 12, 22, 0.72)' }}>
          <div className="w-full max-w-lg overflow-hidden rounded-xl border shadow-2xl" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
            <div className="flex items-center gap-3 border-b px-5 py-4" style={{ borderColor: 'var(--line)' }}>
              <BookOpenCheck size={19} style={{ color: 'var(--tone-purple-text)' }} />
              <div>
                <div className="text-sm font-bold" style={{ color: 'var(--ink)' }}>Generar preasiento de activo biológico</div>
                <div className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>Fecha contable: {fechaContable}</div>
              </div>
            </div>
            <div className="space-y-3 px-5 py-4 text-xs leading-relaxed" style={{ color: 'var(--ink-muted)' }}>
              <p>Se tomarán solamente costos congelados del período que todavía no pertenezcan a otro preasiento vigente.</p>
              <div className="rounded-lg border px-3 py-2" style={{ borderColor: 'var(--line)', background: 'var(--surface-overlay)' }}>
                Débito: cuentas del activo biológico por lote y ciclo.<br />
                Crédito: Inventario de Insumos.<br />
                Total disponible: <b style={{ color: 'var(--ink)' }}>{fmtCRC(resumenCongelado.total)}</b>.
              </div>
              <div className="font-bold" style={{ color: 'var(--tone-success-text)' }}>El resultado deberá quedar con diferencia CRC 0.</div>
              <p>No se creará un asiento contable; quedará pendiente para revisión en el ERP.</p>
            </div>
            <div className="flex justify-end gap-2 border-t px-5 py-4" style={{ borderColor: 'var(--line)' }}>
              <button type="button" className={btnSecondary} disabled={generandoPreasiento} onClick={() => setConfirmarPreasiento(false)}>Cancelar</button>
              <button type="button" disabled={generandoPreasiento} onClick={generarPreasiento}
                className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs font-bold text-white disabled:opacity-40"
                style={{ background: 'var(--emp-accent)' }}>
                {generandoPreasiento ? <Loader2 size={14} className="animate-spin" /> : <BookOpenCheck size={14} />}
                Crear preasiento pendiente
              </button>
            </div>
          </div>
        </div>
      )}

      {preasientoAnular && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4" style={{ background: 'rgba(8, 12, 22, 0.72)' }}>
          <div className="w-full max-w-lg overflow-hidden rounded-xl border shadow-2xl" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
            <div className="flex items-center gap-3 border-b px-5 py-4" style={{ borderColor: 'var(--line)' }}>
              <AlertTriangle size={19} style={{ color: '#f87171' }} />
              <div>
                <div className="text-sm font-bold" style={{ color: 'var(--ink)' }}>Anular preasiento</div>
                <div className="font-mono text-[10px]" style={{ color: 'var(--ink-muted)' }}>{preasientoAnular.referencia}</div>
              </div>
            </div>
            <div className="space-y-3 px-5 py-4">
              <p className="text-xs leading-relaxed" style={{ color: 'var(--ink-muted)' }}>
                El registro y sus líneas se conservarán para auditoría. Sus costos congelados quedarán disponibles para un preasiento de reemplazo.
              </p>
              <div>
                <label className={labelCls}>Motivo obligatorio</label>
                <textarea className={`${inputCls} min-h-[82px] resize-y`} value={motivoAnulacion}
                  onChange={(e) => setMotivoAnulacion(e.target.value)}
                  placeholder="Explique la razón de la anulación…" autoFocus />
                <div className="mt-1 text-[10px]" style={{ color: 'var(--ink-faint)' }}>Mínimo 10 caracteres.</div>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t px-5 py-4" style={{ borderColor: 'var(--line)' }}>
              <button type="button" className={btnSecondary} disabled={anulandoPreasiento}
                onClick={() => { setPreasientoAnular(null); setMotivoAnulacion(''); }}>Conservar pendiente</button>
              <button type="button" disabled={anulandoPreasiento || motivoAnulacion.trim().length < 10}
                onClick={anularPreasiento}
                className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs font-bold text-white disabled:opacity-40"
                style={{ background: '#991b1b' }}>
                {anulandoPreasiento ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
                Anular y liberar costos
              </button>
            </div>
          </div>
        </div>
      )}

      {editando && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4" style={{ background: 'rgba(8, 12, 22, 0.72)' }} onMouseDown={cerrarCorreccion}>
          <div className="w-full max-w-2xl overflow-hidden rounded-xl border shadow-2xl" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }} onMouseDown={(event) => event.stopPropagation()}>
            <div className="flex items-center gap-3 border-b px-5 py-4" style={{ borderColor: 'var(--line)' }}>
              <Pencil size={18} style={{ color: 'var(--emp-accent-txt)' }} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold" style={{ color: 'var(--ink)' }}>Corregir producto de la cédula {editando.boleta}</div>
                <div className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>Línea {editando.lineaId} · Movimiento #{editando.movimientoId}</div>
              </div>
              <button type="button" className="rounded-md border p-2" style={{ color: 'var(--ink-muted)', borderColor: 'var(--line)' }} onClick={cerrarCorreccion} disabled={guardandoCorreccion}>
                <X size={16} />
              </button>
            </div>

            <div className="space-y-4 p-5">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border p-3" style={{ borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)' }}>
                  <div className={labelCls}>Producto actual</div>
                  <div className="mt-1 font-mono text-xs font-bold" style={{ color: 'var(--tone-warn-text)' }}>{editando.codigo}</div>
                  <div className="mt-1 text-xs" style={{ color: 'var(--ink)' }}>{editando.producto}</div>
                  <div className="mt-2 text-[10px]" style={{ color: 'var(--ink-muted)' }}>Cantidad conservada: {fmtNumber(editando.cantidad, 3)} {editando.unidad}</div>
                </div>
                <div className="rounded-lg border p-3" style={{ borderColor: productoCorreccion ? 'var(--tone-success-border)' : 'var(--line)', background: productoCorreccion ? 'var(--tone-success-bg)' : 'var(--surface-overlay)' }}>
                  <div className={labelCls}>Producto ERP nuevo</div>
                  {productoCorreccion ? (
                    <>
                      <div className="mt-1 font-mono text-xs font-bold" style={{ color: 'var(--tone-success-text)' }}>{productoCorreccion.codigo}</div>
                      <div className="mt-1 text-xs" style={{ color: 'var(--ink)' }}>{productoCorreccion.descripcion}</div>
                      <div className="mt-2 font-mono text-xs font-bold" style={{ color: 'var(--ink)' }}>
                        {fmtCostoProducto(productoCorreccion).principal}
                      </div>
                      <div className="mt-1 text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                        Equivalente contable: {fmtCostoProducto(productoCorreccion).equivalente}
                      </div>
                    </>
                  ) : (
                    <div className="mt-3 text-xs" style={{ color: 'var(--ink-muted)' }}>Seleccione el producto correcto.</div>
                  )}
                </div>
              </div>

              <div>
                <label className={labelCls}>Buscar producto ERP por código o nombre</label>
                <div className="relative">
                  <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-faint)' }} />
                  <input className={`${inputCls} pl-9`} value={busquedaCorreccion} onChange={(e) => setBusquedaCorreccion(e.target.value)} placeholder="Ejemplo: 00001 o ETHREL…" autoFocus />
                </div>
                {resultadosCorreccion.length > 0 && (
                  <div className="mt-1 max-h-48 overflow-auto rounded-lg border" style={{ borderColor: 'var(--line)', background: 'var(--surface-overlay)' }}>
                    {resultadosCorreccion.map((producto) => (
                      <button
                        type="button"
                        key={producto.id}
                        className="flex w-full items-center gap-3 border-b px-3 py-2 text-left last:border-b-0"
                        style={{ borderColor: 'var(--line)', background: productoCorreccion?.id === producto.id ? 'var(--emp-accent-bg)' : 'transparent' }}
                        onClick={() => {
                          setProductoCorreccion(producto);
                          setBusquedaCorreccion(`${producto.codigo} · ${producto.descripcion}`);
                          setResultadosCorreccion([]);
                        }}
                      >
                        <span className="min-w-[64px] font-mono text-[11px] font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{producto.codigo}</span>
                        <span className="min-w-0 flex-1 truncate text-xs" style={{ color: 'var(--ink)' }}>{producto.descripcion}</span>
                        <span className="text-right font-mono text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                          <span className="block font-bold" style={{ color: 'var(--ink)' }}>{fmtCostoProducto(producto).principal}</span>
                          <span className="block">{fmtCostoProducto(producto).equivalente}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className={labelCls}>Motivo de la corrección *</label>
                <textarea
                  className={`${inputCls} min-h-[78px] resize-y`}
                  value={motivoCorreccion}
                  onChange={(e) => setMotivoCorreccion(e.target.value)}
                  placeholder="Explique por qué la cédula quedó vinculada al producto incorrecto…"
                />
              </div>

              <div className="rounded-lg border px-3 py-2 text-[10px]" style={{ color: 'var(--ink-muted)', borderColor: 'var(--line)', background: 'var(--surface-overlay)' }}>
                La fecha y cantidad no cambiarán. Se corregirán juntos el producto de la cédula, el movimiento de inventario y su costo ERP. La operación se bloquea si existe asiento o costo congelado.
              </div>
              {errorCorreccion && (
                <div className="rounded-lg border px-3 py-2 text-xs" style={{ color: 'var(--tone-warn-text)', borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)' }}>{errorCorreccion}</div>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t px-5 py-4" style={{ borderColor: 'var(--line)' }}>
              <button type="button" className={btnSecondary} onClick={cerrarCorreccion} disabled={guardandoCorreccion}>Cancelar</button>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                style={{ background: 'var(--emp-accent)' }}
                disabled={!productoCorreccion || motivoCorreccion.trim().length < 10 || guardandoCorreccion}
                onClick={guardarCorreccion}
              >
                {guardandoCorreccion ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Confirmar corrección
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
